import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  NgZone,
  OnDestroy,
  Optional,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import type { VersionEvent } from '@angular/service-worker';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  ActionSheetController,
  AlertController,
  IonicModule,
  ModalController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AuthService } from './core/services/auth.service';
import { ConversationHistoryService } from './core/services/conversation-history.service';
import { ImageUsageService } from './core/services/image-usage.service';
import { OCRService } from './core/services/ocr.service';
import { StripePaymentService } from './core/services/stripe-payment.service';
import {
  TranslationKeys,
  TranslationService,
} from './core/services/translation.service';
import { TranslatorService } from './core/services/translator.service';
import { WordUsageService } from './core/services/word-usage.service';
import { AnalyticsComponent } from './features/analytics/analytics.component';
import { LoginComponent } from './features/auth/login/login.component';
import { BidirectionalConversationModalComponent } from './features/bidirectional-conversation/bidirectional-conversation-modal/bidirectional-conversation-modal.component';
import { BroadcastTranslationModalComponent } from './features/broadcast-translation/broadcast-translation-modal/broadcast-translation-modal.component';
import { BuyWordsModalComponent } from './features/buy-words/buy-words-modal/buy-words-modal.component';
import { ContinuousConversationModalComponent } from './features/continuous-conversation/continuous-conversation-modal/continuous-conversation-modal.component';
import { ConversationHistoryModalComponent } from './features/conversation-history/conversation-history-modal/conversation-history-modal.component';
import { ImageUsageModalComponent } from './features/image-usage/image-usage-modal/image-usage-modal.component';
import { WordUsageModalComponent } from './features/word-usage/word-usage-modal/word-usage-modal.component';
import { NavigationComponent } from './layout/navigation/navigation.component';

// Shared models
import { Subscription } from 'rxjs';
import {
  ConversationEntry,
  ConversationHistoryEntry,
  User,
} from './shared/models';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    IonicModule,
    RouterOutlet,
    LoginComponent,
    NavigationComponent,
    ConversationHistoryModalComponent,
    WordUsageModalComponent,
    ImageUsageModalComponent,
    BuyWordsModalComponent,
    AnalyticsComponent,
    ContinuousConversationModalComponent, // Add for ModalController to work on Android
    BidirectionalConversationModalComponent,
    BroadcastTranslationModalComponent,
  ],
})
export class AppComponent
  implements AfterViewInit, AfterViewChecked, OnDestroy
{
  // Authentication
  currentUser: User | null = null;
  isLoggedIn = false;
  isCheckingAuth = true; // Show loading while checking authentication
  isLoggingOut = false; // Show loading while logging out
  
  // Route tracking
  currentRoute = '';

  // Translation
  translations: TranslationKeys = {} as TranslationKeys;
  private translationSubscription?: Subscription;
  
  // Dynamic logo URL based on language
  logoUrl: string = 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344';
  private authSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private deepLinkListenerHandle?: any;
  private paymentSuccessHandled = false;

  // Service worker update interval
  private swUpdateInterval?: any;

  // Service worker subscriptions
  private swVersionUpdatesSubscription?: Subscription;

  // Textarea resize listener cleanup functions
  private textareaResizeCleanupFunctions: (() => void)[] = [];
  
  // Store resize handles for responsive updates
  private resizeHandles: HTMLElement[] = [];
  
  // Window resize listener cleanup
  private windowResizeCleanup?: () => void;

  // Audio processing timeout
  private audioProcessingTimeout?: any;

  // Audio event listener handlers
  private audioEndedHandler?: () => void;

  // iOS browser detection for audio context unlocking
  private isIOSBrowser = this.detectIOSBrowser();
  private audioContextUnlocked = false;

  private detectIOSBrowser(): boolean {
    // Check if running in iOS browser (not native app)
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return false; // Native iOS app - doesn't need unlocking

    // Check for iOS browser
    if (platform === 'web') {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    }

    return false;
  }

  /**
   * Unlock iOS browser audio context by playing a silent audio
   * This must be called in user interaction context (e.g., button click)
   */
  private async unlockIOSAudioContext(): Promise<void> {
    if (!this.isIOSBrowser) {
      console.log('🎵 [iOS Browser] Not iOS browser, skipping unlock');
      return;
    }

    // Skip unlock attempt - iOS Safari data URLs don't work reliably
    // Instead, we'll rely on the user interaction context from the button click
    // When audio is ready, we'll try to play it directly
    console.log(
      '🎵 [iOS Browser] Skipping silent audio unlock (not reliable on iOS Safari)'
    );
    console.log(
      '🎵 [iOS Browser] Will attempt to play audio directly when ready'
    );
    this.audioContextUnlocked = true; // Mark as "unlocked" so we at least try
  }

  showPaymentSuccessBanner = false;
  paymentSuccessMessage = '';

  // OpenAI quota error banner
  showQuotaErrorBanner = false;
  quotaErrorMessage = '';

  // Language selection - using signals for reactive state management
  selectedUserLanguage = signal<
    | 'en'
    | 'ko'
    | 'zh-CN'
    | 'zh-HK'
    | 'ky'
    | 'ru'
    | 'kk'
    | 'tg'
    | 'tk'
    | 'uz'
    | 'fa'
    | 'es'
  >('en');
  selectedPartnerLanguage = signal<
    | 'en'
    | 'ko'
    | 'zh-CN'
    | 'zh-HK'
    | 'ky'
    | 'ru'
    | 'kk'
    | 'tg'
    | 'tk'
    | 'uz'
    | 'fa'
    | 'es'
  >('ky');

  // Conversation topic for better translation context (e.g., "dental lecture", "medical conference")
  conversationTopic = signal<string>('');

  // Section toggle for bidirectional conversation (default: show user section, hide partner section)
  showUserSection = signal<boolean>(true);
  showPartnerSection = signal<boolean>(false);

  // Toggle between user and partner sections
  toggleBidirectionalSection(): void {
    const currentUserVisible = this.showUserSection();
    this.showUserSection.set(!currentUserVisible);
    this.showPartnerSection.set(currentUserVisible);

    // Re-initialize resize handles when either section becomes visible
    // Use a longer timeout to ensure DOM is fully updated after *ngIf changes
    setTimeout(() => {
      if (currentUserVisible) {
        console.log(
          '🔧 Partner section became visible, setting up resize handles...'
        );
      } else {
        console.log(
          '🔧 User section became visible, setting up resize handles...'
        );
      }
      // Force re-initialization by removing data-resize-initialized attributes first
      // This ensures handles are re-setup even if elements were cached somehow
      if (this.userTextArea?.nativeElement) {
        const userContainer = this.userTextArea.nativeElement.parentElement;
        if (userContainer) {
          const userHandle = userContainer.querySelector(
            '.resize-handle-indicator'
          ) as HTMLElement;
          if (userHandle) {
            userHandle.removeAttribute('data-resize-initialized');
          }
        }
      }
      if (this.partnerTextArea?.nativeElement) {
        const partnerContainer =
          this.partnerTextArea.nativeElement.parentElement;
        if (partnerContainer) {
          const partnerHandle = partnerContainer.querySelector(
            '.resize-handle-indicator'
          ) as HTMLElement;
          if (partnerHandle) {
            partnerHandle.removeAttribute('data-resize-initialized');
          }
        }
      }
      this.setupTextareaResize();
    }, 150);
  }

  // UI Language signal for interface language switching
  currentUILanguage = signal<
    | 'en'
    | 'ko'
    | 'zh-CN'
    | 'zh-HK'
    | 'ky'
    | 'ru'
    | 'kk'
    | 'tg'
    | 'tk'
    | 'uz'
    | 'fa'
    | 'es'
  >('en');

  // Available UI languages with flags and native names
  availableUILanguages = [
    { value: 'en', label: 'English', nativeName: 'English', flag: '🇺🇸' },
    { value: 'ko', label: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
    { value: 'zh-CN', label: 'Mandarin', nativeName: '普通话', flag: '🇨🇳' },
    { value: 'zh-HK', label: 'Cantonese', nativeName: '粵語', flag: '🇭🇰' },
    { value: 'ky', label: 'Kyrgyz', nativeName: 'Кыргызча', flag: '🇰🇬' },
    { value: 'ru', label: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
    { value: 'kk', label: 'Kazakh', nativeName: 'Қазақша', flag: '🇰🇿' },
    { value: 'tg', label: 'Tajik', nativeName: 'Тоҷикӣ', flag: '🇹🇯' },
    { value: 'tk', label: 'Turkmen', nativeName: 'Türkmençe', flag: '🇹🇲' },
    { value: 'uz', label: 'Uzbek', nativeName: "O'zbekcha", flag: '🇺🇿' },
    { value: 'fa', label: 'Farsi', nativeName: 'فارسی', flag: '🇮🇷' },
    { value: 'es', label: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  ];

  // Computed signals for language options
  userLanguages = computed(() => {
    const allLanguages = [
      { value: 'en', label: 'English' },
      { value: 'ko', label: 'Korean (한국어)' },
      { value: 'zh-CN', label: 'Mandarin (普通话)' },
      { value: 'zh-HK', label: 'Cantonese (粵語)' },
      { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
      { value: 'ru', label: 'Russian (Русский)' },
      { value: 'kk', label: 'Kazakh (Қазақша)' },
      { value: 'tg', label: 'Tajik (Тоҷикӣ)' },
      { value: 'tk', label: 'Turkmen (Türkmençe)' },
      { value: 'uz', label: "Uzbek (O'zbekcha)" },
      { value: 'fa', label: 'Farsi (فارسی)' },
      { value: 'es', label: 'Spanish (Español)' },
    ];
    return allLanguages.filter(
      (lang) => lang.value !== this.selectedPartnerLanguage()
    );
  });

  partnerLanguages = computed(() => {
    const allLanguages = [
      { value: 'en', label: 'English' },
      { value: 'ko', label: 'Korean (한국어)' },
      { value: 'zh-CN', label: 'Mandarin (普通话)' },
      { value: 'zh-HK', label: 'Cantonese (粵語)' },
      { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
      { value: 'ru', label: 'Russian (Русский)' },
      { value: 'kk', label: 'Kazakh (Қазақша)' },
      { value: 'tg', label: 'Tajik (Тоҷикӣ)' },
      { value: 'tk', label: 'Turkmen (Türkmençe)' },
      { value: 'uz', label: "Uzbek (O'zbekcha)" },
      { value: 'fa', label: 'Farsi (فارسی)' },
      { value: 'es', label: 'Spanish (Español)' },
    ];
    return allLanguages.filter(
      (lang) => lang.value !== this.selectedUserLanguage()
    );
  });

  // Voice and speed settings - separate voices for user and partner
  selectedUserVoice = 'alloy';
  selectedPartnerVoice = 'nova';
  // Keep selectedVoice for backward compatibility with navigation component
  get selectedVoice(): string {
    return this.selectedUserVoice;
  }
  set selectedVoice(voice: string) {
    this.selectedUserVoice = voice;
  }
  speakingSpeed = 1.0;
  availableVoices = [
    { id: 'alloy', name: 'Alloy (Neutral)', icon: '😐' },
    { id: 'echo', name: 'Echo (Male)', icon: '👨' },
    { id: 'fable', name: 'Fable (Theatrical)', icon: '🎭' },
    { id: 'onyx', name: 'Onyx (Deep)', icon: '' },
    { id: 'nova', name: 'Nova (Female)', icon: '♀️' },
    { id: 'shimmer', name: 'Shimmer (Soft)', icon: '' },
  ];

  // Diarization settings - Always enabled for better detection
  useDiarization = true; // Always ON for improved accuracy
  diarizationResults: any[] = [];
  showDiarizationResults = false;

  // Audio generation settings
  skipAudioGeneration = signal<boolean>(false);

  // Conversation visibility toggle
  showAllConversations = false;
  showAllPartnerConversations = false;

  // Guard to prevent multiple concurrent image picker openings
  private isOpeningImagePicker = false;

  // Simple in-app dropdown menus for image source selection
  isUserImageMenuOpen = false;
  isPartnerImageMenuOpen = false;

  // Conversation management
  conversationId = 1;

  // Audio processing
  private mediaRecorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private sentenceBuffer = '';
  private silenceTimeout?: any;
  private periodicRefreshInterval?: any;
  private idleTimeout?: any;
  private speechActivityTimeout?: any;
  private lastSpeechTime = 0;
  private readonly SPEECH_SILENCE_THRESHOLD = 6000; // 6 seconds of silence before stopping (increased from 3s to allow natural pauses)

  // Constants
  private readonly USER_LANGUAGE_KEY = 'selectedUserLanguage';
  private readonly PARTNER_LANGUAGE_KEY = 'selectedPartnerLanguage';
  private readonly SPEAKING_SPEED_KEY = 'speakingSpeed';
  private readonly USER_VOICE_SELECTION_KEY = 'selectedUserVoice';
  private readonly PARTNER_VOICE_SELECTION_KEY = 'selectedPartnerVoice';
  private readonly SKIP_AUDIO_GENERATION_KEY = 'skipAudioGeneration';

  // Spinners
  isProcessingEnd = false;
  isProcessingPostTranslation = false;
  isProcessingIntermediate = false;
  isProcessingFinalizing = false;
  isProcessingAudioGeneration = false;

  // Error messages
  wordLimitErrorMessage: string | null = null;
  isProcessingTranslation = false; // General translation spinner
  isProcessingOCR = false; // Specific OCR processing state
  currentLane: 'user' | 'partner' | null = null;

  // Text input
  userTextInput: string = '';
  partnerTextInput: string = '';

  // Conversation History
  showHistoryModal = false;
  showWordUsageModal = false;
  showImageUsageModal = false;
  showBuyWordsModal = false;
  preSelectedPackageId: string | null = null;
  preSelectedImagePackageId: string | null = null;
  showAnalyticsModal = false;
  showLanguageSettingsModal = false;
  showContinuousConversationModal = false;
  continuousConversationSourceLanguage: string = 'en';
  continuousConversationTargetLanguage: string = 'en';
  showBidirectionalConversationModal = false;
  bidirectionalConversationSourceLanguage: string = 'en';
  bidirectionalConversationTargetLanguage: string = 'en';
  showBroadcastTranslationModal = false;
  broadcastSourceLanguage: string = 'en';
  broadcastTargetLanguage: string = 'en';

  // EN → KY lane - conversation array converted to signal
  enConversation = signal<ConversationEntry[]>([]);
  currentEnText = '';
  currentKyText = '';
  currentEnConfidence = 0;

  // KY → EN lane - conversation array converted to signal
  kyConversation = signal<ConversationEntry[]>([]);
  currentKyInText = '';
  currentEnOutText = '';
  currentKyConfidence = 0;

  // Recording state
  isRecording = false;
  isAutoRestarting = false;
  manuallyStopped = false;
  isPlayingAudio = false;

  // Mobile detection
  isMobile: boolean = false;

  // Mobile menu state
  isMobileMenuOpen: boolean = false;

  // Textarea references for iOS custom resize
  @ViewChild('userTextArea', { static: false })
  userTextArea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('partnerTextArea', { static: false })
  partnerTextArea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('navigation', { static: false })
  navigation!: NavigationComponent;

  // Math object for template
  Math = Math;

  // Getter for recording state (matches original webapp)
  get recording() {
    return this.isRecording;
  }

  constructor(
    private svc: TranslatorService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private conversationHistoryService: ConversationHistoryService,
    private platform: Platform,
    private translationService: TranslationService,
    private wordUsageService: WordUsageService,
    private imageUsageService: ImageUsageService,
    private renderer: Renderer2,
    private ocrService: OCRService,
    private stripePaymentService: StripePaymentService,
    private router: Router,
    private toastController: ToastController,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private http: HttpClient,
    @Optional() private swUpdate: SwUpdate | null
  ) {
    this.isMobile = this.platform.is('capacitor');
    this.initializeApp();
    this.setupServiceWorkerUpdates();
    this.loadLanguageSelections();
    this.loadUILanguagePreference();
    this.setupAuthListener();
    this.setupTranslation();
    this.setupRouterListener();
    this.setupDeepLinkListener();
  }

  async initializeApp() {
    console.log('🚀 AppComponent: initializeApp() called');
    console.log(
      '📱 Platform:',
      this.platform.is('capacitor') ? 'MOBILE' : 'WEB'
    );
    console.log(
      '🔧 Capacitor.isNativePlatform():',
      Capacitor.isNativePlatform()
    );

    await this.platform.ready();

    // NOTE: appStateChange listener removed for payment processing
    // It was causing premature payment processing when Browser opened for ANY reason
    // Deep link listener (appUrlOpen) now handles payment success exclusively
    // This ensures payment is only processed when Stripe actually confirms it succeeded

    // Set up app state listener for buy parameters (when returning from browser)
    if (this.platform.is('capacitor')) {
      // Track interval for periodic checks
      let checkInterval: any = null;

      // Function to start periodic check
      const startPeriodicCheck = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        checkInterval = setInterval(() => {
          const hasPendingAction = sessionStorage.getItem('pending_buy_action');
          if (hasPendingAction) {
            console.log(
              '🔄 Periodic check - found pending buy action, checking...'
            );
            this.checkForBuyParameter();
          } else {
            // Stop checking if no pending action
            if (checkInterval) {
              clearInterval(checkInterval);
              checkInterval = null;
            }
          }
        }, 2000); // Check every 2 seconds
      };

      // Check immediately on app load (in case user returned from browser)
      setTimeout(() => {
        console.log(
          '📱 Mobile app initialized, checking for buy parameters...'
        );
        this.checkForBuyParameter();

        // ALWAYS start periodic check on app load (even if no pending action yet)
        // This ensures we catch the return even if sessionStorage is set after app loads
        console.log(
          '🔄 Starting periodic check on app load (always, as fallback)'
        );
        startPeriodicCheck();
      }, 1000);

      App.addListener('appStateChange', (state) => {
        console.log('📱 App state changed:', state);
        console.log('📱 State.isActive:', state.isActive);
        console.log(
          '📱 Current sessionStorage:',
          sessionStorage.getItem('pending_buy_action')
        );

        if (state.isActive) {
          console.log('✅ App became active, checking for buy parameters...');
          console.log(
            '📱 Current sessionStorage when active:',
            sessionStorage.getItem('pending_buy_action')
          );

          // Check if user returned from payment (with or without payment success)
          const hasPaymentSuccess =
            window.location.href.includes('paymentSuccess=true') ||
            window.location.hash.includes('paymentSuccess=true');
          const hasPaymentCancel =
            window.location.href.includes('payment-cancel') ||
            window.location.hash.includes('payment-cancel');
          const hasPendingAction = sessionStorage.getItem('pending_buy_action');

          // IMPORTANT: If user returned from webapp (has pending action), handle both scenarios:
          // 1. Payment success - handled by checkForPaymentSuccess
          // 2. No payment (cancelled or just returned) - close modal and clear state
          if (hasPendingAction && !hasPaymentSuccess && !hasPaymentCancel) {
            console.log(
              '🔄 User returned from webapp - checking if payment was completed...'
            );
            console.log(
              '📱 Has pending action, no payment success/cancel in URL yet'
            );

            // Close modal immediately if open (user might have returned without completing payment)
            // This ensures buttons are active even if payment wasn't completed
            if (this.showBuyWordsModal) {
              console.log(
                '🔄 Closing modal immediately since user returned (payment may or may not be completed)'
              );
              this.showBuyWordsModal = false;
              this.cdr.detectChanges(); // Force UI update to activate buttons
            }

            // Wait to see if payment success is detected (via deep link or URL params)
            // After timeout, clear sessionStorage if no payment was detected
            setTimeout(() => {
              // Double-check: Only clear if payment success still wasn't detected
              const stillNoPaymentSuccess =
                !window.location.href.includes('paymentSuccess=true') &&
                !window.location.hash.includes('paymentSuccess=true') &&
                !window.location.href.includes('payment-cancel') &&
                !window.location.hash.includes('payment-cancel');

              // Also check if payment was processed (by checking if sessionStorage still exists)
              // If it still exists and no payment success, user likely cancelled or closed browser
              const stillHasPendingAction =
                sessionStorage.getItem('pending_buy_action');

              if (stillNoPaymentSuccess && stillHasPendingAction) {
                console.log(
                  '🧹 No payment detected after timeout - clearing sessionStorage (user likely cancelled or closed browser)'
                );
                sessionStorage.removeItem('pending_buy_action');
                sessionStorage.removeItem('combined_purchase');
                // Force UI update to ensure buttons are active
                this.cdr.detectChanges();
              } else if (hasPaymentSuccess) {
                console.log(
                  '✅ Payment success was detected - keeping sessionStorage (already being processed)'
                );
              }
            }, 3000); // Wait 3 seconds to allow deep link payment success to be detected
          }

          // IMPORTANT: Ensure modal is closed and buttons are active when app becomes active
          // This prevents frozen buttons after returning from payment (with or without payment)
          if (this.showBuyWordsModal && hasPaymentSuccess) {
            console.log(
              '🔄 App became active after payment success - closing modal to ensure buttons are active'
            );
            this.showBuyWordsModal = false;
            this.cdr.detectChanges();
          }

          // Check for buy parameters when app becomes active (user returned from browser)
          // Use longer delay to ensure app is fully initialized and user is logged in
          setTimeout(() => {
            console.log('📱 Delayed check - calling checkForBuyParameter()...');
            console.log(
              '📱 SessionStorage at delayed check:',
              sessionStorage.getItem('pending_buy_action')
            );
            // Only check for buy parameters if there's no payment success/cancel being processed
            // This prevents re-opening modal after payment
            if (!hasPaymentSuccess && !hasPaymentCancel) {
              // Wait a bit longer to ensure payment processing is done first
              setTimeout(() => {
                this.checkForBuyParameter();
              }, 500);
            } else {
              console.log(
                '💳 Payment detected - skipping buy parameter check to prevent modal re-opening'
              );
            }
          }, 1500);

          // Start periodic checks when app is active (fallback in case appStateChange doesn't fire on return)
          // BUT: Skip if payment success/cancel is being processed
          if (hasPendingAction && !hasPaymentSuccess && !hasPaymentCancel) {
            console.log(
              '🔄 Starting periodic check (app active, pending action found, no payment)'
            );
            startPeriodicCheck();
          } else if (hasPaymentSuccess || hasPaymentCancel) {
            console.log(
              '💳 Payment detected - skipping periodic check to prevent modal re-opening'
            );
          }
        } else {
          console.log('📱 App went to background (isActive = false)');
          // Stop periodic checks when app goes to background
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
        }
      });
    }

    // Handle Auth0 redirect callback for web platform
    if (!this.platform.is('capacitor')) {
      try {
        console.log('🔗 Checking for Auth0 redirect callback...');
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (code && state) {
          console.log(
            '🔗 Auth0 authorization code detected, handling callback...'
          );
          await this.authService.handleRedirectCallback();
          console.log('✅ Auth0 callback handled successfully');

          // Clean up URL parameters after handling callback
          const newUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        } else {
          console.log('🔗 No Auth0 callback detected');
        }
      } catch (error) {
        console.error('❌ Error handling Auth0 callback:', error);
      }
    }

    // Stripe is now initialized lazily when purchase modals are opened
    // This prevents unnecessary analytics requests to r.stripe.com/b on app startup

    // Request microphone permission on mobile
    if (this.platform.is('capacitor')) {
      try {
        // Use standard Web API for microphone access
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        console.warn('Microphone permission not available:', error);
      }

      // Setup keyboard listeners for toolbar
      this.setupKeyboardListeners();
    }
  }

  /**
   * Setup service worker for automatic app updates
   * Only enabled for web platform in production
   */
  private setupServiceWorkerUpdates(): void {
    // Only enable service worker updates for web platform (not mobile)
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Service worker updates disabled on mobile platform');
      return;
    }

    if (!this.swUpdate || !this.swUpdate.isEnabled) {
      console.log('⚠️ Service worker is not enabled');
      return;
    }

    console.log('🔄 Service worker updates enabled');

    // Check for updates on startup
    this.swUpdate!.checkForUpdate();

    // Check for updates periodically (every 3 hours)
    this.swUpdateInterval = setInterval(() => {
      this.swUpdate!.checkForUpdate();
    }, 3 * 60 * 60 * 1000);

    // Listen for version ready events
    this.swVersionUpdatesSubscription = this.swUpdate!.versionUpdates.pipe(
      filter(
        (event: VersionEvent): event is VersionReadyEvent =>
          event.type === 'VERSION_READY'
      )
    ).subscribe(async (event: VersionReadyEvent) => {
      console.log('🆕 New version available:', event.latestVersion);
      console.log('📦 Current version:', event.currentVersion);

      // Show toast notification with reload button
      const toast = await this.toastController.create({
        message: 'A new version is available. Click to reload.',
        duration: 0, // Don't auto-dismiss
        position: 'bottom',
        buttons: [
          {
            text: 'Reload',
            role: 'reload',
            handler: async () => {
              try {
                await this.swUpdate!.activateUpdate();
                console.log('✅ Update activated, reloading...');
                window.location.reload();
              } catch (error) {
                console.error('❌ Error activating update:', error);
                // Fallback: reload anyway
                window.location.reload();
              }
            },
          },
          {
            text: 'Later',
            role: 'cancel',
          },
        ],
      });

      await toast.present();
    });

    // Handle update activation errors (already subscribed above, no need for separate subscription)
    // The versionUpdates subscription above will handle all event types
  }

  ngAfterViewInit(): void {
    // Only setup textarea resize if we're on the main route and user is logged in
    // The textarea is only rendered when currentRoute === '' || currentRoute === '/' and isLoggedIn === true
    const isOnMainRoute = this.currentRoute === '' || this.currentRoute === '/';
    
    if (!isOnMainRoute || !this.isLoggedIn) {
      // Not on main route or not logged in - textarea won't be available
      // This is expected, so we don't log an error
      return;
    }

    // Setup custom resize handlers for all platforms (web, iOS, and Android)
    // Try multiple times to ensure textareas are ready
    // Note: Partner textarea is conditionally rendered with *ngIf, so it may not be available initially
    setTimeout(() => {
      if (isOnMainRoute && this.isLoggedIn) {
        this.setupTextareaResize();
      }
    }, 1000);

    // Retry after a longer delay if user textarea is not ready
    // Partner textarea is optional (conditionally rendered) so we don't check for it here
    setTimeout(() => {
      if (!isOnMainRoute || !this.isLoggedIn) {
        return; // Route changed or logged out, skip
      }
      
      if (!this.userTextArea?.nativeElement) {
        console.warn(
          '⚠️ User textarea still not ready, retrying resize setup...'
        );
        this.setupTextareaResize();
      } else {
        console.log(
          '✅ User textarea is ready (partner textarea will be set up when visible)'
        );
      }
    }, 2500);

    // Final retry after even longer delay - only check user textarea
    setTimeout(() => {
      if (!isOnMainRoute || !this.isLoggedIn) {
        return; // Route changed or logged out, skip
      }
      
      if (!this.userTextArea?.nativeElement) {
        // Only log error if we're still on the main route and logged in
        // Otherwise, this is expected behavior
        console.warn(
          '⚠️ User textarea still not ready after multiple retries (this may be expected if the component is still loading)'
        );
      } else {
        console.log('✅ User textarea is ready, resize handlers set up');
        // Partner textarea will be set up automatically when it becomes visible via ngAfterViewChecked
        this.setupTextareaResize();
      }
    }, 4000);
  }

  ngAfterViewChecked(): void {
    // Check if partner section just became visible and needs resize setup
    if (this.showPartnerSection() && this.partnerTextArea?.nativeElement) {
      // Check if resize handle already has event listeners
      const container = this.partnerTextArea.nativeElement.parentElement;
      if (container) {
        const handle = container.querySelector(
          '.resize-handle-indicator'
        ) as HTMLElement;
        if (handle && !handle.hasAttribute('data-resize-initialized')) {
          console.log(
            '🔧 Partner section resize handle not initialized, setting up now...'
          );
          this.setupSingleTextareaResize(
            this.partnerTextArea.nativeElement,
            1,
            'partner'
          );
        }
      }
    }
  }

  /**
   * Setup custom resize handler for textareas (web, iOS, and Android)
   */
  private setupTextareaResize(): void {
    // Setup resize for user textarea (always available)
    if (this.userTextArea?.nativeElement) {
      this.setupSingleTextareaResize(
        this.userTextArea.nativeElement,
        0,
        'user'
      );
    } else {
      console.warn('⚠️ User textarea not available yet');
    }

    // Setup resize for partner textarea (conditionally rendered - may not be available)
    if (this.partnerTextArea?.nativeElement) {
      this.setupSingleTextareaResize(
        this.partnerTextArea.nativeElement,
        1,
        'partner'
      );
    } else {
      console.log(
        'ℹ️ Partner textarea not available (likely conditionally rendered with *ngIf)'
      );
    }
    
    // Setup responsive listener after handles are initialized
    if (this.resizeHandles.length > 0) {
      this.setupResizeHandleResponsiveListener();
    }
  }

  private setupSingleTextareaResize(
    textarea: HTMLTextAreaElement,
    index: number,
    name: string
  ): void {
    console.log(`✅ Setting up resize for ${name} textarea (index ${index})`);

    // Check if this specific textarea's resize handle is already initialized
    const container = textarea.parentElement;
    if (!container) {
      console.warn(`⚠️ Container not found for textarea ${index}`);
      return;
    }

    // Check if already initialized
    let handle = container.querySelector(
      '.resize-handle-indicator'
    ) as HTMLElement;
    if (handle && handle.hasAttribute('data-resize-initialized')) {
      console.log(
        `ℹ️ Resize handle for ${name} textarea (index ${index}) already initialized, skipping setup`
      );
      return; // Skip setup if already initialized
    }

    // Only clean up if this is a re-initialization (e.g., after login)
    // Don't clean up all listeners - only clean up if we're re-setting up the same textarea
    // This prevents removing listeners from the other textarea when setting up partner section

    const platform = Capacitor.getPlatform();
    const isWeb = !Capacitor.isNativePlatform();
    // Detect mobile device early for use in touch listener setup
    const isMobileDevice =
      this.isMobile || Capacitor.isNativePlatform() || window.innerWidth <= 768;
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const hasTouchSupport =
      typeof window !== 'undefined' &&
      ('ontouchstart' in window ||
        (nav?.maxTouchPoints ?? 0) > 0 ||
        ((nav as unknown as { msMaxTouchPoints?: number })?.msMaxTouchPoints ??
          0) > 0);
    // BUG 1 FIX: Only attach touch listeners for actual touch devices or native platforms
    // Do NOT use viewport width (isMobileDevice) - desktop browsers resized to mobile width
    // shouldn't get touch listeners with passive:false, which blocks scrolling
    const shouldAttachTouchListeners = !isWeb || hasTouchSupport;
    console.log(
      `🔧 Setting up textarea resize for ${isWeb ? 'web' : platform}`
    );

    // Ensure container has position relative
    this.renderer.setStyle(container, 'position', 'relative');

    // Find resize handle indicator - it's a sibling of the textarea (if not already found above)
    if (!handle) {
      handle = container.querySelector(
        '.resize-handle-indicator'
      ) as HTMLElement;
    }
    if (!handle) {
      console.error(`❌ Resize handle not found for textarea ${index}`);
      console.error(
        `❌ Container HTML:`,
        container.innerHTML.substring(0, 500)
      );
      console.error(
        `❌ All children:`,
        Array.from(container.children).map((c) => ({
          tagName: c.tagName,
          className: c.className,
          id: c.id,
        }))
      );
      return;
    }

    console.log(`✅ Found resize handle for textarea ${index}:`, {
      handle,
      computedStyle: window.getComputedStyle(handle),
      rect: handle.getBoundingClientRect(),
      zIndex: window.getComputedStyle(handle).zIndex,
      pointerEvents: window.getComputedStyle(handle).pointerEvents,
      position: window.getComputedStyle(handle).position,
      right: window.getComputedStyle(handle).right,
      bottom: window.getComputedStyle(handle).bottom,
    });

    // Mark handle as initialized to prevent duplicate setup
    handle.setAttribute('data-resize-initialized', 'true');

    let startY = 0;
    let startHeight = 0;
    let isResizing = false;

    // Touch event handlers (for mobile)
    const onTouchStart = (e: TouchEvent) => {
      // Always start resizing when touching the handle
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];

      isResizing = true;
      startY = touch.clientY;
      startHeight =
        textarea.offsetHeight ||
        parseInt(getComputedStyle(textarea).height) ||
        80;
      console.log('📏 Started resizing textarea (touch)', {
        startY,
        startHeight,
        touchX: touch.clientX,
        touchY: touch.clientY,
      });

      // Ensure textarea has explicit height
      if (!textarea.style.height) {
        this.renderer.setStyle(textarea, 'height', `${startHeight}px`);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isResizing) return;

      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      const deltaY = touch.clientY - startY;
      const newHeight = Math.min(Math.max(startHeight + deltaY, 80), 400);

      this.renderer.setStyle(textarea, 'height', `${newHeight}px`);
      this.renderer.setStyle(textarea, 'max-height', 'none');
      console.log('📏 Resizing:', { deltaY, newHeight });
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (isResizing) {
        isResizing = false;
        e.preventDefault();
        e.stopPropagation();
        console.log('📏 Finished resizing textarea (touch)');
      }
    };

    // Mouse event handlers (for web)
    const onMouseDown = (e: MouseEvent) => {
      // Always start resizing when clicking on the handle
      isResizing = true;
      startY = e.clientY;
      startHeight =
        textarea.offsetHeight ||
        parseInt(getComputedStyle(textarea).height) ||
        80;
      e.preventDefault();
      e.stopPropagation();
      console.log('📏 Started resizing textarea (mouse)', {
        startY,
        startHeight,
        target: e.target,
        currentTarget: e.currentTarget,
        handle: handle,
      });

      // Ensure textarea has explicit height
      if (!textarea.style.height) {
        this.renderer.setStyle(textarea, 'height', `${startHeight}px`);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      e.preventDefault();
      e.stopPropagation();

      const deltaY = e.clientY - startY;
      const newHeight = Math.min(Math.max(startHeight + deltaY, 80), 400);

      this.renderer.setStyle(textarea, 'height', `${newHeight}px`);
      this.renderer.setStyle(textarea, 'max-height', 'none');
      console.log('📏 Resizing:', { deltaY, newHeight });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (isResizing) {
        isResizing = false;
        e.preventDefault();
        e.stopPropagation();
        console.log('📏 Finished resizing textarea (mouse)');
      }
    };

    // Store handle reference for responsive updates
    if (!this.resizeHandles.includes(handle)) {
      this.resizeHandles.push(handle);
    }
    
    // BUG 2 FIX: Update handle styles based on current viewport (will be updated on resize)
    // This allows CSS media queries to work properly by not using !important on responsive properties
    this.updateResizeHandleStyles(handle);
    
    // Set non-responsive styles that don't conflict with CSS media queries
    handle.style.pointerEvents = 'auto';
    handle.style.cursor = 'ns-resize';
    handle.style.position = 'absolute';
    handle.style.display = 'flex';
    handle.style.visibility = 'visible';
    handle.style.zIndex = '1000';
    handle.style.bottom = '28px';
    this.renderer.setStyle(handle, 'pointer-events', 'auto');
    this.renderer.setStyle(handle, 'cursor', 'ns-resize');
    this.renderer.setStyle(handle, 'position', 'absolute');
    this.renderer.setStyle(handle, 'display', 'flex');
    this.renderer.setStyle(handle, 'visibility', 'visible');
    // Use renderer for consistency (non-responsive properties only)
    this.renderer.setStyle(handle, 'pointer-events', 'auto');
    this.renderer.setStyle(handle, 'cursor', 'ns-resize');
    this.renderer.setStyle(handle, 'position', 'absolute');
    this.renderer.setStyle(handle, 'display', 'flex');
    this.renderer.setStyle(handle, 'visibility', 'visible');
    this.renderer.setStyle(handle, 'z-index', '1000');
    console.log(`✅ Resize handle styled for textarea ${index}`, {
      isMobileDevice,
      isMobile: this.isMobile,
      platform,
      isWeb,
      shouldAttachTouchListeners,
      width: handle.style.width,
      height: handle.style.height,
      right: handle.style.right,
      computedRight: window.getComputedStyle(handle).right,
      pointerEvents: handle.style.pointerEvents,
      cursor: handle.style.cursor,
      position: handle.style.position,
      display: handle.style.display,
      visibility: handle.style.visibility,
      zIndex: handle.style.zIndex,
      bottom: handle.style.bottom,
      opacity: handle.style.opacity,
      computedStyle: window.getComputedStyle(handle).pointerEvents,
    });

    // Platform-specific styling
    if (platform === 'android') {
      this.renderer.setStyle(handle, 'touch-action', 'none');
      this.renderer.setStyle(handle, 'z-index', '1000');
      this.renderer.setStyle(handle, 'opacity', '0.85');
    } else if (platform === 'ios' || (!isWeb && platform !== 'android')) {
      // iOS (native) - ensure proper touch handling
      this.renderer.setStyle(handle, 'touch-action', 'none');
      this.renderer.setStyle(handle, '-webkit-touch-callout', 'none');
      this.renderer.setStyle(handle, '-webkit-user-select', 'none');
      this.renderer.setStyle(handle, 'user-select', 'none');
      this.renderer.setStyle(handle, 'z-index', '1000');
      this.renderer.setStyle(
        handle,
        'opacity',
        isMobileDevice ? '0.85' : '0.75'
      );
      // Force pointer events for iOS
      this.renderer.setStyle(handle, 'pointer-events', 'auto');
      // Ensure handle is visible and clickable on iOS
      handle.style.pointerEvents = 'auto';
      handle.style.touchAction = 'none';
      // Use setProperty for webkit properties that TypeScript doesn't recognize
      handle.style.setProperty('-webkit-touch-callout', 'none');
      handle.style.setProperty('-webkit-user-select', 'none');
      handle.style.userSelect = 'none';
      console.log(`✅ iOS-specific styles applied for textarea ${index}`);
    }

    // Always set high z-index regardless of platform
    this.renderer.setStyle(handle, 'z-index', '1000');

    if (shouldAttachTouchListeners) {
      this.renderer.setStyle(handle, 'touch-action', 'none');
      // For iOS, also set inline style
      if (platform === 'ios' || (!isWeb && platform !== 'android')) {
        handle.style.touchAction = 'none';
      }
    }

    // Remove any existing listeners first (if re-running setup)
    // Store listener cleanup functions
    const cleanupFunctions: (() => void)[] = [];

    // Add event listeners based on available inputs
    if (shouldAttachTouchListeners) {
      // Use addEventListener with passive: false for touchstart to allow preventDefault
      // For iOS, we need to be more aggressive with event handling
      const touchStartHandler = (e: TouchEvent) => {
        console.log(`📱 Touch start on resize handle ${index}`, {
          touches: e.touches.length,
          target: e.target,
          currentTarget: e.currentTarget,
        });
        onTouchStart(e);
      };

      // For iOS, use both capture and bubble phases, and ensure passive is false
      const touchStartOptions: AddEventListenerOptions = {
        passive: false,
        capture: true,
      };
      handle.addEventListener(
        'touchstart',
        touchStartHandler,
        touchStartOptions
      );

      // Also add without capture as backup for iOS
      if (platform === 'ios' || (!isWeb && platform !== 'android')) {
        handle.addEventListener('touchstart', touchStartHandler, {
          passive: false,
        });
        console.log(
          `✅ iOS: Added touchstart listener (both capture and bubble) for textarea ${index}`
        );
      }

      const touchMoveHandler = (e: TouchEvent) => {
        if (isResizing) {
          onTouchMove(e);
        }
      };
      document.addEventListener('touchmove', touchMoveHandler, {
        passive: false,
        capture: true,
      });

      // For iOS, also add without capture
      if (platform === 'ios' || (!isWeb && platform !== 'android')) {
        document.addEventListener('touchmove', touchMoveHandler, {
          passive: false,
        });
      }

      const touchEndHandler = (e: TouchEvent) => {
        if (isResizing) {
          onTouchEnd(e);
        }
      };
      document.addEventListener('touchend', touchEndHandler, {
        passive: false,
        capture: true,
      });

      // For iOS, also add without capture
      if (platform === 'ios' || (!isWeb && platform !== 'android')) {
        document.addEventListener('touchend', touchEndHandler, {
          passive: false,
        });
      }

      // Also use renderer.listen as backup
      const touchStartCleanup = this.renderer.listen(
        handle,
        'touchstart',
        onTouchStart
      );
      const touchMoveCleanup = this.renderer.listen(
        document,
        'touchmove',
        onTouchMove
      );
      const touchEndCleanup = this.renderer.listen(
        document,
        'touchend',
        onTouchEnd
      );

      cleanupFunctions.push(
        touchStartCleanup,
        touchMoveCleanup,
        touchEndCleanup,
        () => {
          try {
            handle.removeEventListener('touchstart', touchStartHandler, true);
            document.removeEventListener('touchmove', touchMoveHandler, true);
            document.removeEventListener('touchend', touchEndHandler, true);
            // Also remove iOS-specific listeners (non-capture)
            if (platform === 'ios' || (!isWeb && platform !== 'android')) {
              handle.removeEventListener('touchstart', touchStartHandler);
              document.removeEventListener('touchmove', touchMoveHandler);
              document.removeEventListener('touchend', touchEndHandler);
            }
          } catch (e) {
            console.warn('Error removing touch event listeners:', e);
          }
        }
      );
      console.log(
        `✅ ${
          isWeb ? 'Web (touch-enabled)' : 'Mobile'
        }: Touch event listeners attached (both direct and renderer) for textarea ${index}`
      );
    }

    // Add pointerdown handler for iOS (iOS Safari supports pointer events)
    if (platform === 'ios' || (!isWeb && platform !== 'android')) {
      const onPointerDown = (e: PointerEvent) => {
        console.log(`📱 Pointer down on resize handle ${index}`, {
          pointerId: e.pointerId,
          pointerType: e.pointerType,
          target: e.target,
          currentTarget: e.currentTarget,
        });
        // Convert pointer event to touch-like behavior
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
          e.preventDefault();
          e.stopPropagation();
          isResizing = true;
          startY = e.clientY;
          startHeight =
            textarea.offsetHeight ||
            parseInt(getComputedStyle(textarea).height) ||
            80;
          console.log('📏 Started resizing textarea (pointer)', {
            startY,
            startHeight,
            pointerX: e.clientX,
            pointerY: e.clientY,
          });

          // Ensure textarea has explicit height
          if (!textarea.style.height) {
            this.renderer.setStyle(textarea, 'height', `${startHeight}px`);
          }
        }
      };

      const onPointerMove = (e: PointerEvent) => {
        if (!isResizing) return;
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
          e.preventDefault();
          e.stopPropagation();

          const deltaY = e.clientY - startY;
          const newHeight = Math.min(Math.max(startHeight + deltaY, 80), 400);

          this.renderer.setStyle(textarea, 'height', `${newHeight}px`);
          this.renderer.setStyle(textarea, 'max-height', 'none');
          console.log('📏 Resizing:', { deltaY, newHeight });
        }
      };

      const onPointerUp = (e: PointerEvent) => {
        if (isResizing && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
          isResizing = false;
          e.preventDefault();
          e.stopPropagation();
          console.log('📏 Finished resizing textarea (pointer)');
        }
      };

      // Add pointer event listeners for iOS
      handle.addEventListener('pointerdown', onPointerDown, { passive: false });
      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', onPointerUp, { passive: false });
      document.addEventListener('pointercancel', onPointerUp, { passive: false });

      cleanupFunctions.push(() => {
        handle.removeEventListener('pointerdown', onPointerDown);
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);
      });

      console.log(
        `✅ iOS: Pointer event listeners attached for textarea ${index}`
      );
    }

    // Add click test handler to verify handle is clickable
    const testClickHandler = (e: Event) => {
      console.log(`🔍 Test click on resize handle ${index} (${name})`, {
        type: e.type,
        target: e.target,
        currentTarget: e.currentTarget,
        bubbles: e.bubbles,
        cancelable: e.cancelable,
      });
    };
    handle.addEventListener('click', testClickHandler, true); // Use capture phase
    handle.addEventListener('mousedown', testClickHandler, true);
    handle.addEventListener('pointerdown', testClickHandler, true);
    handle.addEventListener('touchstart', testClickHandler, true);

    if (isWeb) {
      // Use both renderer.listen and direct addEventListener for maximum compatibility
      const mouseDownCleanup = this.renderer.listen(
        handle,
        'mousedown',
        onMouseDown
      );
      // Also add direct event listener as backup
      const directMouseDown = (e: MouseEvent) => {
        console.log('🔍 Direct mousedown listener fired', e);
        onMouseDown(e);
      };
      handle.addEventListener('mousedown', directMouseDown, true);

      const mouseMoveCleanup = this.renderer.listen(
        document,
        'mousemove',
        onMouseMove
      );
      const mouseUpCleanup = this.renderer.listen(
        document,
        'mouseup',
        onMouseUp
      );
      cleanupFunctions.push(
        mouseDownCleanup,
        mouseMoveCleanup,
        mouseUpCleanup,
        () => {
          handle.removeEventListener('click', testClickHandler, true);
          handle.removeEventListener('mousedown', testClickHandler, true);
          handle.removeEventListener('pointerdown', testClickHandler, true);
          handle.removeEventListener('touchstart', testClickHandler, true);
          handle.removeEventListener('mousedown', directMouseDown, true);
        }
      );
      console.log(
        `✅ Web: Mouse event listeners attached for textarea ${index} (both renderer and direct)`
      );
    }

    // Store cleanup functions for ngOnDestroy
    this.textareaResizeCleanupFunctions.push(...cleanupFunctions);
  }

  /**
   * Update resize handle styles based on current viewport width
   * BUG 2 FIX: This allows CSS media queries to work properly by not using !important on responsive properties
   */
  private updateResizeHandleStyles(handle: HTMLElement): void {
    const isMobileViewport = window.innerWidth <= 768;
    const isNativeMobile = Capacitor.isNativePlatform();
    
    // Only set responsive properties without !important to allow CSS media queries to override
    // For native mobile apps, always use mobile styles
    if (isNativeMobile || isMobileViewport) {
      // Mobile styles - CSS media query will also apply, but we set defaults here
      handle.style.right = '0px';
      handle.style.width = '40px';
      handle.style.height = '40px';
      handle.style.border = '2px solid #6b7280';
      handle.style.borderRadius = '8px 0 8px 0';
      handle.style.opacity = '0.85';
    } else {
      // Desktop styles - CSS will handle these, but we set defaults
      handle.style.right = '-14px';
      handle.style.width = '28px';
      handle.style.height = '28px';
      handle.style.border = '1.5px solid #9ca3af';
      handle.style.borderRadius = '6px';
      handle.style.opacity = '0.75';
    }
  }

  /**
   * Setup window resize listener to update resize handle styles responsively
   * BUG 2 FIX: Updates styles when viewport changes, allowing CSS media queries to work
   */
  private setupResizeHandleResponsiveListener(): void {
    // Clean up existing listener if any
    if (this.windowResizeCleanup) {
      this.windowResizeCleanup();
      this.windowResizeCleanup = undefined;
    }

    // Add resize listener to update handle styles when viewport changes
    const resizeHandler = () => {
      this.resizeHandles.forEach((handle) => {
        if (handle && handle.parentElement) {
          this.updateResizeHandleStyles(handle);
        }
      });
    };

    // Add direct window listener (renderer.listen doesn't work well with window object)
    window.addEventListener('resize', resizeHandler);
    
    // Store cleanup function
    this.windowResizeCleanup = () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }

  /**
   * Setup router listener to detect payment success redirects
   */
  private setupRouterListener(): void {
    // Check for payment success immediately on app load (for external redirects)
    console.log('🔄 Checking for payment success on app load...');
    console.log('🔄 Current URL on app load:', window.location.href);
    this.checkForPaymentSuccess();

    // Check for buy parameter to open purchase modal
    this.checkForBuyParameter();

    // Also listen to router events for internal navigation
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
        console.log('🔄 Router navigation detected:', event.url);
        console.log('🔄 Full URL after navigation:', window.location.href);
        // Check for payment success on every navigation
        this.checkForPaymentSuccess();
        // Check for buy parameter on every navigation
        this.checkForBuyParameter();
      }
    });
    
    // Initialize current route
    this.currentRoute = this.router.url;
  }

  /**
   * Check URL parameters for buy parameter and open purchase modal
   */
  private checkForBuyParameter(): void {
    console.log('🔍 checkForBuyParameter() CALLED');
    console.log(
      '🔍 Current platform:',
      this.platform.is('capacitor') ? 'MOBILE' : 'WEB'
    );
    console.log(
      '🔍 Current sessionStorage:',
      sessionStorage.getItem('pending_buy_action')
    );
    try {
      let buy: string | null = null;
      let packageId: string | null = null;
      let packageType: string | null = null;
      let price: string | null = null;
      let words: string | null = null;
      let images: string | null = null;
      let wordPackageId: string | null = null;
      let imagePackageId: string | null = null;
      let url: URL | null = null;

      // On web, check URL parameters
      if (!this.platform.is('capacitor')) {
        url = new URL(window.location.href);
        buy = url.searchParams.get('buy');
        packageId = url.searchParams.get('packageId');
        packageType = url.searchParams.get('packageType');
        price = url.searchParams.get('price');
        words = url.searchParams.get('words');
        images = url.searchParams.get('images');
        wordPackageId = url.searchParams.get('wordPackageId');
        imagePackageId = url.searchParams.get('imagePackageId');

        // Extract image count from imagePackageId if images parameter is missing (same pattern as words)
        // imagePackageId format: "images-50" -> extract "50"
        if (!images && imagePackageId && imagePackageId.startsWith('images-')) {
          const extractedCount = imagePackageId.replace('images-', '');
          if (extractedCount && !isNaN(parseInt(extractedCount))) {
            images = extractedCount;
            console.log('🔍 Extracted image count from imagePackageId:', {
              imagePackageId,
              extractedCount: images,
            });
          }
        }

        // Extract word count from wordPackageId if words parameter is missing (for consistency)
        // wordPackageId format: "5000" -> use directly
        if (!words && wordPackageId && !isNaN(parseInt(wordPackageId))) {
          words = wordPackageId;
          console.log('🔍 Extracted word count from wordPackageId:', {
            wordPackageId,
            extractedCount: words,
          });
        }

        // Log all URL parameters for debugging (especially image counts)
        console.log('🌐 Web: Reading URL parameters:', {
          buy,
          packageId,
          packageType,
          price,
          words,
          images,
          wordPackageId,
          imagePackageId,
          allParams: Object.fromEntries(url.searchParams.entries()),
        });
      }

      // On mobile, check sessionStorage (parameters stored when browser was opened)
      if (this.platform.is('capacitor')) {
        const storedBuyAction = sessionStorage.getItem('pending_buy_action');
        if (storedBuyAction) {
          try {
            const storedParams = JSON.parse(storedBuyAction);
            console.log(
              '📱 Mobile: Found stored buy parameters:',
              storedParams
            );
            buy = storedParams.buy;
            packageId = storedParams.packageId;
            packageType = storedParams.packageType;
            price = storedParams.price;
            words = storedParams.words;
            images = storedParams.images;
            wordPackageId = storedParams.wordPackageId;
            imagePackageId = storedParams.imagePackageId;

            // Extract image count from imagePackageId if images parameter is missing (same pattern as words)
            // imagePackageId format: "images-50" -> extract "50"
            if (
              !images &&
              imagePackageId &&
              imagePackageId.startsWith('images-')
            ) {
              const extractedCount = imagePackageId.replace('images-', '');
              if (extractedCount && !isNaN(parseInt(extractedCount))) {
                images = extractedCount;
                console.log(
                  '🔍 Mobile: Extracted image count from imagePackageId:',
                  {
                    imagePackageId,
                    extractedCount: images,
                  }
                );
              }
            }

            // Extract word count from wordPackageId if words parameter is missing (for consistency)
            if (!words && wordPackageId && !isNaN(parseInt(wordPackageId))) {
              words = wordPackageId;
              console.log(
                '🔍 Mobile: Extracted word count from wordPackageId:',
                {
                  wordPackageId,
                  extractedCount: words,
                }
              );
            }

            // Verify image count is present (just like word count)
            console.log('📱 Mobile: Parameter values:', {
              buy,
              wordPackageId,
              imagePackageId,
              words,
              images,
              hasWords: !!words,
              hasImages: !!images,
            });
          } catch (error) {
            console.error('❌ Error parsing stored buy action:', error);
          }
        }
      }

      // Store buy parameters in sessionStorage if they exist (in case URL gets cleared during Auth0 redirect)
      // Store if we have buy parameter AND at least one package ID (word or image)
      if (buy && (packageId || wordPackageId || imagePackageId)) {
        const paramsToStore: any = {
          buy,
          packageId,
          packageType,
          price,
          wordPackageId,
          imagePackageId,
        };

        // Always include words and images if they exist (same pattern for both)
        if (words) paramsToStore.words = words;
        if (images) paramsToStore.images = images;

        console.log(
          '💾 Storing buy parameters in sessionStorage:',
          paramsToStore
        );
        console.log('💾 Verifying image count is included:', {
          hasImages: !!images,
          imagesValue: images,
          willBeStored: !!images,
        });

        sessionStorage.setItem(
          'pending_buy_action',
          JSON.stringify(paramsToStore)
        );

        // Verify it was stored correctly
        const verifyStored = sessionStorage.getItem('pending_buy_action');
        if (verifyStored) {
          try {
            const verifyParsed = JSON.parse(verifyStored);
            console.log('✅ Verified stored parameters include:', {
              words: verifyParsed.words,
              images: verifyParsed.images,
              wordPackageId: verifyParsed.wordPackageId,
              imagePackageId: verifyParsed.imagePackageId,
            });
          } catch (e) {
            console.error('❌ Error verifying stored params:', e);
          }
        }
      }

      // Also check sessionStorage for stored parameters (in case URL was cleared during Auth0 redirect)
      const storedBuyAction = sessionStorage.getItem('pending_buy_action');
      let finalBuy = buy;
      let finalPackageId = packageId;
      let finalPackageType = packageType;
      let finalPrice = price;
      let finalWords = words;
      let finalImages = images;
      let finalWordPackageId = wordPackageId;
      let finalImagePackageId = imagePackageId;

      if (storedBuyAction) {
        try {
          const storedParams = JSON.parse(storedBuyAction);
          console.log(
            '💾 Restoring buy parameters from sessionStorage:',
            storedParams
          );
          // Use stored params if URL params are missing (they were cleared during redirect)
          // For combined purchases, restore both word and image counts (same pattern)
          if (!finalBuy) finalBuy = storedParams.buy;
          if (!finalPackageId) finalPackageId = storedParams.packageId;
          if (!finalPackageType) finalPackageType = storedParams.packageType;
          if (!finalPrice) finalPrice = storedParams.price;

          // Restore words count (same pattern as images)
          if (!finalWords) finalWords = storedParams.words;

          // Restore images count (same pattern as words) - handle exactly like words
          if (!finalImages) finalImages = storedParams.images;

          if (!finalWordPackageId)
            finalWordPackageId = storedParams.wordPackageId;
          if (!finalImagePackageId)
            finalImagePackageId = storedParams.imagePackageId;

          console.log('💾 After restoring from sessionStorage:', {
            finalBuy,
            finalWords,
            finalImages,
            finalWordPackageId,
            finalImagePackageId,
            storedHasWords: !!storedParams.words,
            storedHasImages: !!storedParams.images,
            storedWords: storedParams.words,
            storedImages: storedParams.images,
          });
        } catch (error) {
          console.error('❌ Error parsing stored buy action:', error);
        }
      }

      // Debug: Log all final values before condition check
      console.log('🔍 Checking buy parameters:', {
        finalBuy,
        finalPackageId,
        finalPackageType,
        finalWords,
        finalImages,
        finalWordPackageId,
        finalImagePackageId,
        finalPrice,
        isWords: finalBuy === 'words',
        isImages: finalBuy === 'images',
        isCombined: finalBuy === 'combined',
        hasPackageId: !!finalPackageId,
        hasWordPackageId: !!finalWordPackageId,
        hasImagePackageId: !!finalImagePackageId,
      });

      // Handle buy=words - open modal (with or without packageId)
      // This allows opening modal when user clicks "Buy Translation Data" from mobile
      if (finalBuy === 'words') {
        console.log('🛒 Buy words parameter detected:', {
          buy: finalBuy,
          packageId: finalPackageId,
          hasPackageId: !!finalPackageId,
        });

        // Don't open modal if it's already open (prevent duplicate opens)
        if (this.showBuyWordsModal) {
          console.log('⚠️ Buy words modal is already open, skipping re-open');
          // Still clear the sessionStorage to prevent re-opening later
          sessionStorage.removeItem('pending_buy_action');
          return;
        }

        // Wait for user to be logged in, then open modal
        const checkLoginAndOpenModal = () => {
          // Double-check modal is not already open before opening
          if (this.showBuyWordsModal) {
            console.log('⚠️ Buy words modal is already open, skipping re-open');
            sessionStorage.removeItem('pending_buy_action');
            return;
          }

          if (this.isLoggedIn && this.currentUser) {
            console.log('✅ User logged in, opening buy-words modal...');

            // Clear URL parameters (web only)
            if (url && !this.platform.is('capacitor')) {
              url.searchParams.delete('buy');
              url.searchParams.delete('packageId');
              url.searchParams.delete('packageType');
              url.searchParams.delete('price');
              url.searchParams.delete('words');
              window.history.replaceState({}, document.title, url.toString());
            }

            // Clear stored parameters BEFORE opening modal to prevent re-opening
            sessionStorage.removeItem('pending_buy_action');

            // Pre-select the package if provided, otherwise open modal without pre-selection
            if (finalPackageId) {
              this.preSelectedPackageId = finalPackageId;
            } else {
              this.preSelectedPackageId = null;
            }

            // Open buy-words modal
            this.showBuyWordsModal = true;
            this.cdr.detectChanges();
          } else {
            console.log('⏳ Waiting for user to log in...');
            // Retry after a short delay if not logged in yet
            setTimeout(checkLoginAndOpenModal, 500);
          }
        };

        // Start checking after a short delay to allow auth to initialize
        setTimeout(checkLoginAndOpenModal, 1000);
      } else if (finalBuy === 'images' && finalPackageId) {
        // Redirect buy=images to merged modal for backward compatibility
        console.log(
          '🛒 Buy images parameter detected - redirecting to merged modal:',
          {
            buy: finalBuy,
            packageId: finalPackageId,
          }
        );

        // Don't open modal if it's already open (prevent duplicate opens)
        if (this.showBuyWordsModal) {
          console.log('⚠️ Buy words modal is already open, skipping re-open');
          sessionStorage.removeItem('pending_buy_action');
          return;
        }

        // Wait for user to be logged in, then open merged modal
        const checkLoginAndOpenModal = () => {
          // Double-check modal is not already open before opening
          if (this.showBuyWordsModal) {
            console.log('⚠️ Buy words modal is already open, skipping re-open');
            sessionStorage.removeItem('pending_buy_action');
            return;
          }

          if (this.isLoggedIn && this.currentUser) {
            console.log(
              '✅ User logged in, opening merged modal with image package...'
            );

            // Clear URL parameters (web only)
            if (url && !this.platform.is('capacitor')) {
              url.searchParams.delete('buy');
              url.searchParams.delete('packageId');
              url.searchParams.delete('packageType');
              url.searchParams.delete('price');
              url.searchParams.delete('images');
              window.history.replaceState({}, document.title, url.toString());
            }

            // Clear stored parameters BEFORE opening modal to prevent re-opening
            sessionStorage.removeItem('pending_buy_action');

            // Pre-select the image package in merged modal (words package stays null)
            this.preSelectedPackageId = null;
            this.preSelectedImagePackageId = finalPackageId;

            console.log('🎯 Pre-selected packages:', {
              wordPackageId: this.preSelectedPackageId,
              imagePackageId: this.preSelectedImagePackageId,
            });

            // Open merged modal (buy-words-modal) with image package pre-selected
            this.showBuyWordsModal = true;
            this.cdr.detectChanges();
          } else {
            console.log('⏳ Waiting for user to log in...');
            // Retry after a short delay if not logged in yet
            setTimeout(checkLoginAndOpenModal, 500);
          }
        };

        // Start checking after a short delay to allow auth to initialize
        setTimeout(checkLoginAndOpenModal, 1000);
      } else if (finalBuy === 'combined') {
        // Combined purchase: check counts based on which packages are present
        const hasWordPackage = !!finalWordPackageId;
        const hasImagePackage = !!finalImagePackageId;
        const hasWordCount = !!finalWords;
        const hasImageCount = !!finalImages;

        // Combined purchase: open merged modal with word and/or image packages pre-selected
        console.log('🛒✅ Combined purchase parameter detected:', {
          buy: finalBuy,
          wordPackageId: finalWordPackageId,
          imagePackageId: finalImagePackageId,
          words: finalWords,
          images: finalImages,
          hasWordPackage,
          hasImagePackage,
          hasWordCount,
          hasImageCount,
          wordPrice: finalPrice, // May contain word price
          packageId: finalPackageId, // Legacy support
        });

        // Validate counts based on which packages are present:
        // - If word package is present, check word count
        // - If image package is present, check image count
        // - Only check what's actually selected
        if (hasWordPackage) {
          if (!hasWordCount) {
            console.warn('⚠️ Word package ID present but word count missing:', {
              wordPackageId: finalWordPackageId,
              words: finalWords,
            });
          } else {
            console.log('✅ Word package and count both present:', {
              wordPackageId: finalWordPackageId,
              words: finalWords,
            });
          }
        }

        if (hasImagePackage) {
          if (!hasImageCount) {
            console.warn(
              '⚠️ Image package ID present but image count missing:',
              {
                imagePackageId: finalImagePackageId,
                images: finalImages,
              }
            );
          } else {
            console.log('✅ Image package and count both present:', {
              imagePackageId: finalImagePackageId,
              images: finalImages,
            });
          }
        }

        // Only proceed if at least one package is selected
        if (!hasWordPackage && !hasImagePackage) {
          console.warn('⚠️ Combined purchase detected but no packages found');
          return;
        }

        // Don't open modal if it's already open (prevent duplicate opens)
        if (this.showBuyWordsModal) {
          console.log('⚠️ Buy words modal is already open, skipping re-open');
          // Still clear the sessionStorage to prevent re-opening later
          sessionStorage.removeItem('pending_buy_action');
          return;
        }

        // Wait for user to be logged in, then open modal
        const checkLoginAndOpenModal = () => {
          // Double-check modal is not already open before opening
          if (this.showBuyWordsModal) {
            console.log('⚠️ Buy words modal is already open, skipping re-open');
            sessionStorage.removeItem('pending_buy_action');
            return;
          }

          if (this.isLoggedIn && this.currentUser) {
            console.log(
              '✅ User logged in, opening buy-words modal (merged) with combined packages...'
            );

            // Clear URL parameters (web only)
            if (url && !this.platform.is('capacitor')) {
              url.searchParams.delete('buy');
              url.searchParams.delete('packageId');
              url.searchParams.delete('packageType');
              url.searchParams.delete('price');
              url.searchParams.delete('words');
              url.searchParams.delete('images');
              url.searchParams.delete('wordPackageId');
              url.searchParams.delete('imagePackageId');
              window.history.replaceState({}, document.title, url.toString());
            }

            // Clear stored parameters BEFORE opening modal to prevent re-opening
            sessionStorage.removeItem('pending_buy_action');

            // Pre-select both packages before opening modal
            // Use wordPackageId/imagePackageId if available, otherwise fall back to packageId
            this.preSelectedPackageId =
              finalWordPackageId || finalPackageId || null;
            this.preSelectedImagePackageId = finalImagePackageId || null;

            console.log('🎯 Pre-selected packages (combined):', {
              wordPackageId: this.preSelectedPackageId,
              imagePackageId: this.preSelectedImagePackageId,
              words: finalWords,
              images: finalImages,
              wordCount: finalWords ? parseInt(finalWords) : null,
              imageCount: finalImages ? parseInt(finalImages) : null,
            });

            // Open buy-words modal (merged modal)
            this.showBuyWordsModal = true;
            this.cdr.detectChanges();
          } else {
            console.log('⏳ Waiting for user to log in...');
            // Retry after a short delay if not logged in yet
            setTimeout(checkLoginAndOpenModal, 500);
          }
        };

        // Start checking after a short delay to allow auth to initialize
        setTimeout(checkLoginAndOpenModal, 1000);
      }
    } catch (error) {
      console.error('❌ Error checking buy parameter:', error);
    }
  }

  /**
   * Setup deep link listener for mobile platforms
   */
  private async setupDeepLinkListener(): Promise<void> {
    console.log('🔧 setupDeepLinkListener called');
    console.log('🔧 Platform is capacitor:', this.platform.is('capacitor'));
    console.log(
      '🔧 Capacitor.isNativePlatform():',
      Capacitor.isNativePlatform()
    );

    if (!this.platform.is('capacitor')) {
      console.log('🌐 Web platform detected, skipping deep link listener');
      return;
    }

    console.log('📱 Setting up deep link listener for mobile platform...');

    try {
      this.deepLinkListenerHandle = await App.addListener(
        'appUrlOpen',
        async (event: URLOpenListenerEvent) => {
          console.log('📱 Deep link received:', event.url);
          console.log('📱 Deep link URL type:', typeof event.url);
          console.log('📱 Deep link URL length:', event.url?.length);
          console.log(
            '📱 Deep link contains paymentSuccess:',
            event.url?.includes('paymentSuccess')
          );
          console.log(
            '📱 Deep link contains translation:',
            event.url?.includes('translation')
          );

          // Check if this is a payment success redirect (check BEFORE Auth0 to prevent conflicts)
          if (
            event.url &&
            (event.url.includes('paymentSuccess=true') ||
              event.url.includes('payment-success'))
          ) {
            console.log('💳 Payment success deep link detected!');
            console.log('🔍 Full callback URL:', event.url);

            // Close browser immediately (like Auth0 does) - fire and forget
            Browser.close().catch(() => {}); // Ignore errors if already closed

            try {
              // Extract payment parameters from the URL
              const url = new URL(event.url);
              const searchParams = url.searchParams;
              const hashParams = new URLSearchParams(url.hash.substring(1));

              const paymentSuccess =
                searchParams.get('paymentSuccess') ||
                hashParams.get('paymentSuccess');
              const packageId =
                searchParams.get('packageId') || hashParams.get('packageId');
              const packageType =
                searchParams.get('packageType') ||
                hashParams.get('packageType');
              const sessionId =
                searchParams.get('sessionId') || hashParams.get('sessionId');

              console.log('💳 Payment success params:', {
                paymentSuccess,
                packageId,
                packageType,
                sessionId,
                searchParams: searchParams.toString(),
                hashParams: hashParams.toString(),
              });

              if (
                paymentSuccess === 'true' &&
                packageId &&
                packageType &&
                sessionId
              ) {
                console.log('✅ Valid payment success detected, processing...');

                // Process payment success
                await this.processPaymentSuccess(
                  packageId,
                  packageType,
                  sessionId
                );
              } else {
                console.log(
                  '⚠️ Payment success detected but missing required params'
                );
              }
            } catch (urlError) {
              console.error('❌ Error parsing payment success URL:', urlError);
              console.log('❌ Raw URL that failed to parse:', event.url);
            }

            // Return early to prevent Auth0 handler from processing payment deep links
            return;
          } else if (event.url && event.url.includes('com.kyrgz.translation')) {
            console.log('🔐 Auth0 callback deep link detected');
            // Let the login component handle Auth0 callbacks
            // This prevents interference with payment success handling
          } else {
            console.log('🔗 Other deep link detected:', event.url);
            console.log('🔗 Deep link analysis:', {
              hasUrl: !!event.url,
              urlLength: event.url?.length || 0,
              containsPaymentSuccess:
                event.url?.includes('paymentSuccess') || false,
              containsTranslation: event.url?.includes('translation') || false,
              containsCallback: event.url?.includes('callback') || false,
            });
          }
        }
      );

      console.log('✅ Deep link listener set up successfully');
    } catch (error) {
      console.error('❌ Failed to set up deep link listener:', error);
    }
  }

  /**
   * Handle mobile payment return (Browser closed after payment)
   */
  private async handleMobilePaymentReturn(paymentInfo: {
    sessionId: string;
    packageId: string;
    packageType: 'words' | 'images';
  }): Promise<void> {
    try {
      console.log('💳 Handling mobile payment return:', paymentInfo);

      // Call the existing processPaymentSuccess method
      await this.processPaymentSuccess(
        paymentInfo.packageId,
        paymentInfo.packageType,
        paymentInfo.sessionId
      );

      // Clear pending payment info after processing
      this.stripePaymentService.clearPendingPaymentInfo();
    } catch (error) {
      console.error('❌ Error handling mobile payment return:', error);
      this.stripePaymentService.clearPendingPaymentInfo();
    }
  }

  /**
   * Process payment success
   */
  private async processPaymentSuccess(
    packageId: string,
    packageType: string,
    sessionId: string
  ): Promise<void> {
    try {
      console.log('💳 Processing payment success:', {
        packageId,
        packageType,
        sessionId,
      });

      // IMPORTANT: Close buy-words modal if it's open to ensure buttons are active
      // User might return from webapp with modal still open, causing buttons to appear frozen
      if (this.showBuyWordsModal) {
        console.log(
          '🔄 Closing buy-words modal after payment success to ensure buttons are active'
        );
        this.showBuyWordsModal = false;
        this.cdr.detectChanges();
      }

      // Show success banner immediately for better UX
      this.showPaymentSuccessBanner = true;

      // Check if this is a combined purchase (words + images)
      // Read and parse once, then store for later use
      const combinedPurchaseStr = sessionStorage.getItem('combined_purchase');
      let combinedPurchase: any = null;

      if (combinedPurchaseStr) {
        try {
          combinedPurchase = JSON.parse(combinedPurchaseStr);
          console.log('📦 Combined purchase detected:', combinedPurchase);
        } catch (error) {
          console.error('❌ Error parsing combined purchase info:', error);
        }
      }

      let friendlyMessage = '';

      if (combinedPurchase) {
        // Build message with both word and image counts
        const parts: string[] = [];
        if (combinedPurchase.words) {
          parts.push(`${combinedPurchase.words.toLocaleString()} word credits`);
        }
        if (combinedPurchase.images) {
          parts.push(
            `${combinedPurchase.images.toLocaleString()} image credits`
          );
        }

        if (parts.length > 0) {
          friendlyMessage = `🎉 Payment Successful! ${parts.join(
            ' and '
          )} have been added to your account.`;
        } else {
          // Fallback if no counts found
          friendlyMessage = `🎉 Payment Successful! Credits have been added to your account.`;
        }
      }

      // If no combined purchase or parsing failed, create single package message
      // Note: Only show single package message if NOT a combined purchase
      if (!friendlyMessage && !combinedPurchase) {
        if (packageType === 'images') {
          // Extract count from packageId (e.g., "images-50" -> "50")
          const imageCount = packageId.replace('images-', '');
          friendlyMessage = `🎉 Payment Successful! ${imageCount} image credits have been added to your account.`;
        } else if (packageType === 'words') {
          // For words, packageId is just the number
          friendlyMessage = `🎉 Payment Successful! ${packageId} word credits have been added to your account.`;
        } else {
          // Fallback to original message
          friendlyMessage = `🎉 Payment Successful! ${packageId} ${packageType} credits have been added to your account.`;
        }
      }

      this.paymentSuccessMessage = friendlyMessage;

      // Call new API endpoint to confirm payment and update counts immediately
      try {
        console.log('💳 Calling payment confirmation API...');

        // Get auth token and create headers
        const token = await this.authService.getAuthToken();
        if (!token) {
          throw new Error('No auth token available');
        }

        const authHeaders = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        console.log('💳 Auth headers:', authHeaders);

        // Check if this is a combined purchase and include image info
        let requestBody: any = {
          sessionId,
          packageId,
          packageType,
        };

        if (combinedPurchase) {
          console.log(
            '📦 Including combined purchase info in API call:',
            combinedPurchase
          );
          // Add image purchase info to request if available
          if (combinedPurchase.imagePackageId && combinedPurchase.images) {
            requestBody.combinedPurchase = {
              imagePackageId: combinedPurchase.imagePackageId,
              images: combinedPurchase.images,
              imagePrice: combinedPurchase.imagePrice,
            };
          }
        }

        const response = await this.http
          .post(`${environment.apiUrl}/api/payment/confirm`, requestBody, {
            headers: authHeaders,
          })
          .toPromise();

        console.log('✅ Payment confirmed by API:', response);
        console.log(
          '✅ Payment confirmed - Full response:',
          JSON.stringify(response, null, 2)
        );

        // If quotaSettings are provided in the response, use them directly
        // This ensures we get the updated data immediately without waiting for a refresh
        const apiResponse = response as any;
        console.log('📊 Checking for quotaSettings in response...', {
          hasQuotaSettings: !!apiResponse?.quotaSettings,
          quotaSettings: apiResponse?.quotaSettings,
          responseKeys: Object.keys(apiResponse || {}),
        });

        if (apiResponse?.quotaSettings) {
          console.log(
            '📊 Using quotaSettings from API response:',
            apiResponse.quotaSettings
          );
          // Update the word usage service with the new quota settings directly
          // This ensures immediate UI update
          this.wordUsageService.updateQuotaSettingsFromResponse(
            apiResponse.quotaSettings
          );
        } else {
          console.warn(
            '⚠️ No quotaSettings in API response - will rely on refresh'
          );
        }

        // Refresh usage stats to show updated counts
        // Always refresh regardless of alreadyProcessed flag
        // Payment is processed immediately by this API call, not by webhook
        this.wordUsageService.forceRefreshData();
        this.imageUsageService.forceRefreshData();

        // If already processed (duplicate API call), wait a moment then refresh again to ensure UI is updated
        if (apiResponse?.alreadyProcessed) {
          console.log(
            '⚠️ Payment already processed (duplicate API call), refreshing data after delay...'
          );
          setTimeout(() => {
            this.wordUsageService.forceRefreshData();
            this.imageUsageService.forceRefreshData();
          }, 1000);
        }
      } catch (apiError) {
        console.error('❌ Error confirming payment with API:', apiError);
        // Still show success message even if API call fails
        // User can manually refresh or try again
      } finally {
        // Clear combined purchase info from sessionStorage after processing
        if (combinedPurchase) {
          sessionStorage.removeItem('combined_purchase');
          console.log('🧹 Cleared combined_purchase from sessionStorage');
        }
      }

      // Clean up URL
      this.clearPaymentSuccessParams();

      // IMPORTANT: Clear all sessionStorage related to payment to ensure clean state
      // This prevents any lingering states that might cause buttons to appear frozen
      sessionStorage.removeItem('pending_buy_action');
      sessionStorage.removeItem('combined_purchase');
      console.log(
        '🧹 Cleared all payment-related sessionStorage to ensure clean state'
      );

      // Ensure modal is closed (double-check)
      if (this.showBuyWordsModal) {
        console.log('🔄 Final check: Closing buy-words modal if still open');
        this.showBuyWordsModal = false;
      }

      // Force UI update to ensure all buttons are active
      this.cdr.detectChanges();

      console.log(
        '✅ Payment success processed successfully - all buttons should be active'
      );
    } catch (error) {
      console.error('❌ Error processing payment success:', error);
    }
  }

  /**
   * Clear payment success parameters from URL
   */
  private clearPaymentSuccessParams(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('paymentSuccess');
      url.searchParams.delete('packageId');
      url.searchParams.delete('packageType');
      url.searchParams.delete('sessionId');

      // Also clear from hash if present
      const hash = url.hash.substring(1);
      const newHashParams = new URLSearchParams(hash.replace(/&amp;/g, '&'));
      newHashParams.delete('paymentSuccess');
      newHashParams.delete('packageId');
      newHashParams.delete('packageType');
      newHashParams.delete('sessionId');
      url.hash = newHashParams.toString() ? `#${newHashParams.toString()}` : '';

      window.history.replaceState({}, document.title, url.toString());
      console.log('✅ URL cleaned up:', window.location.href);
    } catch (error) {
      console.error('❌ Error cleaning up URL:', error);
    }
  }

  /**
   * Check for payment success redirect and handle it
   */
  private async checkForPaymentSuccess() {
    try {
      console.log('💳 AppComponent: Checking for payment success redirect...');
      console.log('💳 AppComponent: Current URL:', window.location.href);
      console.log('💳 AppComponent: Search params:', window.location.search);
      console.log('💳 AppComponent: Hash fragment:', window.location.hash);

      // Check both query parameters and hash fragments for payment success
      let paymentSuccess = null;
      let packageId = null;
      let packageType = null;
      let sessionId = null;

      // First, try query parameters (current Stripe behavior)
      if (window.location.search) {
        let searchParams = window.location.search;
        if (searchParams.includes('&amp;')) {
          console.log(
            '🔧 Detected HTML-encoded ampersands in query params, decoding...'
          );
          searchParams = searchParams.replace(/&amp;/g, '&');
        }

        const queryParams = new URLSearchParams(searchParams);
        paymentSuccess = queryParams.get('paymentSuccess');
        packageId =
          queryParams.get('packageId') || queryParams.get('packageld'); // Handle typo
        packageType = queryParams.get('packageType');
        sessionId = queryParams.get('sessionId');

        console.log('💳 AppComponent: Query params:', {
          paymentSuccess,
          packageId,
          packageType,
          sessionId,
          searchParams: searchParams,
        });
      }

      // Always check hash fragments (Stripe redirects to hash, not query params)
      if (window.location.hash) {
        let hashParams = window.location.hash;
        console.log('🔍 Raw hash before processing:', hashParams);

        if (hashParams.startsWith('#')) {
          hashParams = hashParams.substring(1); // Remove the # symbol
        }

        console.log('🔍 Hash after removing #:', hashParams);

        // Handle both regular and HTML-encoded ampersands
        if (hashParams.includes('&amp;')) {
          console.log(
            '🔧 Detected HTML-encoded ampersands in hash, decoding...'
          );
          const beforeReplace = hashParams;
          hashParams = hashParams.replace(/&amp;/g, '&');
          console.log('🔧 Before replace:', beforeReplace);
          console.log('🔧 After replace:', hashParams);
        }

        console.log('🔍 Hash params string to parse:', hashParams);
        const hashUrlParams = new URLSearchParams(hashParams);

        // Log all params from URLSearchParams
        console.log(
          '🔍 All hash params keys:',
          Array.from(hashUrlParams.keys())
        );
        console.log(
          '🔍 All hash params entries:',
          Array.from(hashUrlParams.entries())
        );

        paymentSuccess = hashUrlParams.get('paymentSuccess');
        packageId =
          hashUrlParams.get('packageId') || hashUrlParams.get('packageld'); // Handle typo
        packageType = hashUrlParams.get('packageType');
        sessionId = hashUrlParams.get('sessionId');

        console.log('💳 AppComponent: Hash params after parsing:', {
          paymentSuccess,
          packageId,
          packageType,
          sessionId,
          hashFragment: window.location.hash,
          hashParams: hashParams,
        });
      }

      console.log('💳 AppComponent: Final params:', {
        paymentSuccess,
        packageId,
        packageType,
        sessionId,
        fullUrl: window.location.href,
        allParams: {
          query: window.location.search,
          hash: window.location.hash,
        },
      });

      if (paymentSuccess === 'true' && packageId && packageType && sessionId) {
        // Prevent multiple executions of payment processing
        if (this.paymentSuccessHandled) {
          console.log(
            '⚠️ AppComponent: Payment success already handled, skipping processing but cleaning URL...'
          );

          // Use Angular Router for URL cleanup with comprehensive logging
          setTimeout(() => {
            try {
              console.log(
                '🔄 AppComponent: Attempting Angular Router navigation to /home...'
              );
              console.log('🔄 AppComponent: Router instance:', this.router);
              console.log(
                '🔄 AppComponent: Current router URL:',
                this.router.url
              );
              console.log(
                '🔄 AppComponent: Current window URL:',
                window.location.href
              );

              this.router
                .navigateByUrl('/', { replaceUrl: true })
                .then(
                  (success) => {
                    console.log(
                      '✅ AppComponent: Angular Router navigation successful:',
                      success
                    );
                    console.log(
                      '✅ AppComponent: New router URL:',
                      this.router.url
                    );
                    console.log(
                      '✅ AppComponent: New window URL:',
                      window.location.href
                    );
                  },
                  (error) => {
                    console.error(
                      '❌ AppComponent: Angular Router navigation failed:',
                      error
                    );

                    // Fallback to full navigation (syncs browser + Angular)
                    console.log(
                      '🔄 AppComponent: Falling back to window.location.replace...'
                    );
                    const cleanUrl = window.location.origin + '/';
                    window.location.replace(cleanUrl);
                    console.log(
                      '✅ AppComponent: URL cleaned with window.location.replace to:',
                      cleanUrl
                    );
                  }
                );
            } catch (error) {
              console.error(
                '❌ AppComponent: Error with Angular Router:',
                error
              );

              // Fallback to full navigation (syncs browser + Angular)
              console.log(
                '🔄 AppComponent: Falling back to window.location.replace...'
              );
              const cleanUrl = window.location.origin + '/';
              window.location.replace(cleanUrl);
              console.log(
                '✅ AppComponent: URL cleaned with window.location.replace to:',
                cleanUrl
              );
            }
          }, 100); // Shorter delay since processing is already done

          return;
        }
        this.paymentSuccessHandled = true;

        console.log('💳 AppComponent: Payment success detected:', {
          packageId,
          packageType,
          sessionId,
        });

        // Process payment success directly
        console.log('💳 AppComponent: Processing payment success...');
        // processPaymentSuccess already shows the success banner with the correct message
        // No need to show it again here
        await this.processPaymentSuccess(packageId, packageType, sessionId);
        console.log('✅ AppComponent: Payment success processed successfully');

        // Refresh usage stats to show updated credits
        console.log('🔄 AppComponent: Refreshing usage stats after payment...');
        await this.refreshUsageStatsAfterPayment();

        // Return to main app via Angular Router. history.replaceState() alone
        // desyncs the router from the address bar (e.g. shows /home while the
        // outlet still thinks /payment-success), which breaks the UI until refresh.
        console.log('🔄 AppComponent: Navigating to main app after payment...');
        try {
          await this.router.navigateByUrl('/', { replaceUrl: true });
          console.log('✅ AppComponent: Navigated to / after payment success');
        } catch (navError) {
          console.error(
            '❌ AppComponent: Router navigation after payment failed:',
            navError
          );
          window.location.replace(window.location.origin + '/');
        }

        console.log('✅ AppComponent: Payment success handled completely');
      } else {
        console.log('💳 AppComponent: No payment success detected');
      }
    } catch (error) {
      console.error('❌ AppComponent: Error handling payment success:', error);
    }
  }

  /**
   * Dismiss payment success banner
   */
  dismissPaymentSuccessBanner(): void {
    console.log('🎉 Dismissing payment success banner');
    this.showPaymentSuccessBanner = false;
    this.paymentSuccessMessage = '';
  }

  /**
   * Show OpenAI quota error banner
   */
  showQuotaErrorBannerMessage(message: string): void {
    console.log('❌ Showing OpenAI quota error banner:', message);
    this.quotaErrorMessage = message;
    this.showQuotaErrorBanner = true;
    this.cdr.detectChanges();
  }

  /**
   * Dismiss OpenAI quota error banner
   */
  dismissQuotaErrorBanner(): void {
    console.log('❌ Dismissing OpenAI quota error banner');
    this.showQuotaErrorBanner = false;
    this.quotaErrorMessage = '';
  }

  /**
   * Show payment success banner (for mobile)
   */
  showPaymentSuccessBannerMessage(message: string): void {
    console.log('🎉 showPaymentSuccessBannerMessage called');
    console.log('🎉 Message:', message);
    console.log('🎉 Message type:', typeof message);
    console.log('🎉 Message length:', message?.length);

    if (!message) {
      console.warn('⚠️ Empty message received, not showing banner');
      return;
    }

    // Simple approach - just like iOS
    this.paymentSuccessMessage = message;
    this.showPaymentSuccessBanner = true;
    console.log(
      '🎉 Set banner state. showPaymentSuccessBanner:',
      this.showPaymentSuccessBanner
    );
    console.log('🎉 paymentSuccessMessage value:', this.paymentSuccessMessage);

    this.cdr.detectChanges();
    console.log('🎉 Change detection triggered');

    console.log('🎉 Banner shown. State:', this.showPaymentSuccessBanner);
    console.log('🎉 Current banner message:', this.paymentSuccessMessage);

    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      if (this.showPaymentSuccessBanner) {
        this.dismissPaymentSuccessBanner();
      }
    }, 8000);
  }

  /**
   * Show payment success toast message
   */
  private async showPaymentSuccessToast(message: string): Promise<void> {
    try {
      console.log('🎉 Creating toast with message:', message);

      const toast = await this.toastController.create({
        message: message,
        duration: 8000, // Longer duration
        position: 'top',
        color: 'success',
        cssClass: 'payment-success-toast', // Add custom CSS class
        buttons: [
          {
            text: 'Close',
            role: 'cancel',
          },
        ],
      });

      console.log('🎉 Toast created successfully:', toast);

      // Add event listeners for debugging
      toast.onDidDismiss().then(() => {
        console.log('🎉 Toast dismissed');
      });

      await toast.present();
      console.log('✅ Payment success toast presented successfully');

      // Force UI update
      setTimeout(() => {
        console.log('🎉 Toast should be visible now');
      }, 100);
    } catch (error) {
      console.error('❌ Error showing payment success toast:', error);

      // Fallback: try with different settings
      try {
        console.log('🔄 Trying fallback toast...');
        const fallbackToast = await this.toastController.create({
          message: message,
          duration: 5000,
          position: 'bottom',
          color: 'primary',
        });
        await fallbackToast.present();
        console.log('✅ Fallback toast presented');
      } catch (fallbackError) {
        console.error('❌ Fallback toast also failed:', fallbackError);
      }
    }
  }

  /**
   * Refresh usage stats after payment
   */
  private async refreshUsageStatsAfterPayment(): Promise<void> {
    try {
      // Force refresh both word and image usage stats
      this.wordUsageService.forceRefreshData();
      this.imageUsageService.forceRefreshData();
      console.log('✅ Usage stats refreshed after payment');
    } catch (error) {
      console.error('❌ Error refreshing usage stats after payment:', error);
    }
  }

  /**
   * Setup Capacitor Keyboard plugin listeners
   */
  private setupKeyboardListeners(): void {
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      const { Keyboard } = (window as any).Capacitor.Plugins;
      if (Keyboard) {
        // Listen for keyboard show event
        Keyboard.addListener('keyboardWillShow', (info: any) => {
          console.log('⌨️ Keyboard will show:', info);
          this.zone.run(() => {
            this.showKeyboardToolbar = true;
            // Get keyboard height from the event
            this.keyboardHeight = info.keyboardHeight || 0;
            console.log('⌨️ Keyboard height:', this.keyboardHeight);
          });
        });

        // Listen for keyboard hide event
        Keyboard.addListener('keyboardWillHide', () => {
          console.log('⌨️ Keyboard will hide');
          this.zone.run(() => {
            this.showKeyboardToolbar = false;
            this.activeTextarea = null;
            this.keyboardHeight = 0;
          });
        });
      }
    }
  }

  private setupAuthListener() {
    console.log('🔗 Setting up auth listener in AppComponent');
    let previousUserEmail: string | null =
      this.currentUser?.email?.trim() || null;

    this.authSubscription = this.authService.currentUser$.subscribe(
      (user: User | null) => {
        console.log('👤 Auth state changed - user:', user);
        console.log('👤 Previous user email:', previousUserEmail);
        console.log('👤 Setting isLoggedIn to:', !!user);

        const currentUserEmail = user?.email?.trim() || null;
        const isDifferentUser =
          previousUserEmail &&
          currentUserEmail &&
          previousUserEmail !== currentUserEmail;

        // If different user detected, clear all UI state
        // Be more aggressive on mobile, but also clear on web
        if (isDifferentUser) {
          console.log(
            '🔄 Different user detected in auth listener, clearing UI state'
          );
          console.log('🔄 Old user:', previousUserEmail);
          console.log('🔄 New user:', currentUserEmail);
          console.log('🔄 Platform is mobile:', this.isMobile);

          // Clear conversation arrays
          this.enConversation.set([]);
          this.kyConversation.set([]);

          // Clear text inputs
          this.userTextInput = '';
          this.partnerTextInput = '';

          // Clear error messages
          this.wordLimitErrorMessage = null;

          // Reset section visibility
          this.showUserSection.set(true);
          this.showPartnerSection.set(false);

          // Force change detection (more aggressive on mobile)
          if (this.isMobile) {
            setTimeout(() => {
              this.cdr.detectChanges();
              // Force another change detection after a short delay on mobile
              setTimeout(() => {
                this.cdr.detectChanges();
              }, 200);
            }, 100);
          } else {
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 50);
          }
        }

        this.currentUser = user;
        this.isLoggedIn = !!user;
        this.isCheckingAuth = false; // Authentication check complete
        this.isLoggingOut = false; // Reset logout state

        // On mobile, ensure section visibility is set and force change detection
        if (this.isMobile && user) {
          console.log(
            '📱 Mobile: User logged in via auth service, ensuring all elements are visible'
          );
          // IMPORTANT: Reset mobile menu state to ensure buttons are visible
          this.isMobileMenuOpen = false;
          console.log(
            '📱 Mobile: Reset isMobileMenuOpen to false to ensure buttons are visible'
          );
          // Ensure sections are visible
          this.showUserSection.set(true);
          this.showPartnerSection.set(false);
          // Force multiple change detection cycles
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 50);
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 150);
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 300);
        }

        // Update previous user email
        previousUserEmail = currentUserEmail;

        // Update conversation history when user changes
        if (user && user.userId) {
          console.log(
            '👤 User logged in, loading their conversation history:',
            user.userId
          );
          this.conversationHistoryService.setCurrentUser(user.userId);
        } else {
          console.log('👤 User logged out, resetting to anonymous history');
          this.conversationHistoryService.setCurrentUser('anonymous');
          previousUserEmail = null;
        }
      }
    );

    // Also register a callback for when AuthService notifies user change
    this.authService.onUserChange((userId: string) => {
      console.log('🔄 User changed callback triggered:', userId);
      this.conversationHistoryService.setCurrentUser(userId);
    });
  }

  private loadLanguageSelections() {
    try {
      let userLang = localStorage.getItem(this.USER_LANGUAGE_KEY);
      let partnerLang = localStorage.getItem(this.PARTNER_LANGUAGE_KEY);
      const speed = localStorage.getItem(this.SPEAKING_SPEED_KEY);
      const userVoice = localStorage.getItem(this.USER_VOICE_SELECTION_KEY);
      const partnerVoice = localStorage.getItem(
        this.PARTNER_VOICE_SELECTION_KEY
      );
      // Backward compatibility: check old key
      const oldVoice = localStorage.getItem('selectedVoice');
      const skipAudio = localStorage.getItem(this.SKIP_AUDIO_GENERATION_KEY);

      // Backward compatibility: convert old 'zh' to 'zh-CN' (Mandarin)
      if (userLang === 'zh') {
        userLang = 'zh-CN';
      }
      if (partnerLang === 'zh') {
        partnerLang = 'zh-CN';
      }

      if (
        userLang &&
        [
          'en',
          'ko',
          'zh-CN',
          'zh-HK',
          'ky',
          'ru',
          'kk',
          'tg',
          'tk',
          'uz',
          'fa',
        ].includes(userLang)
      ) {
        this.selectedUserLanguage.set(userLang as any);
      }
      if (
        partnerLang &&
        [
          'en',
          'ko',
          'zh-CN',
          'zh-HK',
          'ky',
          'ru',
          'kk',
          'tg',
          'tk',
          'uz',
          'fa',
        ].includes(partnerLang)
      ) {
        this.selectedPartnerLanguage.set(partnerLang as any);
      }
      if (speed) {
        this.speakingSpeed = parseFloat(speed);
      }
      if (userVoice) {
        this.selectedUserVoice = userVoice;
      } else if (oldVoice) {
        // Migrate old voice to user voice
        this.selectedUserVoice = oldVoice;
        localStorage.setItem(this.USER_VOICE_SELECTION_KEY, oldVoice);
      }
      if (partnerVoice) {
        this.selectedPartnerVoice = partnerVoice;
      }
      if (skipAudio !== null) {
        this.skipAudioGeneration.set(skipAudio === 'true');
      }
    } catch (error) {
      console.error(
        'Error loading language selections from local storage:',
        error
      );
    }
  }

  onLanguageChanged(event: { userLanguage: string; partnerLanguage: string }) {
    // Update the language selections from the navigation component
    this.selectedUserLanguage.set(event.userLanguage as any);
    this.selectedPartnerLanguage.set(event.partnerLanguage as any);
    this.saveLanguageSelections();

    // NOTE: Removed translationService.setLanguage() call to prevent UI language changes
    // Translation language selection should only affect translation pairs, not UI labels

    console.log(
      '🔄 AppComponent: Translation language changed - User:',
      this.selectedUserLanguage(),
      'Partner:',
      this.selectedPartnerLanguage()
    );

    // Prime languages when they change to ensure backend is ready
    this.primeLanguages();
  }

  onUserLanguageChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newLanguage = selectElement.value as
      | 'en'
      | 'ko'
      | 'zh-CN'
      | 'zh-HK'
      | 'ky'
      | 'ru'
      | 'kk'
      | 'tg'
      | 'tk'
      | 'uz'
      | 'fa';

    // If user selects same language as partner, switch partner to a different language
    if (newLanguage === this.selectedPartnerLanguage()) {
      // Find a different language for partner
      const availableLanguages = [
        'en',
        'ko',
        'zh-CN',
        'zh-HK',
        'ky',
        'ru',
        'kk',
        'tg',
        'tk',
        'uz',
        'fa',
      ];
      const differentLanguage = availableLanguages.find(
        (lang) => lang !== newLanguage
      );
      if (differentLanguage) {
        this.selectedPartnerLanguage.set(differentLanguage as any);
      }
    }

    this.selectedUserLanguage.set(newLanguage);
    this.saveLanguageSelections();
    
    // Prime languages when they change to ensure backend is ready
    this.primeLanguages();
  }

  onPartnerLanguageChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newLanguage = selectElement.value as
      | 'en'
      | 'ko'
      | 'zh-CN'
      | 'zh-HK'
      | 'ky'
      | 'ru'
      | 'kk'
      | 'tg'
      | 'tk'
      | 'uz'
      | 'fa';

    // If partner selects same language as user, switch user to a different language
    if (newLanguage === this.selectedUserLanguage()) {
      // Find a different language for user
      const availableLanguages = [
        'en',
        'ko',
        'zh-CN',
        'zh-HK',
        'ky',
        'ru',
        'kk',
        'tg',
        'tk',
        'uz',
        'fa',
      ];
      const differentLanguage = availableLanguages.find(
        (lang) => lang !== newLanguage
      );
      if (differentLanguage) {
        this.selectedUserLanguage.set(differentLanguage as any);
      }
    }

    this.selectedPartnerLanguage.set(newLanguage);
    this.saveLanguageSelections();
    
    // Prime languages when they change to ensure backend is ready
    this.primeLanguages();
  }

  onSpeedChange(newSpeed: number) {
    this.speakingSpeed = newSpeed;
    this.saveLanguageSelections();
  }

  onDiarizationToggled(useDiarization: boolean) {
    this.useDiarization = useDiarization;
    console.log('👥 Diarization mode changed:', useDiarization);

    // Clear previous diarization results when toggling off
    if (!useDiarization) {
      this.clearDiarizationResults();
    }
  }

  getUserLanguages() {
    const allLanguages = [
      { value: 'en', label: 'English' },
      { value: 'ko', label: 'Korean (한국어)' },
      { value: 'zh', label: 'Chinese (中文)' },
      { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
      { value: 'ru', label: 'Russian (Русский)' },
      { value: 'kk', label: 'Kazakh (Қазақша)' },
      { value: 'tg', label: 'Tajik (Тоҷикӣ)' },
      { value: 'tk', label: 'Turkmen (Türkmençe)' },
      { value: 'uz', label: "Uzbek (O'zbekcha)" },
    ];
    return allLanguages.filter(
      (lang) => lang.value !== this.selectedPartnerLanguage()
    );
  }

  getPartnerLanguages() {
    const allLanguages = [
      { value: 'en', label: 'English' },
      { value: 'ko', label: 'Korean (한국어)' },
      { value: 'zh', label: 'Chinese (中文)' },
      { value: 'ky', label: 'Kyrgyz (Кыргызча)' },
      { value: 'ru', label: 'Russian (Русский)' },
      { value: 'kk', label: 'Kazakh (Қазақша)' },
      { value: 'tg', label: 'Tajik (Тоҷикӣ)' },
      { value: 'tk', label: 'Turkmen (Türkmençe)' },
      { value: 'uz', label: "Uzbek (O'zbekcha)" },
    ];
    return allLanguages.filter(
      (lang) => lang.value !== this.selectedUserLanguage()
    );
  }

  selectVoice(voiceId: string, lane?: 'user' | 'partner') {
    if (lane === 'partner') {
      this.selectedPartnerVoice = voiceId;
      localStorage.setItem(this.PARTNER_VOICE_SELECTION_KEY, voiceId);
    } else {
      // Default to user voice for backward compatibility
      this.selectedUserVoice = voiceId;
      localStorage.setItem(this.USER_VOICE_SELECTION_KEY, voiceId);
    }
    this.saveLanguageSelections();
  }

  private saveLanguageSelections() {
    try {
      localStorage.setItem(this.USER_LANGUAGE_KEY, this.selectedUserLanguage());
      localStorage.setItem(
        this.PARTNER_LANGUAGE_KEY,
        this.selectedPartnerLanguage()
      );
      localStorage.setItem(
        this.SPEAKING_SPEED_KEY,
        this.speakingSpeed.toString()
      );
      localStorage.setItem(
        this.USER_VOICE_SELECTION_KEY,
        this.selectedUserVoice
      );
      localStorage.setItem(
        this.PARTNER_VOICE_SELECTION_KEY,
        this.selectedPartnerVoice
      );
      localStorage.setItem(
        this.SKIP_AUDIO_GENERATION_KEY,
        this.skipAudioGeneration().toString()
      );
    } catch (error) {
      console.error(
        'Error saving language selections to local storage:',
        error
      );
    }
  }

  /**
   * Prime both user and partner languages to ensure backend is ready for first translation
   * This fixes the issue where the first translation doesn't work but subsequent ones do
   */
  private async primeLanguages(): Promise<void> {
    if (!this.isLoggedIn) {
      console.log('⏭️ Skipping language priming - user not logged in');
      return;
    }

    const userLang = this.selectedUserLanguage();
    const partnerLang = this.selectedPartnerLanguage();

    if (!userLang || !partnerLang) {
      console.log('⏭️ Skipping language priming - languages not set');
      return;
    }

    try {
      console.log('🔥 Priming languages - User:', userLang, 'Partner:', partnerLang);
      
      // Prime both languages in parallel
      const [userResult, partnerResult] = await Promise.allSettled([
        this.svc.primeLanguage(userLang as any),
        this.svc.primeLanguage(partnerLang as any),
      ]);

      if (userResult.status === 'fulfilled') {
        console.log('✅ User language primed:', userLang);
      } else {
        console.warn('⚠️ Failed to prime user language:', userResult.reason);
      }

      if (partnerResult.status === 'fulfilled') {
        console.log('✅ Partner language primed:', partnerLang);
      } else {
        console.warn('⚠️ Failed to prime partner language:', partnerResult.reason);
      }
    } catch (error) {
      console.warn('⚠️ Error priming languages (non-critical):', error);
      // Don't throw - priming is optional and shouldn't block the app
    }
  }

  toggleSkipAudioGeneration() {
    console.log('🔊 Toggling skip audio generation');
    const newValue = !this.skipAudioGeneration();
    this.skipAudioGeneration.set(newValue);
    localStorage.setItem(this.SKIP_AUDIO_GENERATION_KEY, newValue.toString());
    console.log('🔊 Skip audio generation set to:', newValue);
  }

  // Authentication methods
  onLoginSuccess(user: {
    email: string;
    name: string;
    roles?: string[];
    permissions?: string[];
  }) {
    console.log('🔄 onLoginSuccess called with:', user);
    console.log('🔄 Current user before login:', this.currentUser);
    console.log('🔄 Platform is mobile:', this.isMobile);
    console.log('🔄 isCheckingAuth before:', this.isCheckingAuth);
    console.log('🔄 isLoggedIn before:', this.isLoggedIn);

    // On mobile, immediately set flags and currentUser to ensure UI shows
    if (this.isMobile) {
      console.log(
        '📱 Mobile: Setting isCheckingAuth to false, isLoggedIn to true, and currentUser immediately'
      );
      this.isCheckingAuth = false;
      this.isLoggedIn = true;
      // IMPORTANT: Reset mobile menu state to ensure buttons are visible
      this.isMobileMenuOpen = false;
      console.log(
        '📱 Mobile: Reset isMobileMenuOpen to false to ensure buttons are visible'
      );
      // Set currentUser immediately so navigation and other components render properly
      this.currentUser = {
        userId: user.email || 'anonymous',
        email: user.email || '',
        name: user.name || '',
        roles: user.roles || [],
        permissions: user.permissions || [],
      };
      console.log('📱 Mobile: Set currentUser immediately:', this.currentUser);
      // Force change detection immediately on mobile
      this.cdr.detectChanges();
    }

    // Clear old user data from localStorage before processing new login
    // This ensures we don't load stale data from a previous user
    const oldUser = localStorage.getItem('currentUser');
    let isDifferentUser = false;

    if (oldUser) {
      try {
        const oldUserObj = JSON.parse(oldUser);
        if (oldUserObj.email !== user.email) {
          isDifferentUser = true;
          console.log('🔄 Different user detected, clearing old user data');
          console.log('🔄 Old user:', oldUserObj.email);
          console.log('🔄 New user:', user.email);

          // On mobile, be more aggressive about clearing old data
          if (this.isMobile) {
            console.log(
              '📱 Mobile platform detected - performing aggressive cleanup'
            );

            // Clear old conversation history
            this.enConversation.set([]);
            this.kyConversation.set([]);

            // Clear old text inputs
            this.userTextInput = '';
            this.partnerTextInput = '';

            // Clear old error messages
            this.wordLimitErrorMessage = null;

            // Force change detection on mobile
            this.cdr.detectChanges();
          }
        }
      } catch (e) {
        console.error('🔄 Error parsing old user data:', e);
      }
    }

    // Reset section visibility to default state when new user logs in
    // Ensure user section is visible and partner section is hidden
    this.showUserSection.set(true);
    this.showPartnerSection.set(false);

    // Clear conversation arrays when new user logs in
    this.enConversation.set([]);
    this.kyConversation.set([]);

    // Clear text inputs
    this.userTextInput = '';
    this.partnerTextInput = '';

    // Clear error messages
    this.wordLimitErrorMessage = null;

    // On mobile, force multiple change detection cycles to ensure all elements render
    if (this.isMobile) {
      console.log('📱 Mobile: Forcing change detection cycles after relogin');
      // Immediate change detection
      this.cdr.detectChanges();
      // Additional change detection cycles with delays to ensure view is fully rendered
      setTimeout(() => {
        this.cdr.detectChanges();
        // Ensure sections are visible
        this.showUserSection.set(true);
        this.showPartnerSection.set(false);
      }, 50);
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 150);
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 300);
    }

    // On mobile, add a small delay to ensure state is cleared before login
    const loginAction = () => {
      // Only call traditional login if we don't already have an Auth0 user
      // Auth0 logins are handled by the Auth0 user$ observable
      // Note: WordUsageService will automatically reload data via currentUser$ subscription,
      // so we don't need to call refreshData() here
      if (!user.email || user.email.trim() === '') {
        console.log(
          '🔄 Auth0 login detected (no email), skipping traditional login call'
        );
        // WordUsageService will auto-refresh via currentUser$ subscription
      } else {
        console.log(
          '🔄 Traditional login detected (has email), calling authService.login'
        );
        this.authService.login(
          user.email,
          user.name,
          user.roles,
          user.permissions
        );
        // WordUsageService will auto-refresh via currentUser$ subscription
      }
    };

    // On mobile with different user, add delay to ensure cleanup completes
    if (this.isMobile && isDifferentUser) {
      console.log('📱 Mobile + different user - delaying login action');
      setTimeout(() => {
        loginAction();
        // Force multiple change detection cycles after login on mobile
        setTimeout(() => {
          this.cdr.detectChanges();
          // Ensure sections are visible
          this.showUserSection.set(true);
          this.showPartnerSection.set(false);
        }, 100);
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 300);
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 500);
      }, 200);
    } else {
      loginAction();
      // On mobile, also force change detection after login action
      if (this.isMobile) {
        setTimeout(() => {
          this.cdr.detectChanges();
          // Ensure sections are visible
          this.showUserSection.set(true);
          this.showPartnerSection.set(false);
        }, 100);
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 300);
      }
    }

    // Prime languages after login to ensure backend is ready for first translation
    setTimeout(() => {
      console.log('🔥 Priming languages after login...');
      this.primeLanguages();
    }, 500);

    // Check for buy parameters after login to open purchase modal
    // Wait a bit for the user state to be set (longer delay on mobile)
    const buyParamDelay = this.isMobile ? 1500 : 1000;
    setTimeout(() => {
      console.log('🛒 Checking for buy parameters after login...');
      this.checkForBuyParameter();
    }, buyParamDelay);

    // Re-initialize textarea resize handlers after login
    // Wait a bit for the view to be ready (longer delay on mobile)
    const resizeDelay = this.isMobile ? 800 : 500;
    setTimeout(() => {
      console.log('🔧 Re-initializing textarea resize handlers after login');
      this.setupTextareaResize();

      // On mobile, force change detection after resize setup
      if (this.isMobile) {
        this.cdr.detectChanges();
      }
    }, resizeDelay);

    // Retry after a longer delay if first attempt fails (longer delay on mobile)
    const retryDelay = this.isMobile ? 2000 : 1500;
    setTimeout(() => {
      if (
        this.userTextArea?.nativeElement &&
        this.partnerTextArea?.nativeElement
      ) {
        console.log('🔧 Retrying textarea resize setup after login');
        this.setupTextareaResize();

        // On mobile, force change detection after retry
        if (this.isMobile) {
          this.cdr.detectChanges();
        }
      }
    }, retryDelay);
  }

  onRegisterSuccess(user: {
    email: string;
    name: string;
    roles?: string[];
    permissions?: string[];
  }) {
    console.log('🔄 onRegisterSuccess called with:', user);
    console.log('🔄 Platform is mobile:', this.isMobile);
    console.log('🔄 isCheckingAuth before:', this.isCheckingAuth);
    console.log('🔄 isLoggedIn before:', this.isLoggedIn);

    // On mobile, immediately set flags and currentUser to ensure UI shows
    if (this.isMobile) {
      console.log(
        '📱 Mobile: Setting isCheckingAuth to false, isLoggedIn to true, and currentUser immediately'
      );
      this.isCheckingAuth = false;
      this.isLoggedIn = true;
      // IMPORTANT: Reset mobile menu state to ensure buttons are visible
      this.isMobileMenuOpen = false;
      console.log(
        '📱 Mobile: Reset isMobileMenuOpen to false to ensure buttons are visible'
      );
      // Set currentUser immediately so navigation and other components render properly
      this.currentUser = {
        userId: user.email || 'anonymous',
        email: user.email || '',
        name: user.name || '',
        roles: user.roles || [],
        permissions: user.permissions || [],
      };
      console.log('📱 Mobile: Set currentUser immediately:', this.currentUser);
      // Force change detection immediately on mobile
      this.cdr.detectChanges();
    }

    // Reset section visibility to default state when new user registers
    // Ensure user section is visible and partner section is hidden
    this.showUserSection.set(true);
    this.showPartnerSection.set(false);

    // Clear conversation arrays when new user registers
    this.enConversation.set([]);
    this.kyConversation.set([]);

    // Clear text inputs
    this.userTextInput = '';
    this.partnerTextInput = '';

    // Clear error messages
    this.wordLimitErrorMessage = null;

    // On mobile, force multiple change detection cycles to ensure all elements render
    if (this.isMobile) {
      console.log('📱 Mobile: Forcing change detection cycles after register');
      // Immediate change detection
      this.cdr.detectChanges();
      // Additional change detection cycles with delays to ensure view is fully rendered
      setTimeout(() => {
        this.cdr.detectChanges();
        // Ensure sections are visible
        this.showUserSection.set(true);
        this.showPartnerSection.set(false);
      }, 50);
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 150);
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 300);
    }

    this.authService.login(user.email, user.name, user.roles, user.permissions);

    // On mobile, also force change detection after login action
    if (this.isMobile) {
      setTimeout(() => {
        this.cdr.detectChanges();
        // Ensure sections are visible
        this.showUserSection.set(true);
        this.showPartnerSection.set(false);
      }, 100);
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 300);
    }

    // On mobile, force another change detection after login
    if (this.isMobile) {
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    }

    // Prime languages after registration to ensure backend is ready for first translation
    setTimeout(() => {
      console.log('🔥 Priming languages after registration...');
      this.primeLanguages();
    }, 500);

    // Check for buy parameters after registration to open purchase modal
    // Wait a bit for the user state to be set
    setTimeout(() => {
      console.log('🛒 Checking for buy parameters after registration...');
      this.checkForBuyParameter();
    }, 1000);
  }

  onMenuStateChanged(isOpen: boolean) {
    this.isMobileMenuOpen = isOpen;
  }

  onLogout() {
    console.log('🚪 Logging out user...');

    // Set logout state to prevent showing main page briefly
    this.isLoggingOut = true;

    // Clean up textarea resize listeners before logout
    this.textareaResizeCleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn(
          'Error cleaning up textarea resize listener on logout:',
          error
        );
      }
    });
    this.textareaResizeCleanupFunctions = [];
    console.log('🧹 Cleaned up textarea resize listeners on logout');

    // Clear authentication state
    this.authService.logout();

    // Reset user state
    this.currentUser = null;
    this.isLoggedIn = false;

    // Clear conversation history from service
    this.conversationHistoryService.clearAllHistory();

    // Clear local conversation arrays - using signal set()
    this.enConversation.set([]);
    this.kyConversation.set([]);

    // Clear current text inputs and translations
    this.userTextInput = '';
    this.partnerTextInput = '';
    this.currentEnText = '';
    this.currentKyText = '';
    this.currentKyInText = '';
    this.currentEnOutText = '';
    this.currentEnConfidence = 0;
    this.currentKyConfidence = 0;

    // Stop any ongoing recording
    if (this.isRecording) {
      this.stopRecording();
    }

    // Clear any processing states
    this.isProcessingTranslation = false;
    this.currentLane = null;

    // Clear audio buffers
    this.cleanupBuffers();

    // Reset language selections to defaults
    this.selectedUserLanguage.set('en');
    this.selectedPartnerLanguage.set('ky');
    this.saveLanguageSelections();

    // Close any open modals
    this.showHistoryModal = false;

    console.log('✅ User logged out successfully');
  }

  // Conversation history modal
  onShowHistory() {
    console.log(
      'AppComponent onShowHistory called, setting showHistoryModal to true'
    );
    this.showHistoryModal = true;
    console.log('showHistoryModal is now:', this.showHistoryModal);

    // Additional debugging
    setTimeout(() => {
      console.log('After timeout - showHistoryModal:', this.showHistoryModal);
      const modalElement = document.querySelector(
        'app-conversation-history-modal'
      ) as HTMLElement;
      console.log('Modal element in DOM:', modalElement);
      if (modalElement) {
        console.log(
          'Modal element visible:',
          modalElement.offsetParent !== null
        );
        console.log(
          'Modal element display:',
          window.getComputedStyle(modalElement).display
        );
      } else {
        console.log('❌ Modal element not found in DOM!');
        // Check if there are any elements with modal-related classes
        const modalDivs = document.querySelectorAll(
          '.fixed.inset-0, .bg-black.bg-opacity-50'
        );
        console.log('Modal-related divs found:', modalDivs.length);
        for (let i = 0; i < modalDivs.length; i++) {
          console.log(`Modal div ${i}:`, modalDivs[i]);
        }
      }
    }, 100);
  }

  onCloseHistory() {
    console.log(
      'AppComponent onCloseHistory() called, setting showHistoryModal to false'
    );
    this.showHistoryModal = false;
    console.log('showHistoryModal is now:', this.showHistoryModal);
  }

  onShowWordUsage() {
    console.log(
      'AppComponent onShowWordUsage called, refreshing word usage data...'
    );
    // Refresh word usage data from API to ensure up-to-date information
    this.wordUsageService.refreshData();
    this.showWordUsageModal = true;
    console.log('showWordUsageModal is now:', this.showWordUsageModal);
  }

  onShowAnalytics() {
    console.log(
      'AppComponent onShowAnalytics called, setting showAnalyticsModal to true'
    );
    this.showAnalyticsModal = true;
    console.log('showAnalyticsModal is now:', this.showAnalyticsModal);
  }

  onShowLanguageSettings() {
    console.log(
      '🔧 AppComponent onShowLanguageSettings called, setting showLanguageSettingsModal to true'
    );
    console.log(
      '🔧 Current showLanguageSettingsModal value:',
      this.showLanguageSettingsModal
    );
    this.showLanguageSettingsModal = true;
    console.log(
      '🔧 showLanguageSettingsModal is now:',
      this.showLanguageSettingsModal
    );
  }

  onCloseAnalytics() {
    console.log(
      'AppComponent onCloseAnalytics() called, setting showAnalyticsModal to false'
    );
    this.showAnalyticsModal = false;
    console.log('showAnalyticsModal is now:', this.showAnalyticsModal);
  }

  onCloseLanguageSettings() {
    console.log(
      'AppComponent onCloseLanguageSettings() called, setting showLanguageSettingsModal to false'
    );
    this.showLanguageSettingsModal = false;
    console.log(
      'showLanguageSettingsModal is now:',
      this.showLanguageSettingsModal
    );
  }

  onUILanguageChange(newUILanguage: string) {
    // Backward compatibility: convert old 'zh' to 'zh-CN' for UI
    if (newUILanguage === 'zh') {
      newUILanguage = 'zh-CN';
    }
    const language = newUILanguage as
      | 'en'
      | 'ko'
      | 'zh-CN'
      | 'zh-HK'
      | 'ky'
      | 'ru'
      | 'kk'
      | 'tg'
      | 'tk'
      | 'uz'
      | 'fa';

    console.log(
      '🔄 UI Language changing from',
      this.currentUILanguage(),
      'to',
      language
    );

    // Update the UI language signal
    this.currentUILanguage.set(language);

    // Update the translation service to load new UI translations
    this.translationService.setLanguage(language);

    // Save the UI language preference
    this.saveUILanguagePreference(language);

    console.log('✅ UI Language updated successfully');
  }

  private saveUILanguagePreference(language: string) {
    try {
      localStorage.setItem('ui_language', language);
      console.log('💾 UI Language preference saved to localStorage:', language);
    } catch (error) {
      console.error('Error saving UI language preference:', error);
    }
  }

  private loadUILanguagePreference() {
    try {
      const savedUILanguage = localStorage.getItem('ui_language');
      console.log(
        '📂 Loading UI language preference from localStorage:',
        savedUILanguage
      );

      if (
        savedUILanguage &&
        [
          'en',
          'ko',
          'zh',
          'zh-CN',
          'zh-HK',
          'ky',
          'ru',
          'kk',
          'tg',
          'tk',
          'uz',
          'fa',
          'es',
        ].includes(savedUILanguage)
      ) {
        this.currentUILanguage.set(savedUILanguage as any);
        this.translationService.setLanguage(savedUILanguage);
        console.log(
          '✅ UI Language preference loaded successfully:',
          savedUILanguage
        );
      } else {
        console.log(
          'ℹ️ No valid UI language preference found, using default: en'
        );
        // Ensure TranslationService is set to English if no preference found
        this.currentUILanguage.set('en');
        this.translationService.setLanguage('en');
      }
    } catch (error) {
      console.error('Error loading UI language preference:', error);
    }
  }

  onCloseWordUsage() {
    console.log(
      'AppComponent onCloseWordUsage() called, setting showWordUsageModal to false'
    );
    this.showWordUsageModal = false;
    console.log('showWordUsageModal is now:', this.showWordUsageModal);
  }

  onShowImageUsage() {
    console.log(
      'AppComponent onShowImageUsage called, refreshing image usage data...'
    );
    // Refresh image usage data from API to ensure up-to-date information
    this.imageUsageService.forceRefreshData();
    this.showImageUsageModal = true;
    this.cdr.detectChanges();
    console.log('showImageUsageModal is now:', this.showImageUsageModal);
  }

  onCloseImageUsage() {
    console.log(
      'AppComponent onCloseImageUsage() called, setting showImageUsageModal to false'
    );
    this.showImageUsageModal = false;
    console.log('showImageUsageModal is now:', this.showImageUsageModal);
  }

  /**
   * Get webapp URL based on environment (dev or prod)
   */
  getWebappUrl(): string {
    // Check if we're in dev mode (non-production environment)
    if (!environment.production) {
      return 'https://translation-app-dev-648c3.web.app';
    }
    // For production, use custom domain tilhub.net
    return 'https://tilhub.net';
  }

  /**
   * Check if we're in dev mode (mobile or webapp)
   */
  isDevMode(): boolean {
    return !environment.production;
  }

  // Check if platform is iOS
  isIOS(): boolean {
    return Capacitor.getPlatform() === 'ios';
  }

  async onShowBuyWords() {
    console.log('AppComponent onShowBuyWords called');

    // On mobile, redirect directly to webapp with parameter to auto-open modal
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Mobile detected - redirecting to webapp for purchase');
      // Pass ?buy=words parameter so webapp automatically opens the buy modal
      const webappUrl = `${this.getWebappUrl()}?buy=words`;
      try {
        await Browser.open({ url: webappUrl, windowName: '_blank' });
        console.log('✅ Opened webapp in browser with buy=words parameter');
      } catch (error) {
        console.error('❌ Error opening webapp:', error);
      }
      return; // Don't open modal on mobile
    }

    // On web, open the modal as usual
    console.log('🌐 Web detected - opening modal');
    this.showBuyWordsModal = true;
    this.wordLimitErrorMessage = null; // Clear any error messages
    this.cdr.detectChanges();
    console.log('showBuyWordsModal is now:', this.showBuyWordsModal);
  }

  onCloseBuyWords() {
    console.log(
      'AppComponent onCloseBuyWords() called, setting showBuyWordsModal to false'
    );
    this.showBuyWordsModal = false;

    // Clear sessionStorage when user manually closes the modal
    // This prevents the modal from re-opening when returning from webapp
    const storedBuyAction = sessionStorage.getItem('pending_buy_action');
    if (storedBuyAction) {
      console.log(
        '🧹 Cleared pending_buy_action from sessionStorage (manual close)'
      );
      sessionStorage.removeItem('pending_buy_action');
    }

    // Also clear combined_purchase if it exists
    if (sessionStorage.getItem('combined_purchase')) {
      console.log(
        '🧹 Cleared combined_purchase from sessionStorage (manual close)'
      );
      sessionStorage.removeItem('combined_purchase');
    }

    console.log('showBuyWordsModal is now:', this.showBuyWordsModal);
  }

  openContinuousConversationModal(): void {
    console.log(
      '[AppComponent] Opening continuous conversation modal for user section...'
    );
    // User section: user language -> partner language
    const userLang = this.selectedUserLanguage();
    const partnerLang = this.selectedPartnerLanguage();

    console.log('[AppComponent] User section languages:', {
      userLanguage: userLang,
      partnerLanguage: partnerLang,
    });

    this.continuousConversationSourceLanguage = userLang || 'en';
    this.continuousConversationTargetLanguage = partnerLang || 'en';

    console.log('[AppComponent] Set continuous conversation languages:', {
      sourceLanguage: this.continuousConversationSourceLanguage,
      targetLanguage: this.continuousConversationTargetLanguage,
    });

    this.showContinuousConversationModal = true;
  }

  openContinuousConversationModalForPartner(): void {
    console.log(
      '[AppComponent] Opening continuous conversation modal for partner section...'
    );
    // Partner section: partner language -> user language
    const partnerLang = this.selectedPartnerLanguage();
    const userLang = this.selectedUserLanguage();

    console.log('[AppComponent] Partner section languages:', {
      partnerLanguage: partnerLang,
      userLanguage: userLang,
    });

    this.continuousConversationSourceLanguage = partnerLang || 'en';
    this.continuousConversationTargetLanguage = userLang || 'en';

    console.log('[AppComponent] Set continuous conversation languages:', {
      sourceLanguage: this.continuousConversationSourceLanguage,
      targetLanguage: this.continuousConversationTargetLanguage,
    });

    this.showContinuousConversationModal = true;
  }

  onCloseContinuousConversation(): void {
    console.log('[AppComponent] Closing continuous conversation modal...');
    this.showContinuousConversationModal = false;
  }

  openBidirectionalConversationModal(): void {
    console.log(
      '[AppComponent] Opening bidirectional conversation modal for user section...'
    );
    const userLang = this.selectedUserLanguage();
    const partnerLang = this.selectedPartnerLanguage();

    console.log('[AppComponent] Bidirectional conversation languages:', {
      userLanguage: userLang,
      partnerLanguage: partnerLang,
    });

    this.bidirectionalConversationSourceLanguage = userLang || 'en';
    this.bidirectionalConversationTargetLanguage = partnerLang || 'en';

    console.log('[AppComponent] Set bidirectional conversation languages:', {
      sourceLanguage: this.bidirectionalConversationSourceLanguage,
      targetLanguage: this.bidirectionalConversationTargetLanguage,
    });

    this.showBidirectionalConversationModal = true;
  }

  openBidirectionalConversationModalForPartner(): void {
    console.log(
      '[AppComponent] Opening bidirectional conversation modal for partner section...'
    );
    const partnerLang = this.selectedPartnerLanguage();
    const userLang = this.selectedUserLanguage();

    console.log(
      '[AppComponent] Bidirectional conversation languages (partner section):',
      {
        partnerLanguage: partnerLang,
        userLanguage: userLang,
      }
    );

    // Partner section: partner language -> user language
    this.bidirectionalConversationSourceLanguage = partnerLang || 'en';
    this.bidirectionalConversationTargetLanguage = userLang || 'en';

    console.log('[AppComponent] Set bidirectional conversation languages:', {
      sourceLanguage: this.bidirectionalConversationSourceLanguage,
      targetLanguage: this.bidirectionalConversationTargetLanguage,
    });

    this.showBidirectionalConversationModal = true;
  }

  onCloseBidirectionalConversation(): void {
    console.log('[AppComponent] Closing bidirectional conversation modal...');
    this.showBidirectionalConversationModal = false;
  }

  openBroadcastTranslationModal(): void {
    console.log(
      '[AppComponent] Opening broadcast translation modal for user section...'
    );
    // User section: user language -> partner language
    const userLang = this.selectedUserLanguage();
    const partnerLang = this.selectedPartnerLanguage();

    this.broadcastSourceLanguage = userLang || 'en';
    this.broadcastTargetLanguage = partnerLang || 'en';

    console.log('[AppComponent] Set broadcast translation languages:', {
      sourceLanguage: this.broadcastSourceLanguage,
      targetLanguage: this.broadcastTargetLanguage,
    });

    this.showBroadcastTranslationModal = true;
  }

  openBroadcastTranslationModalForPartner(): void {
    console.log(
      '[AppComponent] Opening broadcast translation modal for partner section...'
    );
    // Partner section: partner language -> user language
    const partnerLang = this.selectedPartnerLanguage();
    const userLang = this.selectedUserLanguage();

    this.broadcastSourceLanguage = partnerLang || 'en';
    this.broadcastTargetLanguage = userLang || 'en';

    console.log('[AppComponent] Set broadcast translation languages:', {
      sourceLanguage: this.broadcastSourceLanguage,
      targetLanguage: this.broadcastTargetLanguage,
    });

    this.showBroadcastTranslationModal = true;
  }

  onCloseBroadcastTranslation(): void {
    console.log('[AppComponent] Closing broadcast translation modal...');
    this.showBroadcastTranslationModal = false;
  }

  async onWordsPurchased(wordsAmount: number) {
    console.log('Words purchased:', wordsAmount);
    // Force refresh word usage data after purchase
    await this.wordUsageService.forceRefreshData();
    this.onCloseBuyWords();
  }

  async onImagesPurchased(count: number) {
    console.log('Images purchased event received, count:', count);

    try {
      // Purchase is handled by the modal component or payment flow.
      // We just need to show success message and refresh data.

      // Show success message
      const successMsg = this.translationService.formatMessage(
        'imagesAddedSuccessfully',
        count
      );
      this.showToast(successMsg, 'success');

      // Force refresh image usage data from backend API to ensure latest data
      this.imageUsageService.forceRefreshData();

      // Close modal (use buy-words-modal close since everything is merged now)
      this.onCloseBuyWords();
    } catch (error) {
      console.error('Error processing image purchase event:', error);
      this.showToast(
        this.translationService.getTranslation('purchaseFailedTryAgain'),
        'error'
      );
    }
  }

  // Button click handler with promise support
  async onStartRecording(lane: 'user' | 'partner') {
    try {
      console.log(
        '🔍 BUTTON CLICKED - onStartRecording called for lane:',
        lane
      );
      console.log(`Starting recording for ${lane}...`);

      // Check microphone availability first
      if (!(await this.checkMicrophoneAvailability())) {
        return;
      }

      await this.startRecording(lane);
      console.log(`Recording started successfully for ${lane}`);
    } catch (error) {
      console.error(`Failed to start recording for ${lane}:`, error);
      // The error is already handled in startRecording method
    }
  }

  /**
   * Toggle recording - start if not recording, stop if recording
   */
  async toggleRecording(lane: 'user' | 'partner') {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.onStartRecording(lane);
    }
  }

  // Check if microphone is available
  private async checkMicrophoneAvailability(): Promise<boolean> {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert(
          'Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari.'
        );
        return false;
      }

      // For localhost, skip device enumeration and try direct access
      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      ) {
        console.log('🔧 Localhost detected - skipping device enumeration');
        console.log('🔧 Browser info:', {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          onLine: navigator.onLine,
        });
        return true; // Let the actual getUserMedia call handle the error
      }

      // Check available devices (only works after permission is granted)
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(
          (device) => device.kind === 'audioinput'
        );

        console.log('🎤 Available audio inputs:', audioInputs.length);

        // If no devices found, it might be because permission wasn't granted yet
        if (audioInputs.length === 0) {
          console.log('⚠️ No audio inputs found - might need permission first');
          return true; // Let getUserMedia handle the actual error
        }

        return true;
      } catch (enumError) {
        console.warn(
          '⚠️ Device enumeration failed, proceeding anyway:',
          enumError
        );
        return true; // Let getUserMedia handle the actual error
      }
    } catch (error) {
      console.error('❌ Error checking microphone availability:', error);
      // Don't show alert here, let the actual recording attempt handle it
      return true;
    }
  }

  // Spinner management
  showSpinner(
    spinnerType:
      | 'end'
      | 'postTranslation'
      | 'intermediate'
      | 'finalizing'
      | 'audioGeneration',
    lane: 'user' | 'partner'
  ) {
    this.zone.run(() => {
      this.currentLane = lane;

      // Clear translation flags when audio generation starts to keep state consistent
      // This ensures flags match UI state (audio generation spinner suppresses translation spinner)
      if (spinnerType === 'audioGeneration') {
        this.isProcessingPostTranslation = false;
        this.isProcessingTranslation = false;
      }

      // Only set general translation spinner for non-audio generation types
      if (spinnerType !== 'audioGeneration') {
        this.isProcessingTranslation = true;
      }
      switch (spinnerType) {
        case 'end':
          this.isProcessingEnd = true;
          break;
        case 'postTranslation':
          this.isProcessingPostTranslation = true;
          break;
        case 'intermediate':
          this.isProcessingIntermediate = true;
          break;
        case 'finalizing':
          this.isProcessingFinalizing = true;
          break;
        case 'audioGeneration':
          this.isProcessingAudioGeneration = true;
          break;
      }
      this.cdr.detectChanges();
    });
  }

  hideSpinner(
    spinnerType:
      | 'end'
      | 'postTranslation'
      | 'intermediate'
      | 'finalizing'
      | 'audioGeneration'
  ) {
    this.zone.run(() => {
      switch (spinnerType) {
        case 'end':
          this.isProcessingEnd = false;
          break;
        case 'postTranslation':
          this.isProcessingPostTranslation = false;
          break;
        case 'intermediate':
          this.isProcessingIntermediate = false;
          break;
        case 'finalizing':
          this.isProcessingFinalizing = false;
          break;
        case 'audioGeneration':
          this.isProcessingAudioGeneration = false;
          break;
      }
      // Only hide general translation spinner if all specific spinners are off
      if (
        !this.isProcessingEnd &&
        !this.isProcessingPostTranslation &&
        !this.isProcessingIntermediate &&
        !this.isProcessingFinalizing &&
        !this.isProcessingAudioGeneration
      ) {
        this.isProcessingTranslation = false;
        this.currentLane = null;
      }
      this.cdr.detectChanges();
    });
  }

  hideAllSpinners() {
    this.zone.run(() => {
      this.isProcessingEnd = false;
      this.isProcessingPostTranslation = false;
      this.isProcessingIntermediate = false;
      this.isProcessingFinalizing = false;
      this.isProcessingAudioGeneration = false;
      this.isProcessingTranslation = false;
      this.currentLane = null;
      this.cdr.detectChanges();
    });
  }

  async withSpinner<T>(fn: () => Promise<T>): Promise<T> {
    this.showSpinner('postTranslation', this.currentLane || 'user'); // Default to user if not set
    const timeoutDuration = this.isMobile ? 20000 : 30000; // 20s for mobile, 30s for desktop

    const timeoutPromise = new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error('Operation timed out')),
        timeoutDuration
      )
    );

    try {
      const result = await Promise.race([fn(), timeoutPromise]);
      this.hideSpinner('postTranslation');
      return result;
    } catch (error: any) {
      this.hideAllSpinners();
      console.error('Operation failed or timed out:', error);
      alert(
        `System issue: ${
          error.message || 'An unknown error occurred'
        }. Please try again.`
      );
      throw error;
    }
  }

  // Recording functionality
  async startRecording(lane: 'user' | 'partner'): Promise<void> {
    // For iOS browser: Unlock audio context immediately when user clicks record button
    // This ensures audio can auto-play when TTS is ready after recording stops
    // We need to await it briefly to ensure it completes in user interaction context
    // But use Promise.race with a timeout so it doesn't block too long
    if (this.isIOSBrowser) {
      console.log('🎵 [iOS Browser] Unlocking audio context for recording');
      // Try to unlock quickly, but don't wait more than 500ms
      Promise.race([
        this.unlockIOSAudioContext(),
        new Promise<void>((resolve) => setTimeout(resolve, 500)),
      ])
        .then(() => {
          console.log(
            '🎵 [iOS Browser] Unlock attempt completed (may have timed out)'
          );
        })
        .catch((err) => {
          console.warn(
            '🎵 [iOS Browser] Failed to unlock in startRecording:',
            err
          );
        });
    }

    return new Promise(async (resolve, reject) => {
      try {
        console.log('🎤 Starting recording for lane:', lane);
        console.log('🔍 BUTTON CLICKED - Start recording method called');
        this.hideAllSpinners(); // Ensure all spinners are hidden before starting

        this.currentLane = lane;
        this.sentenceBuffer = '';
        this.chunks = [];
        this.currentEnText = '';
        this.currentKyText = '';
        this.currentKyInText = '';
        this.currentEnOutText = '';
        this.currentEnConfidence = 0;
        this.currentKyConfidence = 0;

        // Update button state immediately
        this.isRecording = true;
        this.cdr.detectChanges();

        console.log('🎤 Requesting microphone access...');
        try {
          // Get appropriate audio constraints for the platform
          const audioConstraints = this.getAudioConstraints();
          console.log('🎤 Audio constraints:', audioConstraints);

          // For localhost, try the most basic approach first
          if (
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'
          ) {
            console.log('🔧 Localhost detected - trying basic audio first');
            try {
              this.stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
              });
              console.log(
                '✅ Microphone access granted with basic audio, stream:',
                this.stream
              );
            } catch (basicError: any) {
              console.warn(
                '⚠️ Basic audio failed, trying empty constraints:',
                basicError
              );
              this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {},
              });
              console.log(
                '✅ Microphone access granted with empty constraints, stream:',
                this.stream
              );
            }
          } else {
            // For production, try platform-specific constraints first
            try {
              this.stream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints,
              });
              console.log(
                '✅ Microphone access granted with constraints, stream:',
                this.stream
              );
            } catch (constraintError: any) {
              console.warn(
                '⚠️ Failed with constraints, trying basic audio access:',
                constraintError
              );

              // Fallback to basic audio access
              this.stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
              });
              console.log(
                '✅ Microphone access granted with basic constraints, stream:',
                this.stream
              );
            }
          }

          // Log stream details for debugging
          if (this.stream) {
            const audioTracks = this.stream.getAudioTracks();
            console.log('🎤 Audio tracks:', audioTracks.length);
            audioTracks.forEach((track, index) => {
              console.log(`🎤 Track ${index}:`, {
                label: track.label,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
              });
            });
          }
        } catch (micError: any) {
          console.error('❌ Microphone access denied or failed:', micError);
          this.isRecording = false;
          this.cdr.detectChanges();

          if (micError.name === 'NotAllowedError') {
            alert(
              "Microphone access denied. Please:\n1. Click the microphone icon in your browser's address bar\n2. Allow microphone access\n3. Refresh the page and try again."
            );
          } else if (micError.name === 'NotFoundError') {
            const isLocalhost =
              window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1';
            if (isLocalhost) {
              alert(
                'No microphone found on localhost. Please:\n1. Connect a microphone to your device\n2. Check if your microphone is working in other applications\n3. Try refreshing the page\n4. Make sure your browser has microphone permissions\n5. Try using Chrome or Firefox for better localhost support'
              );
            } else {
              alert(
                'No microphone found. Please:\n1. Connect a microphone to your device\n2. Check if your microphone is working in other applications\n3. Try refreshing the page\n4. If on mobile, ensure the app has microphone permissions.'
              );
            }
          } else if (micError.name === 'NotReadableError') {
            alert(
              'Microphone is already in use by another application. Please close other apps that might be using the microphone and try again.'
            );
          } else {
            alert(
              `Microphone error: ${
                micError.message || 'Unknown error'
              }. Please check your microphone settings and try again.`
            );
          }
          reject(micError);
          return;
        }

        // Use iOS-compatible MediaRecorder options
        const mimeType = this.getAudioMimeType();
        const options = { mimeType };

        try {
          this.mediaRecorder = new MediaRecorder(this.stream, options);
          console.log(
            '🎙️ MediaRecorder created with options:',
            options,
            this.mediaRecorder
          );
        } catch (recorderError) {
          console.warn(
            '⚠️ Failed to create MediaRecorder with options, trying without options:',
            recorderError
          );
          // Fallback: try without options (uses default format)
          try {
            this.mediaRecorder = new MediaRecorder(this.stream);
            console.log('🎙️ MediaRecorder created without options (fallback)');
          } catch (fallbackError) {
            console.error(
              '❌ Failed to create MediaRecorder even without options:',
              fallbackError
            );
            throw new Error('MediaRecorder not supported on this device');
          }
        }

        this.mediaRecorder.ondataavailable = (event) => {
          console.log('📊 Audio data available, size:', event.data.size);
          this.chunks.push(event.data);

          // Check for speech activity - only count as speech if chunk size is significant
          // Small chunks (less than 100 bytes) are likely just silence/background noise
          const MIN_AUDIO_CHUNK_SIZE = 100; // bytes
          if (event.data.size > MIN_AUDIO_CHUNK_SIZE) {
            this.detectSpeechActivity();
          }
        };

        this.mediaRecorder.onstop = async () => {
          console.log('⏹️ Recording stopped, processing audio...');

          // Use iOS-compatible audio format
          const audioType = this.getAudioMimeType();
          const audioBlob = new Blob(this.chunks, { type: audioType });
          console.log(
            '🎵 Audio blob created, size:',
            audioBlob.size,
            'type:',
            audioBlob.type
          );
          const audioUrl = URL.createObjectURL(audioBlob);

          // Process the recorded audio
          await this.processAudio(audioBlob, audioUrl, lane);
        };

        console.log('🚀 Starting MediaRecorder...');
        this.mediaRecorder.start(500); // Start recording, collect data every 500ms for faster processing
        this.isRecording = true;
        this.startPeriodicRefresh();
        this.startIdleTimeout();

        // Initialize speech activity detection
        this.lastSpeechTime = Date.now();
        // Start the initial silence detection timeout
        this.detectSpeechActivity();
        console.log(
          '✅ Recording started for lane:',
          lane,
          'isRecording:',
          this.isRecording,
          'currentLane:',
          this.currentLane
        );
        this.cdr.detectChanges();

        // Resolve the promise immediately after recording starts
        resolve();
      } catch (err) {
        console.error('❌ Error starting recording:', err);
        this.isRecording = false;
        this.cdr.detectChanges();
        alert(
          'Microphone access denied. Please allow microphone access to use this feature.'
        );
        reject(err);
      }
    });
  }

  stopRecording() {
    console.log(
      'Stop recording called, isRecording:',
      this.isRecording,
      'currentLane:',
      this.currentLane
    );
    if (!this.isRecording) return;

    // ALWAYS clear buffers when end button is clicked, regardless of state
    console.log(
      '🛑 End button clicked - clearing all buffers and resetting state'
    );

    // Stop recording state immediately
    this.isRecording = false;
    this.manuallyStopped = true;
    this.isAutoRestarting = false;
    this.isProcessingTranslation = false;

    // Stop all timers and intervals
    this.stopPeriodicRefresh();
    this.stopIdleTimeout();

    // Stop the media recorder
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.stop();
      } catch (err) {
        console.warn('Error stopping media recorder:', err);
      }
      this.mediaRecorder = undefined;
    }

    // Clear timeouts
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = undefined;
    }

    // Stop speech activity detection
    this.stopSpeechActivityDetection();

    // Stop all tracks in the stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      this.stream = undefined;
    }

    // Check if there's any speech content before processing
    const hasSpeechContent = this.sentenceBuffer.trim().length > 0;
    const hasAudioChunks = this.chunks.length > 0;

    if (!hasSpeechContent && !hasAudioChunks) {
      // No speech detected - just clean up without alert
      console.log(
        'No speech detected when stopping recording - cleaning up silently'
      );
      this.hideAllSpinners();
      this.cleanupBuffers(); // Clean up buffers when no speech detected
      this.cdr.detectChanges();
      return; // Exit early - no processing needed
    }

    // If there is speech content, process it but ensure cleanup happens regardless
    console.log('Speech content detected, processing audio...');
    this.cdr.detectChanges();
  }

  private startPeriodicRefresh() {
    this.stopPeriodicRefresh();
    this.periodicRefreshInterval = setInterval(() => {
      this.processCompleteSentence();
    }, 5000); // Process buffer every 5 seconds
  }

  private stopPeriodicRefresh() {
    if (this.periodicRefreshInterval) {
      clearInterval(this.periodicRefreshInterval);
      this.periodicRefreshInterval = undefined;
    }
  }

  private startIdleTimeout() {
    this.stopIdleTimeout();
    // Extended timeout - allow very long recordings for continuous conversations
    // 30 minutes for mobile, 60 minutes for desktop - effectively unlimited for practical use
    const idleDuration = this.isMobile ? 1800000 : 3600000; // 30 minutes for mobile, 60 minutes for desktop
    this.idleTimeout = setTimeout(() => {
      console.log('Extended idle timeout reached. Auto-stopping recording.');
      this.stopRecording();
    }, idleDuration);
  }

  private stopIdleTimeout() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = undefined;
    }
  }

  // Speech activity detection - extends recording while person is speaking
  private detectSpeechActivity() {
    this.lastSpeechTime = Date.now();

    // Clear any existing speech activity timeout
    if (this.speechActivityTimeout) {
      clearTimeout(this.speechActivityTimeout);
    }

    // Set a new timeout to stop recording after silence
    this.speechActivityTimeout = setTimeout(() => {
      const silenceDuration = Date.now() - this.lastSpeechTime;
      console.log(
        `🔇 Silence detected for ${silenceDuration}ms, stopping recording...`
      );
      this.stopRecording();
    }, this.SPEECH_SILENCE_THRESHOLD);
  }

  private stopSpeechActivityDetection() {
    if (this.speechActivityTimeout) {
      clearTimeout(this.speechActivityTimeout);
      this.speechActivityTimeout = undefined;
    }
  }

  private resetApplicationState() {
    this.isRecording = false;
    this.isAutoRestarting = false;
    this.manuallyStopped = true;
    this.isProcessingTranslation = false;
    this.currentLane = null;
    this.sentenceBuffer = '';
    this.chunks = [];
    this.currentEnText = '';
    this.currentKyText = '';
    this.currentKyInText = '';
    this.currentEnOutText = '';
    this.currentEnConfidence = 0;
    this.currentKyConfidence = 0;
    this.stopPeriodicRefresh();
    this.stopIdleTimeout();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }
    this.cdr.detectChanges();
  }

  private getAudioMimeType(): string {
    // Check for iOS Safari compatibility
    if (this.platform.is('ios') || this.isIOSSafari()) {
      // iOS Safari supports MP4 and WAV
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        console.log('📱 Using audio/mp4 for iOS compatibility');
        return 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        console.log('📱 Using audio/wav for iOS compatibility');
        return 'audio/wav';
      }
    }

    // Default to webm for other platforms
    if (MediaRecorder.isTypeSupported('audio/webm')) {
      console.log('🌐 Using audio/webm for other platforms');
      return 'audio/webm';
    }

    // Fallback
    console.log('⚠️ Using fallback audio format');
    return 'audio/webm';
  }

  private isIOSSafari(): boolean {
    const ua = navigator.userAgent;
    return (
      /iPad|iPhone|iPod/.test(ua) &&
      /Safari/.test(ua) &&
      !/CriOS|FxiOS|OPiOS|mercury/.test(ua)
    );
  }

  private getAudioConstraints(): MediaTrackConstraints {
    // For local development, use absolutely minimal constraints
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      console.log('🔧 Using minimal constraints for local development');
      return {}; // Empty constraints - let browser choose defaults
    }

    // iOS Safari has limited support for advanced audio constraints
    if (this.platform.is('ios') || this.isIOSSafari()) {
      console.log('📱 Using iOS-compatible audio constraints');
      return {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 44100, // iOS prefers 44.1kHz
        sampleSize: 16,
        channelCount: 1, // Mono for better compatibility
      };
    }

    // Default constraints for other platforms
    return {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
  }

  private async processAudio(
    audioBlob: Blob,
    audioUrl: string,
    lane: 'user' | 'partner'
  ) {
    // Hide ending spinner and show translating spinner
    // DEBUG: Comment out for spinner timing test
    // this.hideSpinner('end');
    this.showSpinner('postTranslation', lane);
    this.currentLane = lane;

    // Set a timeout to ensure cleanup happens even if processing gets stuck
    // Increased timeout for long audio/TTS processing (TTS can take up to 60 seconds for long texts)
    // Clear any existing timeout first
    if (this.audioProcessingTimeout) {
      clearTimeout(this.audioProcessingTimeout);
    }
    this.audioProcessingTimeout = setTimeout(() => {
      console.warn(
        '⚠️ Audio processing timeout - forcing cleanup after 90 seconds'
      );

      // Check if translation or audio generation is still in progress
      // If so, only cleanup buffers without affecting spinners
      const isTranslationInProgress =
        this.isProcessingPostTranslation || this.isProcessingTranslation;
      const isAudioInProgress = this.isProcessingAudioGeneration;

      if (isTranslationInProgress || isAudioInProgress) {
        console.log(
          '⚠️ Translation/Audio still in progress, only cleaning buffers (preserving spinner state)'
        );
        // Only cleanup buffers without resetting spinner state
        this.cleanupBuffersOnly();
      } else {
        // No active processing, safe to do full cleanup
        console.log('⚠️ No active processing, performing full cleanup');
        this.cleanupBuffers();
      }
    }, 90000); // 90 second timeout (allows time for long TTS generation which can take up to 60s)

    try {
      // Get the target language for transcription
      // For user audio, use the selected user language
      // For partner audio, try to be more flexible with language detection
      const transcriptionLanguage =
        lane === 'user'
          ? this.selectedUserLanguage()
          : this.selectedPartnerLanguage();

      console.log(
        '🎯 Target language for transcription:',
        transcriptionLanguage,
        'for lane:',
        lane
      );

      // For partner audio, add a note about language flexibility
      if (lane === 'partner') {
        console.log(
          '🤝 Partner audio - if language detection fails, the partner might be speaking in a different language than selected:',
          this.selectedPartnerLanguage()
        );
      }
      const systemPrompt = this.getLanguageSystemPrompt(transcriptionLanguage);
      console.log('📝 Language system prompt:', systemPrompt);
      console.log(
        '🔍 System prompt contains WRONG_LANGUAGE_DETECTED:',
        systemPrompt.includes('WRONG_LANGUAGE_DETECTED')
      );

      // Language priming removed for performance - transcription API already includes language context
      console.log(
        '⚡ Skipping language priming (redundant, improves performance by 200-500ms)'
      );

      let transcriptionResult;

      // Determine source and target languages based on lane
      const sourceLang =
        lane === 'user'
          ? this.selectedUserLanguage()
          : this.selectedPartnerLanguage();
      const targetLang =
        lane === 'user'
          ? this.selectedPartnerLanguage()
          : this.selectedUserLanguage();

      // translateToEnglish should be true only if target language is English
      const translateToEnglish = targetLang === 'en';

      // Log language selection for debugging
      console.log('🔍 TRANSLATION LANGUAGE DEBUG:', {
        lane,
        sourceLang,
        targetLang,
        translateToEnglish,
        selectedUserLanguage: this.selectedUserLanguage(),
        selectedPartnerLanguage: this.selectedPartnerLanguage(),
        transcriptionLanguage,
      });

      try {
        transcriptionResult = await this.svc.transcribe(
          audioBlob,
          translateToEnglish,
          transcriptionLanguage,
          this.getLanguageSystemPrompt(transcriptionLanguage),
          this.useDiarization,
          targetLang, // targetLanguage - language to translate TO
          sourceLang // sourceLanguage - language being spoken FROM
        );
      } catch (transcriptionError: any) {
        // If transcription fails for partner audio with language detection error, try without specifying language
        if (
          lane === 'partner' &&
          transcriptionError.message &&
          transcriptionError.message.includes('Language detection failed')
        ) {
          console.log(
            '🔄 Partner language detection failed, trying auto-detection...'
          );
          try {
            // For auto-detection, still pass source and target languages
            const autoSourceLang = this.selectedPartnerLanguage();
            const autoTargetLang = this.selectedUserLanguage();
            const autoTranslateToEnglish = autoTargetLang === 'en';

            transcriptionResult = await this.svc.transcribe(
              audioBlob,
              autoTranslateToEnglish,
              undefined, // Let backend auto-detect language
              'Please transcribe the audio and detect the language automatically.',
              this.useDiarization,
              autoTargetLang, // targetLanguage
              autoSourceLang // sourceLanguage
            );
            console.log('✅ Auto-detection transcription successful');
          } catch (autoDetectionError) {
            console.error('❌ Auto-detection also failed:', autoDetectionError);
            throw transcriptionError; // Throw original error
          }
        } else {
          throw transcriptionError; // Re-throw if not a partner language detection error
        }
      }
      // Backend may return both originalText (source language) and translatedText (target language)
      // If backend already translated, use originalText for display and translatedText for translation
      // Otherwise, use text as transcribedText and translate it ourselves
      const backendTranslated =
        transcriptionResult.originalText && transcriptionResult.translatedText;
      let transcribedText: string;
      let backendTranslation: string | undefined;

      if (backendTranslated) {
        // Backend already performed translation - use originalText for display, translatedText for translation
        transcribedText = transcriptionResult.originalText!;
        backendTranslation = transcriptionResult.translatedText;
        console.log(
          '✅ Backend already translated - using originalText for display and translatedText for translation'
        );
        console.log('  - Original text (source):', transcribedText);
        console.log('  - Translated text (target):', backendTranslation);
        console.log('  - Expected source language:', sourceLang);
        console.log('  - Expected target language:', targetLang);
        console.log('  - Lane:', lane);
      } else {
        // Backend only transcribed - use text as transcribedText, will translate ourselves
        transcribedText = transcriptionResult.text;
        console.log('📝 Backend only transcribed - will translate ourselves');
        console.log('  - Transcribed text:', transcribedText);
        console.log('  - Expected source language:', sourceLang);
        console.log('  - Expected target language:', targetLang);
        console.log('  - Lane:', lane);
      }

      const transcriptionConfidence = transcriptionResult.confidence;

      // Debug: Check what we received
      console.log('📦 Transcription result:', transcriptionResult);
      console.log('📦 Warning field:', (transcriptionResult as any).warning);
      console.log('📦 WrongLanguage field:', transcriptionResult.wrongLanguage);

      // Handle diarization results
      if (
        this.useDiarization &&
        transcriptionResult.useDiarization &&
        transcriptionResult.segments
      ) {
        console.log(
          '👥 Diarization results received:',
          transcriptionResult.segments
        );
        this.diarizationResults = transcriptionResult.segments;
        this.showDiarizationResults = true;
      } else if (this.useDiarization) {
        console.log('⚠️ Diarization requested but not available in response');
        this.diarizationResults = [];
        this.showDiarizationResults = false;
      }

      // Check for language mismatch warning (show toast but continue)
      if ((transcriptionResult as any).warning) {
        console.warn(
          '⚠️ Language warning:',
          (transcriptionResult as any).warning
        );
        // Show auto-dismissing toast (2 seconds) but CONTINUE translation
        // Toast removed for performance
      }

      // Debug logging for transcription issues
      console.log('🎤 TRANSCRIPTION DEBUG:');
      console.log('  - Original audio blob: [processed]');
      console.log('  - Audio blob type:', audioBlob.type);
      console.log('  - Transcribed text:', `"${transcribedText}"`);
      console.log('  - Transcription confidence:', transcriptionConfidence);
      console.log('  - Expected language:', transcriptionLanguage);

      // Check for backend errors FIRST (before checking for empty text)
      // Backend may return error in response body even with status 200
      if (transcriptionResult.error) {
        console.warn(
          '❌ Backend error in transcription response:',
          transcriptionResult.error
        );
        this.hideAllSpinners();
        this.cleanupBuffers();

        // Show user-friendly error message based on error type
        let errorMessage = transcriptionResult.error;
        if (
          transcriptionResult.error.includes('Connection error') ||
          transcriptionResult.error.includes('connection') ||
          transcriptionResult.error.includes('timeout')
        ) {
          errorMessage =
            'Unable to connect to transcription service. Please check your internet connection and try again.';
        } else if (
          transcriptionResult.error.includes('quota') ||
          transcriptionResult.error.includes('insufficient_quota') ||
          transcriptionResult.error.includes('exceeded your current quota')
        ) {
          errorMessage =
            'OpenAI API quota exceeded. Please add a payment method or buy credits at https://platform.openai.com/account/billing';
          // Show prominent banner for quota errors
          this.showQuotaErrorBannerMessage(errorMessage);
        } else if (
          transcriptionResult.error.includes('API') ||
          transcriptionResult.error.includes('OpenAI')
        ) {
          errorMessage =
            'Transcription service temporarily unavailable. Please try again in a moment.';
        }
        this.showTranslationError(errorMessage);
        return;
      }

      // Check for empty transcription AFTER error check
      if (!transcribedText || transcribedText.trim().length === 0) {
        console.log(
          'ℹ️ Empty transcription received (likely silence) - handling silently'
        );
        this.hideAllSpinners();
        this.cleanupBuffers(); // Clean up buffers on empty transcription
        // Don't show error message - this is expected when user stops without speaking
        return;
      }

      // Check for hallucination - common words from silence with low confidence
      const isHallucination = this.detectHallucination(
        transcribedText,
        transcriptionConfidence
      );
      if (isHallucination) {
        console.warn(
          '🚫 Hallucination detected from silence:',
          transcribedText
        );
        this.hideAllSpinners();
        this.cleanupBuffers();

        this.showToast(
          'No speech detected. Please try speaking again.',
          'warning'
        );
        return;
      }

      // Check for wrong language detection
      console.log(
        '🔍 Checking for wrong language detection in:',
        transcribedText
      );
      console.log('🔍 Looking for WRONG_LANGUAGE_DETECTED pattern...');
      console.log(
        '🔍 Exact transcribed text:',
        JSON.stringify(transcribedText)
      );
      if (transcribedText.includes('WRONG_LANGUAGE_DETECTED')) {
        console.warn('🚫 Wrong language detected:', transcribedText);
        this.hideAllSpinners();
        this.cleanupBuffers();

        // Extract the user-friendly message after the colon (if present)
        let userMessage = 'Please speak in the selected language.';
        if (transcribedText.includes('WRONG_LANGUAGE_DETECTED:')) {
          userMessage =
            transcribedText.split('WRONG_LANGUAGE_DETECTED:')[1]?.trim() ||
            userMessage;
        }
        console.log('📢 Showing user message as toast:', userMessage);
        this.showToast(userMessage, 'warning');
        return;
      } else {
        console.log(
          '✅ No wrong language detection - proceeding with normal transcription'
        );
      }

      // Frontend language detection removed for performance

      // Deadlock detection - check for system errors that could cause deadlocks
      if (this.isDeadlockTranslation(transcribedText)) {
        console.warn('🚫 Deadlock translation detected, stopping process');
        this.hideAllSpinners();
        this.cleanupBuffers();
        this.showTranslationError(
          'Translation deadlock detected. Please try again.'
        );
        return;
      }

      // Language filtering - REMOVED blocking, now just warning via toast
      // The backend AI detection will show a warning toast but still translate
      console.log(
        '✅ Proceeding with translation regardless of language warning'
      );

      // Convert Kazakh text to Kyrgyz if detected
      if (transcriptionLanguage === 'ky') {
        const kazakhToKyrgyzMap: { [key: string]: string } = {
          // Common words
          сәлем: 'салам',
          жақсы: 'жакшы',
          рахмет: 'рахмат',
          қалай: 'кантип',
          не: 'эмне',
          қазақ: 'кыргыз',
          қазақша: 'кыргызча',
          қазақстан: 'кыргызстан',
          // Character mappings
          қ: 'к',
          ғ: 'г',
          ә: 'а',
          ө: 'о',
          ү: 'ү',
          і: 'и',
          ұ: 'у', // Kazakh ұ → Kyrgyz у
          һ: 'х', // Kazakh һ → Kyrgyz х
        };

        let convertedText = transcribedText;
        let wasConverted = false;

        // Check for Kazakh indicators and convert them
        for (const [kazakh, kyrgyz] of Object.entries(kazakhToKyrgyzMap)) {
          if (convertedText.includes(kazakh)) {
            convertedText = convertedText.replace(
              new RegExp(kazakh, 'gi'),
              kyrgyz
            );
            wasConverted = true;
          }
        }

        if (wasConverted) {
          console.log(
            '🔄 Converted Kazakh text to Kyrgyz:',
            transcribedText,
            '→',
            convertedText
          );
          // Update the transcribed text with the converted version
          transcribedText = convertedText;
        }

        console.log('✅ Kyrgyz transcription processed:', transcribedText);
      }

      let translation: string;
      let translationTargetLanguage:
        | 'en'
        | 'ko'
        | 'zh-CN'
        | 'zh-HK'
        | 'zh'
        | 'ky'
        | 'ru'
        | 'kk'
        | 'tg'
        | 'tk'
        | 'uz'
        | 'fa'
        | 'es';

      if (lane === 'user') {
        translationTargetLanguage = this.selectedPartnerLanguage();
        console.log('🔄 TRANSLATION DEBUG:');
        console.log('  - Source text:', `"${transcribedText}"`);
        console.log('  - Source language:', this.selectedUserLanguage());
        console.log('  - Target language:', translationTargetLanguage);

        // Use backend translation if available, otherwise translate ourselves
        if (backendTranslation) {
          translation = backendTranslation;
          console.log('  - Using backend translation:', `"${translation}"`);
        } else {
          // Ensure translation spinner is visible before API call (important for long translations)
          this.showSpinner('postTranslation', lane);
          this.cdr.detectChanges();
          await new Promise((resolve) => setTimeout(resolve, 0));

          translation = await this.svc.translate(
            transcribedText,
            translationTargetLanguage,
            this.selectedUserLanguage(),
            this.selectedUserLanguage(),
            this.selectedPartnerLanguage(),
            this.conversationTopic()
          );

          console.log('  - Translated text:', `"${translation}"`);
        }

        this.currentEnText = transcribedText;
        this.currentEnConfidence = transcriptionConfidence;
        this.currentKyText = translation;
      } else {
        translationTargetLanguage = this.selectedUserLanguage();
        console.log('🔄 TRANSLATION DEBUG:');
        console.log('  - Source text:', `"${transcribedText}"`);
        console.log('  - Source language:', this.selectedPartnerLanguage());
        console.log('  - Target language:', translationTargetLanguage);

        // Use backend translation if available, otherwise translate ourselves
        if (backendTranslation) {
          translation = backendTranslation;
          console.log('  - Using backend translation:', `"${translation}"`);
        } else {
          // Ensure translation spinner is visible before API call (important for long translations)
          this.showSpinner('postTranslation', lane);
          this.cdr.detectChanges();
          await new Promise((resolve) => setTimeout(resolve, 0));

          translation = await this.svc.translate(
            transcribedText,
            translationTargetLanguage,
            this.selectedPartnerLanguage(),
            this.selectedUserLanguage(),
            this.selectedPartnerLanguage(),
            this.conversationTopic()
          );

          console.log('  - Translated text:', `"${translation}"`);
        }

        this.currentKyInText = transcribedText;
        this.currentKyConfidence = transcriptionConfidence;
        this.currentEnOutText = translation;
      }

      if (!translation || translation.trim().length === 0) {
        console.warn('Empty translation received from API');
        this.hideAllSpinners();
        this.cleanupBuffers(); // Clean up buffers on empty translation
        this.showTranslationError(
          'Translation failed. Please try speaking again.'
        );
        return;
      }

      const skipAudio = this.skipAudioGeneration();
      const ttsTargetLang =
        lane === 'user'
          ? this.selectedPartnerLanguage()
          : this.selectedUserLanguage();
      const voiceForLane =
        lane === 'user' ? this.selectedPartnerVoice : this.selectedUserVoice;
      // Send the translation to speech on this turn, before UI work.
      const ttsPromise = skipAudio
        ? null
        : this.ttsOrNull(
            translation,
            this.speakingSpeed,
            voiceForLane,
            ttsTargetLang
          );

      // Ensure we have a higher confidence value for better translations
      const transcriptionConf = Math.max(transcriptionConfidence || 0, 0.3);
      const translationConf = this.calculateTranslationConfidence(
        transcribedText,
        translation
      );
      const capturedConfidence = Math.max(
        Math.min(transcriptionConf, translationConf),
        0.9 // Minimum confidence of 90% (increased from 80%)
      );

      // Use the transcribed text directly without language detection
      const finalTranscribedText = transcribedText;

      // ⚡ IMMEDIATE UI UPDATE: Display translation text immediately after translation completes
      // This ensures users see the translation even if audio generation takes time
      const conversationEntryPreview: ConversationEntry = {
        id: this.conversationId++,
        original: finalTranscribedText,
        translated: translation,
        timestamp: new Date(),
        audioUrl: undefined, // Will be updated when TTS completes
        audioData: '',
        originalAudioUrl: undefined,
        originalAudioData: undefined,
        recordedAudioUrl: undefined, // Will be updated when blob conversion completes
        recordedAudioData: '',
        userLanguage:
          lane === 'user'
            ? this.selectedUserLanguage()
            : this.selectedPartnerLanguage(),
        targetLanguage:
          lane === 'user'
            ? this.selectedPartnerLanguage()
            : this.selectedUserLanguage(),
        partnerLanguage:
          lane === 'user'
            ? this.selectedPartnerLanguage()
            : this.selectedUserLanguage(),
        confidence: capturedConfidence,
        inputType: 'speech',
      };

      // Display translation immediately in UI
      this.zone.run(() => {
        if (lane === 'user') {
          this.enConversation.update((conv) => [
            conversationEntryPreview,
            ...conv,
          ]);
        } else {
          this.kyConversation.update((conv) => [
            conversationEntryPreview,
            ...conv,
          ]);
        }
        this.cdr.detectChanges();
        this.scrollToLatest(lane);
      });

      // ⚡ PERFORMANCE: Skip both TTS and blob conversion when skipAudio is true
      console.log(
        skipAudio
          ? '⏭️ Skipping TTS audio generation and original voice recording save...'
          : '🎵 Generating TTS audio and converting blob in parallel...'
      );
      if (!skipAudio) {
        console.log(
          '🎵 Audio blob size:',
          audioBlob.size,
          'bytes (',
          (audioBlob.size / 1024 / 1024).toFixed(2),
          'MB)'
        );
        console.log('🎵 Audio blob type:', audioBlob.type);
      }

      // ⚡ PERFORMANCE: Skip saving original voice recording when skipAudio is true
      let recordedAudioData = '';
      let translatedTtsResult: any = null;

      if (skipAudio) {
        // If audio generation is skipped, hide translation spinner now that translation is displayed
        this.hideSpinner('postTranslation');
      } else {
        // Only convert blob and generate TTS if skipAudio is false
        // Speech request is already in flight. Show the audio spinner without delaying it.
        this.showSpinner('audioGeneration', lane);
        this.cdr.detectChanges();
        this.hideSpinner('postTranslation');

        console.log(
          '🎵 Speech request already sent (blob conversion will happen in background)...'
        );

        // Start blob conversion in background (don't await it yet)
        const blobConversionPromise = this.isMobile
          ? this.blobToBase64(audioBlob)
          : this.blobToBlobUrl(audioBlob);

        // Wait for TTS to complete first (this is what user sees)
        translatedTtsResult = await ttsPromise;

        // Hide audio generation spinner after TTS completes (user sees this immediately)
        this.hideSpinner('audioGeneration');

        // Now get blob data (it may still be converting, but we don't block TTS on it)
        recordedAudioData = await blobConversionPromise;

        console.log(
          '✅ TTS completed first, blob conversion completed, blob length:',
          recordedAudioData.length,
          'characters'
        );
      }

      console.log('🎵 recordedAudioData empty?', recordedAudioData === '');
      console.log('🎵 lane:', lane, 'inputType: speech');

      if (!skipAudio && !translatedTtsResult?.audioUrl) {
        console.warn('No TTS audio received - keeping translation without audio');
      }

      // Note: We don't generate TTS for original text because OpenAI TTS is optimized for English
      // and doesn't handle other languages well. Users can hear the original through the recorded audio.
      console.log(
        '🎵 Skipping TTS for original text (TTS optimized for English only)'
      );

      // Process audio data
      const translatedAudioData = translatedTtsResult?.audioData || '';
      const originalAudioData = ''; // No TTS for original text

      // Store the blob URL for immediate playback (only if skipAudio is false)
      const storedRecordedAudioUrl = skipAudio ? undefined : audioUrl;

      const audioDuration =
        skipAudio || !translatedTtsResult?.audioUrl
          ? 0
          : await this.calculateAudioDuration(translatedTtsResult.audioUrl);

      // Update the existing conversation entry with audio data (instead of creating duplicate)
      this.zone.run(() => {
        if (lane === 'user') {
          this.enConversation.update((conv) => {
            // Find and update the first entry (most recent)
            const updated = [...conv];
            if (
              updated.length > 0 &&
              updated[0].id === conversationEntryPreview.id
            ) {
              updated[0] = {
                ...updated[0],
                audioUrl: translatedTtsResult?.audioUrl,
                audioData: translatedAudioData,
                recordedAudioUrl: storedRecordedAudioUrl,
                recordedAudioData: recordedAudioData,
                duration: audioDuration,
              };
            }
            return updated;
          });
        } else {
          this.kyConversation.update((conv) => {
            // Find and update the first entry (most recent)
            const updated = [...conv];
            if (
              updated.length > 0 &&
              updated[0].id === conversationEntryPreview.id
            ) {
              updated[0] = {
                ...updated[0],
                audioUrl: translatedTtsResult?.audioUrl,
                audioData: translatedAudioData,
                recordedAudioUrl: storedRecordedAudioUrl,
                recordedAudioData: recordedAudioData,
                duration: audioDuration,
              };
            }
            return updated;
          });
        }
        this.cdr.detectChanges();
      });

      // Create complete ConversationEntry for history storage
      const conversationEntry: ConversationEntry = {
        ...conversationEntryPreview,
        audioUrl: translatedTtsResult?.audioUrl,
        audioData: translatedAudioData,
        recordedAudioUrl: storedRecordedAudioUrl,
        recordedAudioData: recordedAudioData,
        duration: audioDuration,
      };

      if (!skipAudio && translatedTtsResult?.audioUrl) {
        await this.playAudio(translatedTtsResult.audioUrl);
      }

      // Clean up buffers after successful translation
      this.cleanupBuffers();

      // ⚡ PERFORMANCE: Save to storage in background (don't block UI)
      const historyEntry: any = {
        originalText: finalTranscribedText,
        translatedText: translation,
        audioUrl: translatedTtsResult?.audioUrl,
        audioData: translatedAudioData,
        originalAudioUrl: undefined, // No TTS for original text
        originalAudioData: undefined, // No TTS for original text
        recordedAudioUrl: storedRecordedAudioUrl, // Original recorded audio URL (only if data stored)
        recordedAudioData: recordedAudioData, // Original recorded audio data
        userLanguage:
          lane === 'user'
            ? this.selectedUserLanguage()
            : this.selectedPartnerLanguage(),
        targetLanguage:
          lane === 'user'
            ? this.selectedPartnerLanguage()
            : this.selectedUserLanguage(),
        confidence: capturedConfidence,
        duration: audioDuration,
        inputType: 'speech', // Mark as speech input
      };

      setTimeout(() => {
        this.conversationHistoryService.addConversation(historyEntry);
      }, 0);
    } catch (error: any) {
      console.error('Error during audio processing:', error);
      this.hideAllSpinners();
      this.cleanupBuffers(); // Clean up buffers on error

      // Provide more specific error messages for language detection failures
      let errorMessage =
        'An error occurred during processing. Please try again.';

      // Check for word limit errors first - these are critical and should be shown prominently
      if (
        error.message &&
        (error.message.includes('Word limit exceeded') ||
          error.message.includes('Please buy more words'))
      ) {
        // Word limit error - show in UI for mobile (most reliable approach)
        console.log('💬 Word limit error detected, showing in UI for mobile');

        // Get translated error message
        const usageStats = this.wordUsageService.getUsageStats();
        const wordsUsed = usageStats.wordsUsed || 0;
        const wordsLimit = usageStats.wordsLimit || 0;

        // Use translation service to get the translated message
        this.wordLimitErrorMessage = this.translationService.formatMessage(
          'wordLimitExceededDetail',
          wordsUsed.toLocaleString(),
          wordsLimit.toLocaleString()
        );
        this.cdr.detectChanges();

        console.log(
          '💬 Word limit error message set in UI:',
          this.wordLimitErrorMessage
        );

        // Auto-clear error message after 10 seconds
        setTimeout(() => {
          this.wordLimitErrorMessage = null;
          this.cdr.detectChanges();
        }, 10000);

        return; // Return early to prevent further processing
      } else if (
        error.message &&
        error.message.includes('Language detection failed')
      ) {
        if (lane === 'partner') {
          errorMessage = `Language detection failed for partner audio. The partner might be speaking in a different language than selected (${this.getLanguageName(
            this.selectedPartnerLanguage()
          )}). Please ensure the partner speaks in the selected language or change the partner language setting in the navigation menu.`;
        } else {
          errorMessage = `Language detection failed. Please ensure you are speaking in ${this.getLanguageName(
            this.selectedUserLanguage()
          )}.`;
        }
      } else if (error.message && error.message.includes('400')) {
        errorMessage = 'Server error: Please check your audio and try again.';
      }

      this.showTranslationError(errorMessage);
    } finally {
      // Clear the timeout since processing is complete
      if (this.audioProcessingTimeout) {
        clearTimeout(this.audioProcessingTimeout);
        this.audioProcessingTimeout = undefined;
      }

      // Cleanup is handled in try/catch blocks to prevent double cleanup
      console.log('🧹 Audio processing completed - buffers cleared');
    }
  }

  private async processCompleteSentence() {
    const sentence = this.sentenceBuffer.trim();
    if (!sentence) return;

    // Check if sentence is too short or contains only whitespace/special characters
    if (sentence.length < 2 || /^[\s\.,!?\-_]+$/.test(sentence)) {
      console.log('Empty or invalid speech detected, skipping translation');
      this.sentenceBuffer = '';
      return;
    }

    // Clear the sentence buffer immediately to prepare for next sentence
    this.sentenceBuffer = '';

    try {
      if (this.currentLane === 'user') {
        // Hide ending spinner and show translating spinner
        // DEBUG: Comment out for spinner timing test
        // this.hideSpinner('end');
        this.showSpinner('postTranslation', 'user');

        // Translate user's language to partner's language with automatic spinner
        const translation = await this.svc.translate(
          sentence,
          this.selectedPartnerLanguage(), // target language
          this.selectedUserLanguage(), // source language
          this.selectedUserLanguage(), // user language
          this.selectedPartnerLanguage(), // partner language
          this.conversationTopic()
        );

        // Check if translation is empty or invalid
        if (!translation || translation.trim().length === 0) {
          console.warn('Empty translation received from API');
          this.hideAllSpinners();
          this.showTranslationError(
            'Translation failed. Please try speaking again.'
          );
          return;
        }

        this.currentEnText = sentence;
        this.currentKyText = translation;

        const capturedConfidence = this.calculateTranslationConfidence(
          sentence,
          translation
        );

        // Show the translation now; audio is attached when speech returns.
        const conversationEntry: ConversationEntry = {
          id: this.conversationId++,
          original: sentence,
          translated: translation,
          timestamp: new Date(),
          audioUrl: undefined,
          audioData: '',
          originalAudioUrl: undefined, // No TTS for original text
          originalAudioData: undefined, // No TTS for original text
          userLanguage: this.selectedUserLanguage(),
          targetLanguage: this.selectedPartnerLanguage(),
          partnerLanguage: this.selectedPartnerLanguage(),
          confidence: capturedConfidence,
        };
        this.zone.run(() => {
          this.enConversation.update((conv) => [conversationEntry, ...conv]);
          this.scrollToLatest('user');
        });

        const skipAudio = this.skipAudioGeneration();
        let translatedTtsResult: any = null;

        if (skipAudio) {
          this.hideSpinner('postTranslation');
          console.log('⏭️ Skipping TTS audio generation (speech)...');
        } else {
          this.showSpinner('audioGeneration', 'user');
          this.hideSpinner('postTranslation');

          console.log(
            '🎵 Generating TTS audio for translated text (manual)...'
          );
          // Use translated text (target language) for TTS
          translatedTtsResult = await this.ttsOrNull(
            translation,
            this.speakingSpeed,
            this.selectedVoice,
            this.selectedPartnerLanguage() // Target language for user lane
          );

          this.hideSpinner('audioGeneration');

          if (!translatedTtsResult?.audioUrl) {
            console.warn('No TTS audio received - keeping translation without audio');
          }
        }

        const translatedAudioData = translatedTtsResult?.audioData || '';
        const audioDuration = !translatedTtsResult?.audioUrl
          ? 0
          : await this.calculateAudioDuration(translatedTtsResult.audioUrl);

        if (translatedTtsResult?.audioUrl) {
          this.updateConversationEntry('user', conversationEntry.id, {
            audioUrl: translatedTtsResult.audioUrl,
            audioData: translatedAudioData,
            duration: audioDuration,
          });
          this.showSpinner('finalizing', 'user');
          await this.playAudio(translatedTtsResult.audioUrl, true);
        }

        // ⚡ PERFORMANCE: Save to storage in background
        const historyEntry: Omit<ConversationHistoryEntry, 'id' | 'timestamp'> =
          {
            originalText: sentence,
            translatedText: translation,
            audioUrl: translatedTtsResult?.audioUrl,
            audioData: translatedAudioData,
            originalAudioUrl: undefined, // No TTS for original text
            originalAudioData: undefined, // No TTS for original text
            userLanguage: this.selectedUserLanguage(),
            targetLanguage: this.selectedPartnerLanguage(),
            confidence: capturedConfidence,
            duration: audioDuration,
          };

        setTimeout(() => {
          this.conversationHistoryService.addConversation(historyEntry);
        }, 0);
      } else {
        // Hide ending spinner and show translating spinner
        // DEBUG: Comment out for spinner timing test
        // this.hideSpinner('end');
        this.showSpinner('postTranslation', 'partner');

        // Partner spoke their language - translate to user's language with automatic spinner
        const translation = await this.svc.translate(
          sentence,
          this.selectedUserLanguage(), // target language
          this.selectedPartnerLanguage(), // source language
          this.selectedUserLanguage(), // user language
          this.selectedPartnerLanguage(), // partner language
          this.conversationTopic()
        );

        // Check if translation is empty or invalid
        if (!translation || translation.trim().length === 0) {
          console.warn('Empty translation received from API');
          this.hideAllSpinners();
          this.showTranslationError(
            'Translation failed. Please try speaking again.'
          );
          return;
        }

        this.currentKyInText = sentence;
        this.currentEnOutText = translation;

        const capturedConfidence = this.calculateTranslationConfidence(
          sentence,
          translation
        );

        // Show the translation now; audio is attached when speech returns.
        const conversationEntry: ConversationEntry = {
          id: this.conversationId++,
          original: sentence,
          translated: translation,
          timestamp: new Date(),
          audioUrl: undefined,
          audioData: '',
          originalAudioUrl: undefined, // No TTS for original text
          originalAudioData: undefined, // No TTS for original text
          userLanguage: this.selectedPartnerLanguage(),
          targetLanguage: this.selectedUserLanguage(),
          partnerLanguage: this.selectedUserLanguage(),
          confidence: capturedConfidence,
          inputType: 'speech', // Mark as speech input
        };
        this.zone.run(() => {
          this.kyConversation.update((conv) => [conversationEntry, ...conv]);
          this.scrollToLatest('partner');
        });

        const skipAudio = this.skipAudioGeneration();
        let translatedTtsResult: any = null;

        if (skipAudio) {
          this.hideSpinner('postTranslation');
          console.log('⏭️ Skipping TTS audio generation (partner speech)...');
        } else {
          this.showSpinner('audioGeneration', 'partner');
          this.hideSpinner('postTranslation');

          console.log(
            '🎵 Generating TTS audio for translated text (partner)...'
          );
          // Use translated text (target language) for TTS
          translatedTtsResult = await this.ttsOrNull(
            translation,
            this.speakingSpeed,
            this.selectedVoice,
            this.selectedUserLanguage() // Target language for partner lane
          );

          this.hideSpinner('audioGeneration');

          if (!translatedTtsResult?.audioUrl) {
            console.warn('No TTS audio received - keeping translation without audio');
          }
        }

        const translatedAudioData = translatedTtsResult?.audioData || '';
        const audioDuration = !translatedTtsResult?.audioUrl
          ? 0
          : await this.calculateAudioDuration(translatedTtsResult.audioUrl);

        if (translatedTtsResult?.audioUrl) {
          this.updateConversationEntry('partner', conversationEntry.id, {
            audioUrl: translatedTtsResult.audioUrl,
            audioData: translatedAudioData,
            duration: audioDuration,
          });
          this.showSpinner('finalizing', 'partner');
          await this.playAudio(translatedTtsResult.audioUrl, true);
        }

        // ⚡ PERFORMANCE: Save to storage in background
        const historyEntry: Omit<ConversationHistoryEntry, 'id' | 'timestamp'> =
          {
            originalText: sentence,
            translatedText: translation,
            audioUrl: translatedTtsResult?.audioUrl,
            audioData: translatedAudioData,
            originalAudioUrl: undefined, // No TTS for original text
            originalAudioData: undefined, // No TTS for original text
            userLanguage: this.selectedPartnerLanguage(), // Partner's original language
            targetLanguage: this.selectedUserLanguage(), // Partner's translated language (user's language)
            confidence: capturedConfidence,
            duration: audioDuration,
            inputType: 'speech', // Mark as speech input
          };

        setTimeout(() => {
          this.conversationHistoryService.addConversation(historyEntry);
        }, 0);
      }
    } catch (error) {
      console.error('Error during sentence processing:', error);
      this.hideAllSpinners();
      this.cleanupBuffers(); // Clean up buffers on error
      this.showTranslationError(
        'An error occurred during processing. Please try again.'
      );
    }
  }

  async translateUserText() {
    if (!this.userTextInput.trim()) {
      console.warn('No text to translate');
      return;
    }

    // For iOS browser: Unlock audio context immediately while we're in user interaction context
    // This allows audio to auto-play when TTS is ready
    if (this.isIOSBrowser) {
      await this.unlockIOSAudioContext();
    }

    const text = this.userTextInput.trim();

    // Clear cache for "horse" translations to ensure we get fresh results
    if (text.toLowerCase() === 'horse') {
      console.log(
        '🐎 Clearing cache for "horse" translation to get fresh results'
      );
      this.svc.clearTranslationCacheEntry(
        text,
        this.selectedPartnerLanguage(),
        this.selectedUserLanguage()
      );
    }

    // Check word limit (200 words)
    const wordCount = text.split(/\s+/).length;
    if (wordCount > 200) {
      this.showTranslationError(
        this.translationService.getTranslation('textExceedsWordLimit')
      );
      return;
    }

    // Check if text is too short or contains only whitespace/special characters
    if (text.length < 2 || /^[\s\.,!?\-_]+$/.test(text)) {
      console.log('Empty or invalid text detected, skipping translation');
      this.userTextInput = '';
      return;
    }

    console.log(`Translating user text: "${text}"`);

    // Set current lane to user for spinner display
    this.currentLane = 'user';

    try {
      // Show translating spinner
      this.showSpinner('postTranslation', 'user');

      // Use the same translation flow as speech with language context
      console.log(
        '🔍 TRANSLATE USER TEXT DEBUG - Calling svc.translate with:',
        {
          text,
          target: this.selectedPartnerLanguage,
          sourceLanguage: this.selectedUserLanguage,
          userLanguage: this.selectedUserLanguage,
          partnerLanguage: this.selectedPartnerLanguage,
        }
      );

      const translation = await this.svc.translate(
        text,
        this.selectedPartnerLanguage(),
        this.selectedUserLanguage(),
        this.selectedUserLanguage(),
        this.selectedPartnerLanguage(),
        this.conversationTopic()
      );

      console.log(
        '🔍 TRANSLATE USER TEXT DEBUG - Received translation:',
        translation
      );

      // Check if translation is empty or invalid
      if (!translation || translation.trim().length === 0) {
        console.warn('Empty translation received from API');
        this.hideAllSpinners();
        // this.showToast('Translation failed. Please try again.');
        return;
      }

      // Track manual translation word usage
      this.wordUsageService.trackTranslation(translation).subscribe({
        next: (success) => {
          console.log(
            '📝 Manual translation tracked successfully:',
            'translated: "' + translation + '"'
          );
          // Refresh word usage data to update the display with a small delay
          setTimeout(() => {
            console.log(
              '🔄 Refreshing word usage data after manual translation...'
            );
            this.wordUsageService.refreshData();
          }, 500);
        },
        error: (error) => {
          console.error('❌ Failed to track manual translation:', error);
        },
      });

      this.currentEnText = text;
      this.currentKyText = translation;

      const capturedConfidence = this.calculateTranslationConfidence(
        text,
        translation
      );

      // Show the translation now; audio is attached when speech returns.
      const conversationEntry: ConversationEntry = {
        id: this.conversationId++,
        original: text,
        translated: translation,
        timestamp: new Date(),
        audioUrl: undefined,
        audioData: '',
        originalAudioUrl: undefined,
        originalAudioData: undefined,
        userLanguage: this.selectedUserLanguage(),
        targetLanguage: this.selectedPartnerLanguage(),
        partnerLanguage: this.selectedPartnerLanguage(),
        confidence: capturedConfidence,
        inputType: 'text', // Mark as text input
      };
      this.zone.run(() => {
        this.enConversation.update((conv) => [conversationEntry, ...conv]);
        this.userTextInput = ''; // Clear input after translation
        this.scrollToLatest('user');
      });

      let translatedTtsResult: any = null;
      let originalTtsResult: any = null;
      const skipAudio = this.skipAudioGeneration();

      if (skipAudio) {
        this.hideSpinner('postTranslation');
        console.log('⏭️ Skipping TTS audio generation (user manual)...');
      } else {
        this.showSpinner('audioGeneration', 'user');
        this.hideSpinner('postTranslation');

        console.log(
          '🎵 Generating TTS audio for translated text (user manual)...'
        );
        // User manual translation: use partner voice (translation is to partner language)
        translatedTtsResult = await this.ttsOrNull(
          translation,
          this.speakingSpeed,
          this.selectedPartnerVoice,
          this.selectedPartnerLanguage()
        );

        if (!translatedTtsResult?.audioUrl) {
          console.warn('No TTS audio received - keeping translation without audio');
        } else {
          // User original text: use user voice (speaking in user's own language)
          originalTtsResult = await this.ttsOrNull(
            text,
            this.speakingSpeed,
            this.selectedUserVoice,
            this.selectedUserLanguage() // Source language for user lane
          );
        }

        this.hideSpinner('audioGeneration');
      }

      const translatedAudioData = translatedTtsResult?.audioData || '';
      const originalAudioData = originalTtsResult?.audioData || '';
      const audioDuration = !translatedTtsResult?.audioUrl
        ? 0
        : await this.calculateAudioDuration(translatedTtsResult.audioUrl);

      if (translatedTtsResult?.audioUrl) {
        this.updateConversationEntry('user', conversationEntry.id, {
          audioUrl: translatedTtsResult.audioUrl,
          audioData: translatedAudioData,
          originalAudioUrl: originalTtsResult?.audioUrl,
          originalAudioData: originalAudioData,
          duration: audioDuration,
        });
        this.showSpinner('finalizing', 'user');
        await this.playAudio(translatedTtsResult.audioUrl, true);
      }

      // ⚡ PERFORMANCE: Save to storage in background
      const historyEntry: any = {
        originalText: text,
        translatedText: translation,
        audioUrl: translatedTtsResult?.audioUrl,
        audioData: translatedAudioData,
        originalAudioUrl: originalTtsResult?.audioUrl,
        originalAudioData: originalAudioData,
        userLanguage: this.selectedUserLanguage(),
        targetLanguage: this.selectedPartnerLanguage(),
        confidence: capturedConfidence,
        duration: audioDuration,
        inputType: 'text', // Mark as text input
      };

      setTimeout(() => {
        this.conversationHistoryService.addConversation(historyEntry);
      }, 0);
    } catch (error: any) {
      console.error('Error translating user text:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown',
      });
      this.hideAllSpinners();

      // Check for word limit errors - show toast on mobile
      if (
        error.message &&
        (error.message.includes('Word limit exceeded') ||
          error.message.includes('Please buy more words'))
      ) {
        console.log('💬 Word limit error detected, showing in UI for mobile');

        // Get translated error message
        const usageStats = this.wordUsageService.getUsageStats();
        const wordsUsed = usageStats.wordsUsed || 0;
        const wordsLimit = usageStats.wordsLimit || 0;

        // Use translation service to get the translated message
        this.wordLimitErrorMessage = this.translationService.formatMessage(
          'wordLimitExceededDetail',
          wordsUsed.toLocaleString(),
          wordsLimit.toLocaleString()
        );
        this.cdr.detectChanges();

        console.log(
          '💬 Word limit error message set in UI:',
          this.wordLimitErrorMessage
        );

        // Auto-clear error message after 10 seconds
        setTimeout(() => {
          this.wordLimitErrorMessage = null;
          this.cdr.detectChanges();
        }, 10000);

        return;
      }

      this.showTranslationError('Failed to translate text. Please try again.');
    }
  }

  async translatePartnerText() {
    if (!this.partnerTextInput.trim()) {
      console.warn('No text to translate');
      return;
    }

    // For iOS browser: Unlock audio context immediately while we're in user interaction context
    // This allows audio to auto-play when TTS is ready
    // Note: We'll also unlock again in playAudio() as a fallback
    if (this.isIOSBrowser) {
      // Don't await - unlock in background so button click doesn't block
      this.unlockIOSAudioContext().catch((err) => {
        console.warn(
          '🎵 [iOS Browser] Failed to unlock in translatePartnerText:',
          err
        );
      });
    }

    const text = this.partnerTextInput.trim();

    // Check word limit (200 words)
    const wordCount = text.split(/\s+/).length;
    if (wordCount > 200) {
      this.showTranslationError(
        this.translationService.getTranslation('textExceedsWordLimit')
      );
      return;
    }

    // Check if text is too short or contains only whitespace/special characters
    if (text.length < 2 || /^[\s\.,!?\-_]+$/.test(text)) {
      console.log('Empty or invalid text detected, skipping translation');
      this.partnerTextInput = '';
      return;
    }

    console.log(`Translating partner text: "${text}"`);

    // Set current lane to partner for spinner display
    this.currentLane = 'partner';

    try {
      // Show translating spinner
      this.showSpinner('postTranslation', 'partner');

      // Use the same translation flow as speech with language context
      const translation = await this.svc.translate(
        text,
        this.selectedUserLanguage(),
        this.selectedPartnerLanguage(),
        this.selectedUserLanguage(),
        this.selectedPartnerLanguage(),
        this.conversationTopic()
      );

      // Check if translation is empty or invalid
      if (!translation || translation.trim().length === 0) {
        console.warn('Empty translation received from API');
        this.hideAllSpinners();
        // this.showToast('Translation failed. Please try again.');
        return;
      }

      // Track manual translation word usage
      this.wordUsageService.trackTranslation(translation).subscribe({
        next: (success) => {
          console.log(
            '📝 Manual translation tracked successfully:',
            'translated: "' + translation + '"'
          );
          // Refresh word usage data to update the display with a small delay
          setTimeout(() => {
            console.log(
              '🔄 Refreshing word usage data after manual translation...'
            );
            this.wordUsageService.refreshData();
          }, 500);
        },
        error: (error) => {
          console.error('❌ Failed to track manual translation:', error);
        },
      });

      this.currentKyInText = text;
      this.currentEnOutText = translation;

      const capturedConfidence = this.calculateTranslationConfidence(
        text,
        translation
      );

      // Show the translation now; audio is attached when speech returns.
      const conversationEntry: ConversationEntry = {
        id: this.conversationId++,
        original: text,
        translated: translation,
        timestamp: new Date(),
        audioUrl: undefined,
        audioData: '',
        originalAudioUrl: undefined,
        originalAudioData: undefined,
        userLanguage: this.selectedPartnerLanguage(),
        targetLanguage: this.selectedUserLanguage(),
        partnerLanguage: this.selectedUserLanguage(),
        confidence: capturedConfidence,
        inputType: 'text', // Mark as text input
      };
      this.zone.run(() => {
        this.kyConversation.update((conv) => [conversationEntry, ...conv]);
        this.partnerTextInput = ''; // Clear input after translation
        this.scrollToLatest('partner');
      });

      let translatedTtsResult: any = null;
      let originalTtsResult: any = null;
      const skipAudio = this.skipAudioGeneration();

      if (skipAudio) {
        this.hideSpinner('postTranslation');
        console.log('⏭️ Skipping TTS audio generation (partner manual)...');
      } else {
        this.showSpinner('audioGeneration', 'partner');
        this.hideSpinner('postTranslation');

        console.log(
          '🎵 Generating TTS audio for translated text (partner manual)...'
        );
        // Partner manual translation: use user voice (translation is to user language)
        translatedTtsResult = await this.ttsOrNull(
          translation,
          this.speakingSpeed,
          this.selectedUserVoice,
          this.selectedUserLanguage() // Target language for partner manual lane
        );

        if (!translatedTtsResult?.audioUrl) {
          console.warn('No TTS audio received - keeping translation without audio');
        } else {
          // Partner original text: use partner voice (speaking in partner's own language)
          originalTtsResult = await this.ttsOrNull(
            text,
            this.speakingSpeed,
            this.selectedPartnerVoice,
            this.selectedPartnerLanguage() // Source language for partner lane
          );
        }

        this.hideSpinner('audioGeneration');
      }

      const translatedAudioData = translatedTtsResult?.audioData || '';
      const originalAudioData = originalTtsResult?.audioData || '';
      const audioDuration = !translatedTtsResult?.audioUrl
        ? 0
        : await this.calculateAudioDuration(translatedTtsResult.audioUrl);

      if (translatedTtsResult?.audioUrl) {
        this.updateConversationEntry('partner', conversationEntry.id, {
          audioUrl: translatedTtsResult.audioUrl,
          audioData: translatedAudioData,
          originalAudioUrl: originalTtsResult?.audioUrl,
          originalAudioData: originalAudioData,
          duration: audioDuration,
        });
        this.showSpinner('finalizing', 'partner');
        await this.playAudio(translatedTtsResult.audioUrl, true);
      }

      // ⚡ PERFORMANCE: Save to storage in background
      const historyEntry: any = {
        originalText: text,
        translatedText: translation,
        audioUrl: translatedTtsResult?.audioUrl,
        audioData: translatedAudioData,
        originalAudioUrl: originalTtsResult?.audioUrl,
        originalAudioData: originalAudioData,
        userLanguage: this.selectedPartnerLanguage(), // Partner's original language
        targetLanguage: this.selectedUserLanguage(), // Partner's translated language (user's language)
        confidence: capturedConfidence,
        duration: audioDuration,
        inputType: 'text', // Mark as text input
      };

      setTimeout(() => {
        this.conversationHistoryService.addConversation(historyEntry);
      }, 0);
    } catch (error: any) {
      console.error('Error translating partner text:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown',
      });
      this.hideAllSpinners();

      // Check for word limit errors - show toast on mobile
      if (
        error.message &&
        (error.message.includes('Word limit exceeded') ||
          error.message.includes('Please buy more words'))
      ) {
        console.log('💬 Word limit error detected, showing in UI for mobile');

        // Get translated error message
        const usageStats = this.wordUsageService.getUsageStats();
        const wordsUsed = usageStats.wordsUsed || 0;
        const wordsLimit = usageStats.wordsLimit || 0;

        // Use translation service to get the translated message
        this.wordLimitErrorMessage = this.translationService.formatMessage(
          'wordLimitExceededDetail',
          wordsUsed.toLocaleString(),
          wordsLimit.toLocaleString()
        );
        this.cdr.detectChanges();

        console.log(
          '💬 Word limit error message set in UI:',
          this.wordLimitErrorMessage
        );

        // Auto-clear error message after 10 seconds
        setTimeout(() => {
          this.wordLimitErrorMessage = null;
          this.cdr.detectChanges();
        }, 10000);

        return;
      }

      this.showTranslationError('Failed to translate text. Please try again.');
    }
  }

  // Audio duration calculation
  private async calculateAudioDuration(audioUrl: string): Promise<number> {
    return new Promise((resolve) => {
      const audio = new Audio(audioUrl);
      const loadedMetadataHandler = () => {
        audio.removeEventListener('loadedmetadata', loadedMetadataHandler);
        audio.removeEventListener('error', errorHandler);
        resolve(audio.duration);
      };
      const errorHandler = () => {
        audio.removeEventListener('loadedmetadata', loadedMetadataHandler);
        audio.removeEventListener('error', errorHandler);
        resolve(0);
      };
      audio.addEventListener('loadedmetadata', loadedMetadataHandler);
      audio.addEventListener('error', errorHandler);
    });
  }

  // Convert audio blob to base64 for persistent storage
  private async blobToBlobUrl(blob: Blob): Promise<string> {
    try {
      console.log(
        '🎵 [BLOB TO BASE64] Converting blob to base64 for persistent storage...'
      );
      console.log('🎵 [BLOB TO BASE64] Blob size:', blob.size, 'bytes');
      console.log('🎵 [BLOB TO BASE64] Blob type:', blob.type);

      // Convert blob to base64 for persistent storage
      const base64Data = await this.blobToBase64(blob);
      console.log(
        '🎵 [BLOB TO BASE64] Conversion completed, base64 length:',
        base64Data.length
      );
      return base64Data;
    } catch (error) {
      console.error(
        '🎵 [BLOB TO BASE64] Error converting blob to base64:',
        error
      );
      return '';
    }
  }

  // Convert audio blob to base64 for storage with size limits
  private async blobToBase64(blob: Blob): Promise<string> {
    try {
      console.log('🎵 [BLOB TO BASE64] Starting conversion...');
      console.log('🎵 [BLOB TO BASE64] Blob: [processed]');
      console.log('🎵 [BLOB TO BASE64] Blob type:', blob.type);

      // Check if blob is valid
      if (!blob || blob.size === 0) {
        console.error(
          '❌ [BLOB TO BASE64] Invalid blob: size is 0 or blob is null'
        );
        return '';
      }

      // No size limit - save all recordings regardless of length
      console.log(
        `🎵 [BLOB TO BASE64] Converting audio blob: ${blob.size} bytes (${(
          blob.size /
          1024 /
          1024
        ).toFixed(2)}MB or ${(blob.size / 1024).toFixed(2)}KB)`
      );

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            console.log('🎵 [BLOB TO BASE64] FileReader onload triggered');
            const result = reader.result as string;

            if (!result) {
              console.error('❌ [BLOB TO BASE64] FileReader result is empty');
              resolve('');
              return;
            }

            console.log(
              '🎵 [BLOB TO BASE64] FileReader result length:',
              result.length
            );
            console.log(
              '🎵 [BLOB TO BASE64] Result preview:',
              result.substring(0, 100)
            );

            // Remove the data:audio/mp4;base64, prefix
            const parts = result.split(',');
            if (parts.length < 2) {
              console.error('❌ [BLOB TO BASE64] Invalid data URL format');
              resolve('');
              return;
            }

            const base64 = parts[1];
            console.log(
              '✅ [BLOB TO BASE64] Conversion successful, base64 length:',
              base64.length
            );
            resolve(base64);
          } catch (error) {
            console.error(
              '❌ [BLOB TO BASE64] Error in onload handler:',
              error
            );
            resolve(''); // Resolve with empty string instead of rejecting
          }
        };

        reader.onerror = (error) => {
          console.error('❌ [BLOB TO BASE64] FileReader error:', error);
          console.error(
            '❌ [BLOB TO BASE64] FileReader error details:',
            JSON.stringify(error, null, 2)
          );
          resolve(''); // Resolve with empty string instead of rejecting
        };

        reader.onabort = () => {
          console.error('❌ [BLOB TO BASE64] FileReader aborted');
          resolve(''); // Resolve with empty string instead of rejecting
        };

        console.log('🎵 [BLOB TO BASE64] Starting FileReader.readAsDataURL...');
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('❌ [BLOB TO BASE64] Unexpected error:', error);
      return '';
    }
  }

  // Convert base64 to audio blob for playback
  private base64ToAudioBlob(
    base64: string,
    mimeType: string = 'audio/mp4'
  ): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  // Audio playback
  private currentAudio?: HTMLAudioElement;

  async playAudio(audioUrl: string, hideSpinnerOnPlay: boolean = false) {
    console.log('🎵 playAudio called:', {
      audioUrl: audioUrl.substring(0, 50) + '...',
      isIOSBrowser: this.isIOSBrowser,
      audioContextUnlocked: this.audioContextUnlocked,
      hideSpinnerOnPlay,
    });

    // Stop any currently playing audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      // Remove previous event listeners
      if (this.audioEndedHandler) {
        this.currentAudio.removeEventListener('ended', this.audioEndedHandler);
        this.audioEndedHandler = undefined;
      }
    }

    this.currentAudio = new Audio(audioUrl);
    this.currentAudio.playbackRate = this.speakingSpeed;
    this.currentAudio.volume = 1.0; // Ensure max volume
    this.currentAudio.preload = 'auto'; // Preload audio

    // Hide spinner when audio actually starts playing (not just when play() is called)
    if (hideSpinnerOnPlay) {
      this.currentAudio.addEventListener(
        'playing',
        () => {
          console.log('🎵 [onplaying] Audio started playing');
          this.zone.run(() => {
            this.hideSpinner('finalizing');
          });
        },
        { once: true }
      );
    }

    // For iOS browser: Try to play immediately
    // The user interaction context from the record button click should still be valid
    // If it's not, the play() will fail and we'll handle the error
    if (this.isIOSBrowser) {
      console.log(
        '🎵 [iOS Browser] Attempting to play audio (user interaction context should be valid)'
      );
    }

    // Use promise-based approach for all platforms (including iOS browser)
    try {
      console.log('🎵 Calling audio.play()...');

      // For iOS browser: Wait for audio to be ready if needed
      if (this.isIOSBrowser && this.currentAudio.readyState < 2) {
        console.log('🎵 [iOS Browser] Waiting for audio to be ready...', {
          readyState: this.currentAudio.readyState,
        });
        await new Promise<void>((resolve) => {
          if (!this.currentAudio) {
            resolve();
            return;
          }
          const onCanPlay = () => {
            this.currentAudio?.removeEventListener('canplay', onCanPlay);
            console.log('🎵 [iOS Browser] Audio ready to play');
            resolve();
          };
          this.currentAudio.addEventListener('canplay', onCanPlay);
          // Timeout after 3 seconds to prevent hanging
          setTimeout(() => {
            this.currentAudio?.removeEventListener('canplay', onCanPlay);
            console.warn(
              '🎵 [iOS Browser] Audio ready timeout, proceeding anyway'
            );
            resolve();
          }, 3000);
        });
      }

      // For iOS browser: Try to play immediately after unlock
      // The unlock should have happened just above, so this should work
      await this.currentAudio.play();
      console.log(
        '🎵 audio.play() promise resolved - audio should be playing:',
        {
          paused: this.currentAudio.paused,
          currentTime: this.currentAudio.currentTime,
          duration: this.currentAudio.duration,
          readyState: this.currentAudio.readyState,
        }
      );
    } catch (error) {
      console.error('🎵 Audio playback failed:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        isIOSBrowser: this.isIOSBrowser,
        audioContextUnlocked: this.audioContextUnlocked,
      });
      this.showTranslationError(
        'Unable to play audio: ' +
          (error instanceof Error ? error.message : String(error))
      );
      // Hide spinner on error
      if (hideSpinnerOnPlay) {
        this.hideAllSpinners();
      }
    }

    // Clean up when audio ends - store handler reference for cleanup
    this.audioEndedHandler = () => {
      console.log('🎵 [onended] Audio ended');
      if (this.currentAudio) {
        this.currentAudio = undefined;
      }
      // Revoke blob URL to free memory
      if (audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
        console.log('🎵 Revoked blob URL:', audioUrl.substring(0, 50));
      }
    };
    this.currentAudio.addEventListener('ended', this.audioEndedHandler, {
      once: true,
    });
  }

  // Confidence calculation (simplified for client-side estimation)
  private calculateTranslationConfidence(
    originalText: string,
    translatedText: string
  ): number {
    if (!originalText || !translatedText) return 0;

    const originalWords = originalText.split(/\s+/).filter(Boolean);
    const translatedWords = translatedText.split(/\s+/).filter(Boolean);

    const lengthRatio =
      Math.min(originalWords.length, translatedWords.length) /
      Math.max(originalWords.length, translatedWords.length);

    // Simple check for significant content
    const hasContent = originalWords.length > 2 && translatedWords.length > 2;

    // Assign a base confidence if content exists and ratio is reasonable
    let confidence = 0.85; // Default confidence for any translation (increased from 0.6)
    if (hasContent && lengthRatio > 0.5) {
      confidence = 0.95; // High confidence for good translations (increased from 0.8)
    } else if (hasContent && lengthRatio > 0.2) {
      confidence = 0.9; // Medium confidence for reasonable translations (increased from 0.6)
    } else if (hasContent) {
      confidence = 0.8; // Lower confidence for shorter/less matching translations (increased from 0.4)
    }

    // Further adjust based on length difference (less penalizing)
    const lengthDiff = Math.abs(originalText.length - translatedText.length);
    if (lengthDiff > originalText.length * 0.7) {
      confidence *= 0.9; // Less penalizing for large length differences (increased from 0.8)
    }

    return confidence; // Return value between 0-1, not 0-100
  }

  // Auto-scroll to latest message
  private scrollToLatest(lane?: 'user' | 'partner') {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        // First, try to scroll to the current translation display (most recent)
        const currentTranslations = document.querySelectorAll(
          '.current-translation'
        );
        if (currentTranslations.length > 0) {
          const latestCurrentTranslation =
            currentTranslations[currentTranslations.length - 1];
          latestCurrentTranslation.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // Add a highlight effect to draw attention
          latestCurrentTranslation.classList.add('highlight-new');
          setTimeout(() => {
            latestCurrentTranslation.classList.remove('highlight-new');
          }, 2000);
        }

        // Then scroll to the latest conversation item in the appropriate lane
        let conversationItems;
        if (lane === 'user') {
          // Focus on user conversation (English)
          conversationItems = document.querySelectorAll(
            '#en-conversation .conversation-item'
          );
        } else if (lane === 'partner') {
          // Focus on partner conversation (Kyrgyz)
          conversationItems = document.querySelectorAll(
            '#ky-conversation .conversation-item'
          );
        } else {
          // Default: focus on all conversation items
          conversationItems = document.querySelectorAll('.conversation-item');
        }

        if (conversationItems.length > 0) {
          const latestConversationItem = conversationItems[0]; // First item is now the latest
          latestConversationItem.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // Add a highlight effect to draw attention
          latestConversationItem.classList.add('highlight-new');
          setTimeout(() => {
            latestConversationItem.classList.remove('highlight-new');
          }, 2000);
        }

        // Also scroll the conversation containers to top (since latest items are now at the top)
        const enConversation = document.getElementById('en-conversation');
        const kyConversation = document.getElementById('ky-conversation');

        if (lane === 'user' && enConversation) {
          enConversation.scrollTop = 0;
        } else if (lane === 'partner' && kyConversation) {
          kyConversation.scrollTop = 0;
        } else {
          // Default: scroll both containers
          if (enConversation) {
            enConversation.scrollTop = 0;
          }
          if (kyConversation) {
            kyConversation.scrollTop = 0;
          }
        }
      }, 300); // Increased delay to ensure DOM is fully updated
    });
  }

  /**
   * Clean up all buffers and reset state for a fresh start
   */
  private isCleaningUp = false; // Flag to prevent double cleanup

  /**
   * Clean up buffers only (without resetting spinner state)
   * Used when we want to cleanup but preserve active spinner states
   */
  private cleanupBuffersOnly() {
    console.log('🧹 Cleaning up buffers only (preserving spinner state)...');

    // Clear all text buffers
    this.sentenceBuffer = '';
    this.currentEnText = '';
    this.currentKyText = '';
    this.currentKyInText = '';
    this.currentEnOutText = '';

    // Reset confidence values
    this.currentEnConfidence = 0;
    this.currentKyConfidence = 0;

    // Clear audio chunks
    this.chunks = [];

    // Stop any ongoing recording
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    // Stop media stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }

    // Reset recording state (but preserve currentLane for spinner)
    this.isRecording = false;
    // Note: We DON'T reset currentLane here to preserve spinner visibility

    // Trigger change detection
    this.cdr.detectChanges();

    console.log('✅ Buffer cleanup completed (spinner state preserved)');
  }

  private cleanupBuffers() {
    // Prevent double cleanup
    if (this.isCleaningUp) {
      console.log('🧹 Cleanup already in progress, skipping...');
      return;
    }

    this.isCleaningUp = true;
    console.log('🧹 Cleaning up buffers and resetting state...');

    // Clear all text buffers
    this.sentenceBuffer = '';
    this.currentEnText = '';
    this.currentKyText = '';
    this.currentKyInText = '';
    this.currentEnOutText = '';

    // Reset confidence values
    this.currentEnConfidence = 0;
    this.currentKyConfidence = 0;

    // Clear audio chunks
    this.chunks = [];

    // Stop any ongoing recording
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    // Stop media stream
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }

    // Reset recording state
    this.isRecording = false;
    this.currentLane = null;

    // Trigger change detection
    this.cdr.detectChanges();

    console.log('✅ Buffer cleanup completed');
    this.isCleaningUp = false;
  }

  /**
   * Get language-specific system prompt for transcription
   * Enhanced with conversation topic context when provided
   */
  private getLanguageSystemPrompt(language: string): string {
    const topic = this.conversationTopic().trim();
    const topicContext = topic
      ? `\n\nCONVERSATION CONTEXT:\nThis conversation is about: "${topic}". The topic may be in any language - understand it regardless of the language used.\n\n🚨 CRITICAL RULES:\n- Use domain-specific terminology and context to provide more accurate transcriptions and translations\n- Translate ONLY what the user ACTUALLY SPOKE - DO NOT create stories or add information\n- DO NOT expand on the topic or generate examples/explanations\n- When translating, prioritize terms and phrases relevant to this topic, but translate ONLY the actual speech\n- The topic is for understanding context ONLY - NOT for generating new content`
      : '';
    const languagePrompts: { [key: string]: string } = {
      en: `You are an English speech-to-text system. You MUST transcribe ONLY English speech using ONLY Latin script (a-z A-Z).

CRITICAL RULES:
1. ONLY use Latin letters: a b c d e f g h i j k l m n o p q r s t u v w x y z
2. Common English words: "hello", "thank you", "good", "how", "what", "cat", "dog", "yes", "no"
3. If you hear ANY non-English speech (Kyrgyz, Korean, Chinese, etc.), return "WRONG_LANGUAGE_DETECTED: Please speak in English only"
4. NEVER transcribe Cyrillic, Hangul, or Chinese characters

Examples of CORRECT English transcription:
- "hello world" → "hello world"
- "thank you very much" → "thank you very much"
- "cat-cat, bitch-cat" → "cat-cat, bitch-cat"

Examples of WRONG input that should trigger error:
- "салам" → "WRONG_LANGUAGE_DETECTED: Please speak in English only"
- "안녕하세요" → "WRONG_LANGUAGE_DETECTED: Please speak in English only"
- "你好" → "WRONG_LANGUAGE_DETECTED: Please speak in English only"

If you cannot transcribe in pure English, return "WRONG_LANGUAGE_DETECTED: Please speak in English only"`,
      ko: `Transcribe Korean speech (한국어) accurately using ONLY Hangul script. Common words: "안녕하세요" (hello), "감사합니다" (thank you), "좋다" (good), "어떻게" (how), "무엇" (what). NEVER mix scripts - use only Hangul letters. If not Korean, return "WRONG_LANGUAGE_DETECTED: 한국어로만 말해주세요".`,
      'zh-CN': `Transcribe Mandarin Chinese speech (普通话) accurately. Common words: "你好" (hello), "谢谢" (thank you), "好" (good), "怎么" (how), "什么" (what). If not Mandarin, return "WRONG_LANGUAGE_DETECTED: 请只说普通话".`,
      'zh-HK': `Transcribe Cantonese speech (粵語) accurately. Common words: "你好" (hello), "多謝" (thank you), "好" (good), "點樣" (how), "乜嘢" (what). If not Cantonese, return "WRONG_LANGUAGE_DETECTED: 請只講粵語".`,
      zh: `Transcribe Chinese speech (中文) accurately. Common words: "你好" (hello), "谢谢" (thank you), "好" (good), "怎么" (how), "什么" (what). If not Chinese, return "WRONG_LANGUAGE_DETECTED: 请只说中文".`, // Backward compatibility
      ky: `You are a Kyrgyz speech-to-text system. You MUST transcribe ONLY Kyrgyz speech using ONLY Cyrillic script (а-яёА-ЯЁ).

CRITICAL RULES:
1. NEVER use Korean Hangul (ㄱ-ㅎㅏ-ㅣ가-힣), Chinese (一-龯), or Latin characters
2. ONLY use Cyrillic letters: а б в г д е ё ж з и й к л м н о п р с т у ф х ц ч ш щ ъ ы ь э ю я
3. Common Kyrgyz words: "мен" (I), "сен" (you), "атыңыз" (your name), "ким" (who), "салам" (hello), "рахмат" (thanks)
4. If you hear ANY non-Kyrgyz speech (English, Korean, Chinese, etc.), return "WRONG_LANGUAGE_DETECTED: Кыргызча гана сүйлөңүз"
5. If unsure about spelling, use standard Kyrgyz Cyrillic spelling
6. NEVER mix scripts - ONLY Cyrillic allowed
7. NEVER transcribe English words like "cat", "bitch", "hello", "thank you" - these are NOT Kyrgyz

Examples of CORRECT Kyrgyz transcription:
- "мен атыңыз ким" (What is your name?)
- "салам" (Hello)
- "рахмат" (Thank you)

Examples of WRONG input that should trigger error:
- "cat-cat, bitch-cat" → "WRONG_LANGUAGE_DETECTED: Кыргызча гана сүйлөңүз"
- "hello world" → "WRONG_LANGUAGE_DETECTED: Кыргызча гана сүйлөңүз"
- "안녕하세요" → "WRONG_LANGUAGE_DETECTED: Кыргызча гана сүйлөңүз"

If you cannot transcribe in pure Cyrillic Kyrgyz, return "WRONG_LANGUAGE_DETECTED: Кыргызча гана сүйлөңүз"`,
      ru: `You are a Russian speech-to-text system. You MUST transcribe ONLY Russian speech using ONLY Cyrillic script (а-яёА-ЯЁ).

CRITICAL RULES:
1. ONLY use Cyrillic letters: а б в г д е ё ж з и й к л м н о п р с т у ф х ц ч ш щ ъ ы ь э ю я
2. You can transcribe ANY Russian words in Cyrillic script. Examples of correct Cyrillic script usage:
   - Greetings: "привет", "здравствуй", "добрый день", "доброе утро", "добрый вечер"
   - Common words: "да", "нет", "спасибо", "пожалуйста", "хорошо", "плохо"
   - Questions: "как", "что", "где", "когда", "почему", "кто", "который"
   - Expressions: "всё", "всё равно", "конечно", "конечно нет", "может быть", "хорошо"
3. If you hear ANY non-Russian speech (English, Kyrgyz, Korean, Chinese, etc.), return "WRONG_LANGUAGE_DETECTED: Говорите только по-русски"
4. NEVER transcribe Latin, Hangul, or Chinese characters
5. NEVER mix scripts - ONLY Cyrillic allowed

Examples of CORRECT Russian transcription:
- "всё хорошо" → "всё хорошо"
- "привет мир" → "привет мир"
- "спасибо большое" → "спасибо большое"
- "как дела" → "как дела"
- "что делаешь" → "что делаешь"

Examples of WRONG input that should trigger error:
- "everything is fine" → "WRONG_LANGUAGE_DETECTED: Говорите только по-русски"
- "hello world" → "WRONG_LANGUAGE_DETECTED: Говорите только по-русски"
- "салам" → "WRONG_LANGUAGE_DETECTED: Говорите только по-русски"

If you cannot transcribe in pure Cyrillic Russian, return "WRONG_LANGUAGE_DETECTED: Говорите только по-русски"`,
      kk: `Transcribe Kazakh speech (Қазақша) accurately. Common words: "сәлем" (hello), "рахмет" (thank you), "жақсы" (good), "қалай" (how), "не" (what). If not Kazakh, return "WRONG_LANGUAGE_DETECTED: Қазақша ғана сөйлеңіз".`,
      tg: `Transcribe Tajik speech (Тоҷикӣ) accurately. Common words: "салом" (hello), "ташаккур" (thank you), "хуб" (good), "чӣ тавр" (how), "чӣ" (what). If not Tajik, return "WRONG_LANGUAGE_DETECTED: Тоҷикӣ ғана гап занед".`,
      tk: `Transcribe Turkmen speech (Türkmençe) accurately. Common words: "salam" (hello), "sag bol" (thank you), "gowy" (good), "nähili" (how), "näme" (what). If not Turkmen, return "WRONG_LANGUAGE_DETECTED: Türkmençe ýalňyz gürleň".`,
      uz: `Transcribe Uzbek speech (O'zbekcha) accurately. Common words: "salom" (hello), "rahmat" (thank you), "yaxshi" (good), "qanday" (how), "nima" (what). If not Uzbek, return "WRONG_LANGUAGE_DETECTED: O'zbekcha gapiring".`,
      fa: `Transcribe Farsi/Persian speech (فارسی) accurately. Common words: "سلام" (hello), "متشکرم" (thank you), "خوب" (good), "چطور" (how), "چی" (what). If not Farsi, return "WRONG_LANGUAGE_DETECTED: لطفاً فقط فارسی صحبت کنید".`,
    };

    const basePrompt =
      languagePrompts[language] ||
      'You are a speech-to-text system. Transcribe the speech accurately.';

    return basePrompt + topicContext;
  }

  /**
   * Detect if Whisper is hallucinating common words from silence
   * Returns true if the transcription appears to be hallucinated
   */
  private detectHallucination(text: string, confidence: number): boolean {
    if (!text || text.trim().length === 0) {
      return false; // Already handled by empty text check
    }

    const trimmedText = text.trim().toLowerCase();

    // Define common hallucinated words/phrases from each language
    const hallucinatedPatterns = [
      // English common words
      'hello',
      'thank you',
      'good',
      'how are you',
      'how',
      'what',
      'yes',
      'no',
      'cat',
      'dog',
      'please',
      'sorry',
      'excuse me',
      'nice to meet you',

      // Korean common words
      '안녕하세요',
      '감사합니다',
      '좋다',
      '어떻게',
      '무엇',
      '네',
      '아니요',

      // Chinese common words
      '你好',
      '谢谢',
      '好',
      '怎么',
      '什么',
      '是',
      '不是',
      '请',
      '对不起',

      // Kyrgyz common words
      'салам',
      'рахмат',
      'жакшы',
      'кандай',
      'эмне',
      'ооба',
      'жок',
      'мен',
      'сен',
      'атыңыз',
      'ким',

      // Russian common words
      'привет',
      'спасибо',
      'хорошо',
      'как',
      'что',
      'да',
      'нет',
      'пожалуйста',
      'извините',
      'меня зовут',

      // Kazakh common words
      'сәлем',
      'рахмет',
      'жақсы',
      'қалай',
      'не',
      'иә',
      'жоқ',

      // Tajik common words
      'салом',
      'ташаккур',
      'хуб',
      'чӣ',
      'тавр',
      'ҳа',
      'не',

      // Turkmen common words
      'salam',
      'sag bol',
      'gowy',
      'nähili',
      'näme',
      'hawa',
      'ýok',

      // Uzbek common words
      'salom',
      'rahmat',
      'yaxshi',
      'qanday',
      'nima',
      'ha',
      "yo'q",
    ];

    // Check if text matches any hallucinated pattern exactly
    const isExactMatch = hallucinatedPatterns.some(
      (pattern) => trimmedText === pattern.toLowerCase()
    );

    // Check if text is very short (1-2 words) and matches patterns
    const words = trimmedText.split(/\s+/);
    const isShortMatch =
      words.length <= 2 &&
      hallucinatedPatterns.some((pattern) =>
        words.includes(pattern.toLowerCase())
      );

    // Check for very low confidence (indicating uncertainty)
    // Lowered threshold to be more precise - only block very uncertain transcriptions
    const isLowConfidence = confidence < 0.2; // Less than 20% confidence

    // Check for suspicious patterns that indicate hallucination
    const suspiciousPatterns = [
      /^hello\s*$/i,
      /^how\s+are\s+you\s*$/i,
      /^thank\s+you\s*$/i,
      /^good\s*$/i,
      /^yes\s*$/i,
      /^no\s*$/i,
      /^please\s*$/i,
      /^sorry\s*$/i,
      /^excuse\s+me\s*$/i,
      /^nice\s+to\s+meet\s+you\s*$/i,
      /^안녕하세요\s*$/,
      /^감사합니다\s*$/,
      /^좋다\s*$/,
      /^어떻게\s*$/,
      /^무엇\s*$/,
      /^네\s*$/,
      /^아니요\s*$/,
      /^你好\s*$/,
      /^谢谢\s*$/,
      /^好\s*$/,
      /^怎么\s*$/,
      /^什么\s*$/,
      /^是\s*$/,
      /^不是\s*$/,
      /^请\s*$/,
      /^对不起\s*$/,
      /^салам\s*$/i,
      /^рахмат\s*$/i,
      /^жакшы\s*$/i,
      /^кандай\s*$/i,
      /^эмне\s*$/i,
      /^ооба\s*$/i,
      /^жок\s*$/i,
      /^привет\s*$/i,
      /^спасибо\s*$/i,
      /^хорошо\s*$/i,
      /^как\s*$/i,
      /^что\s*$/i,
      /^да\s*$/i,
      /^нет\s*$/i,
      /^пожалуйста\s*$/i,
      /^извините\s*$/i,
    ];

    const matchesSuspiciousPattern = suspiciousPatterns.some((pattern) =>
      pattern.test(trimmedText)
    );

    // Hallucination detected if:
    // 1. Exact match with common words AND low confidence, OR
    // 2. Short match with common words AND low confidence, OR
    // 3. Matches suspicious pattern AND low confidence
    const isHallucination =
      (isExactMatch || isShortMatch || matchesSuspiciousPattern) &&
      isLowConfidence;

    if (isHallucination) {
      console.log('🚫 Hallucination detected:', {
        text: trimmedText,
        confidence: confidence,
        isExactMatch,
        isShortMatch,
        matchesSuspiciousPattern,
        isLowConfidence,
        words: words,
      });
    }

    return isHallucination;
  }

  // Track by function for ngFor
  trackByEntryId(index: number, entry: ConversationEntry): number {
    return entry.id;
  }

  // Frontend language detection methods removed for performance

  // Helper methods for template
  getUserLanguageName(lang: string): string {
    const option = this.userLanguages().find((l) => l.value === lang);
    return option ? option.label : lang;
  }

  getPartnerLanguageName(lang: string): string {
    const option = this.partnerLanguages().find((l) => l.value === lang);
    return option ? option.label : lang;
  }

  // Clear methods
  clearUserText() {
    console.log('🧹 clearUserText() called');
    console.log('Before clear - userTextInput:', this.userTextInput);
    console.log('Before clear - currentEnText:', this.currentEnText);
    console.log('Before clear - currentKyText:', this.currentKyText);
    console.log(
      'Before clear - enConversation length:',
      this.enConversation().length
    );
    console.log('Recording state:', this.recording);

    // Clear text input
    this.userTextInput = '';

    // Clear current translation display
    this.currentEnText = '';
    this.currentKyText = '';
    this.currentEnConfidence = 0;

    // Clear conversation history for user lane - using signal set()
    this.enConversation.set([]);

    console.log('After clear - userTextInput:', this.userTextInput);
    console.log('After clear - currentEnText:', this.currentEnText);
    console.log('After clear - currentKyText:', this.currentKyText);
    console.log(
      'After clear - enConversation length:',
      this.enConversation().length
    );

    this.cdr.detectChanges();
    console.log('✅ clearUserText() completed');
  }

  clearPartnerText() {
    console.log('🧹 clearPartnerText() called');
    console.log('Before clear - partnerTextInput:', this.partnerTextInput);
    console.log('Before clear - currentKyInText:', this.currentKyInText);
    console.log('Before clear - currentEnOutText:', this.currentEnOutText);
    console.log(
      'Before clear - kyConversation length:',
      this.kyConversation().length
    );
    console.log('Recording state:', this.recording);

    // Clear text input
    this.partnerTextInput = '';

    // Clear current translation display
    this.currentKyInText = '';
    this.currentEnOutText = '';
    this.currentKyConfidence = 0;

    // Clear conversation history for partner lane - using signal set()
    this.kyConversation.set([]);

    console.log('After clear - partnerTextInput:', this.partnerTextInput);
    console.log('After clear - currentKyInText:', this.currentKyInText);
    console.log('After clear - currentEnOutText:', this.currentEnOutText);
    console.log(
      'After clear - kyConversation length:',
      this.kyConversation().length
    );

    this.cdr.detectChanges();
    console.log('✅ clearPartnerText() completed');
  }

  clearAllConversations() {
    // Clear conversation arrays - using signal set()
    this.enConversation.set([]);
    this.kyConversation.set([]);
    this.cdr.detectChanges();
  }

  // OCR Methods for reading text from images
  async readImageFromUserSection() {
    try {
      console.log('📷 Reading image for user section...');
      if (this.isOpeningImagePicker) {
        console.log('📷 Skipping: image picker already opening (user lane)');
        return;
      }
      this.isOpeningImagePicker = true;

      // Set lane; delay loading state until user chooses source
      this.currentLane = 'user';

      // Get the target language for OCR (user's language)
      const targetLanguage = this.getUserLanguageName(
        this.selectedUserLanguage()
      );

      // On web: open gallery directly
      const isNative = Capacitor.isNativePlatform();
      if (!isNative) {
        await this.captureAndReadText(targetLanguage, 'gallery');
        return;
      }
      // Show simple in-app dropdown menu on native (more reliable than overlay)
      this.isUserImageMenuOpen = true;
    } catch (error) {
      console.error('Error reading image:', error);
      this.showToast(
        this.translationService.getTranslation('readImageFailed'),
        'error'
      );
      this.isProcessingTranslation = false;
      this.currentLane = null;
    } finally {
      this.isOpeningImagePicker = false;
    }
  }

  async readImageFromPartnerSection() {
    try {
      console.log('📷 Reading image for partner section...');
      if (this.isOpeningImagePicker) {
        console.log('📷 Skipping: image picker already opening (partner lane)');
        return;
      }
      this.isOpeningImagePicker = true;

      // Set lane; delay loading state until user chooses source
      this.currentLane = 'partner';

      // Get the target language for OCR (partner's language)
      const targetLanguage = this.getPartnerLanguageName(
        this.selectedPartnerLanguage()
      );

      // On web: open gallery directly
      const isNative2 = Capacitor.isNativePlatform();
      if (!isNative2) {
        await this.captureAndReadText(targetLanguage, 'gallery');
        return;
      }

      // Show simple in-app dropdown menu
      this.isPartnerImageMenuOpen = true;
    } catch (error) {
      console.error('Error reading image:', error);
      this.showToast(
        this.translationService.getTranslation('readImageFailed'),
        'error'
      );
      this.isProcessingTranslation = false;
      this.currentLane = null;
    } finally {
      this.isOpeningImagePicker = false;
    }
  }

  async captureAndReadText(
    targetLanguage: string,
    source: 'camera' | 'gallery'
  ) {
    try {
      // Close any dropdowns
      this.isUserImageMenuOpen = false;
      this.isPartnerImageMenuOpen = false;

      // Capture or select image based on source
      const result =
        source === 'camera'
          ? await this.ocrService.captureAndReadText(targetLanguage, () => {
              // Set OCR processing state after image is captured
              this.isProcessingOCR = true;
              this.isProcessingTranslation = true;
            })
          : await this.ocrService.selectAndReadText(targetLanguage, () => {
              // Set OCR processing state after image is selected
              this.isProcessingOCR = true;
              this.isProcessingTranslation = true;
            });

      if (result && result.text) {
        // Set the extracted text in the appropriate text input
        const lane = this.currentLane;
        if (this.currentLane === 'user') {
          this.userTextInput = result.text;
        } else if (this.currentLane === 'partner') {
          this.partnerTextInput = result.text;
        }

        console.log('✅ Text extracted from image:', result.text);

        // Show success message
        this.showToast(
          this.translationService.getTranslation('textExtractedSuccessfully'),
          'success'
        );

        // Trigger change detection
        this.cdr.detectChanges();

        // Image input should behave like typed text: OCR first, then translate
        // the extracted text into the currently selected target language.
        if (lane === 'user') {
          await this.translateUserText();
        } else if (lane === 'partner') {
          await this.translatePartnerText();
        }
      } else {
        this.showToast(
          this.translationService.getTranslation('noTextFoundInImage'),
          'warning'
        );
      }
    } catch (error) {
      console.error('Error processing image:', error);
      this.showToast(
        this.translationService.getTranslation('readImageFailed'),
        'error'
      );
    } finally {
      this.isProcessingOCR = false;
      this.isProcessingTranslation = false;
      this.currentLane = null;
    }
  }

  // Use native Camera prompt; route result into proper lane
  private async captureFromPrompt(targetLanguage: string) {
    try {
      const result = await this.ocrService.promptAndReadText(targetLanguage);
      if (result && result.text) {
        const lane = this.currentLane;
        if (this.currentLane === 'user') {
          this.userTextInput = result.text;
        } else if (this.currentLane === 'partner') {
          this.partnerTextInput = result.text;
        }
        this.showToast(
          this.translationService.getTranslation('textExtractedSuccessfully'),
          'success'
        );
        this.cdr.detectChanges();

        if (lane === 'user') {
          await this.translateUserText();
        } else if (lane === 'partner') {
          await this.translatePartnerText();
        }
      } else if (!this.isProcessingTranslation) {
        // Canceled: already reset in onDidDismiss; nothing to do
      }
    } catch (e) {
      console.error('Error in captureFromPrompt:', e);
      this.showToast(
        this.translationService.getTranslation('readImageFailed'),
        'error'
      );
    } finally {
      this.isProcessingTranslation = false;
      this.currentLane = null;
    }
  }

  private async showToast(
    message: string,
    type: 'success' | 'error' | 'warning' = 'success'
  ) {
    const toast = await this.toastController.create({
      message: message,
      duration: 3000,
      position: 'bottom',
      color:
        type === 'success'
          ? 'success'
          : type === 'error'
          ? 'danger'
          : 'warning',
    });
    await toast.present();
  }

  // Audio playback for history
  async onPlayHistoryAudio(conversation: ConversationHistoryEntry) {
    console.log('🎵 Playing history audio for conversation:', conversation.id);
    console.log('🎵 Audio sources available:', {
      audioData: !!conversation.audioData,
      audioUrl: !!conversation.audioUrl,
      recordedAudioData: !!conversation.recordedAudioData,
      recordedAudioUrl: !!conversation.recordedAudioUrl,
    });

    // Priority: recordedAudioData > recordedAudioUrl > audioData > audioUrl
    if (conversation.recordedAudioData) {
      // Convert base64 recorded audio to blob URL
      const audioBlob = this.base64ToBlob(conversation.recordedAudioData);
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('🎵 Created audio URL from recorded base64 data');
      await this.playAudio(audioUrl);
    } else if (conversation.recordedAudioUrl) {
      console.log('🎵 Using existing recorded audio URL');
      await this.playAudio(conversation.recordedAudioUrl);
    } else if (conversation.audioData) {
      // Convert base64 to blob URL
      const audioBlob = this.base64ToBlob(conversation.audioData);
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('🎵 Created audio URL from base64 data');
      await this.playAudio(audioUrl);
    } else if (conversation.audioUrl) {
      console.log('🎵 Using existing audio URL');
      await this.playAudio(conversation.audioUrl);
    } else {
      console.log('No audio data available for this conversation');
      this.showTranslationError('No audio available for this conversation');
    }
  }

  private base64ToBlob(base64Data: string): Blob {
    // Remove data URL prefix if present
    const base64String = base64Data.includes(',')
      ? base64Data.split(',')[1]
      : base64Data;

    try {
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'audio/wav' });
    } catch (error) {
      console.error('Error converting base64 to blob:', error);
      throw new Error('Invalid base64 data');
    }
  }

  ngOnDestroy() {
    this.stopRecording();

    // Clean up subscriptions
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }

    // Clean up auth subscription (currentUser$)
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    // Clean up router subscription
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Clean up service worker subscriptions and intervals
    if (this.swVersionUpdatesSubscription) {
      this.swVersionUpdatesSubscription.unsubscribe();
    }
    if (this.swUpdateInterval) {
      clearInterval(this.swUpdateInterval);
      this.swUpdateInterval = undefined;
    }

    // Clean up deep link listener
    if (this.deepLinkListenerHandle) {
      this.deepLinkListenerHandle.remove();
    }

    // Clean up timers and intervals
    this.stopPeriodicRefresh();
    this.stopIdleTimeout();
    this.stopSpeechActivityDetection();

    // Clean up audio processing timeout
    if (this.audioProcessingTimeout) {
      clearTimeout(this.audioProcessingTimeout);
      this.audioProcessingTimeout = undefined;
    }

    // Clean up keyboard dismiss timer
    if (this.keyboardDismissTimer) {
      clearTimeout(this.keyboardDismissTimer);
      this.keyboardDismissTimer = undefined;
    }

    // Clean up textarea resize listeners
    this.textareaResizeCleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn('Error cleaning up textarea resize listener:', error);
      }
    });
    this.textareaResizeCleanupFunctions = [];
    
    // Clean up window resize listener
    if (this.windowResizeCleanup) {
      try {
        this.windowResizeCleanup();
      } catch (error) {
        console.warn('Error cleaning up window resize listener:', error);
      }
      this.windowResizeCleanup = undefined;
    }
    
    // Clear resize handles array
    this.resizeHandles = [];

    // Clean up audio and event listeners
    if (this.currentAudio) {
      // Remove event listeners before cleanup
      if (this.audioEndedHandler) {
        this.currentAudio.removeEventListener('ended', this.audioEndedHandler);
      }
      this.currentAudio.pause();
      this.currentAudio = undefined;
      this.audioEndedHandler = undefined;
    }
  }

  // Method to clear translation cache - can be called from browser console for debugging
  clearTranslationCache() {
    this.svc.clearTranslationCache();
    console.log('🗑️ Translation cache cleared from app component');
  }

  private setupTranslation() {
    this.loadTranslations();
    // Initialize logo with current language
    this.updateLogoUrl(this.translationService.getCurrentLanguage());
    this.translationSubscription =
      this.translationService.currentLanguage$.subscribe(() => {
        this.loadTranslations();
        // Update logo when language changes
        this.updateLogoUrl(this.translationService.getCurrentLanguage());
      });
  }

  private loadTranslations() {
    console.log(
      '🔄 AppComponent: Loading translations for language:',
      this.translationService.getCurrentLanguage()
    );
    this.translations = this.translationService.getAllTranslations();
    console.log('🔄 AppComponent: Loaded translations:', this.translations);
  }

  /**
   * Update logo URL based on current language setting
   * Maps language codes to appropriate logo images
   */
  private updateLogoUrl(language: string): void {
    // Map of language codes to logo URLs
    // Default to app_log.jpeg for most languages
    const logoMap: { [key: string]: string } = {
      'en': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'ko': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'zh-CN': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'zh-HK': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'zh': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'ky': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'ru': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'kk': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'tg': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'tk': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'uz': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'fa': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
      'es': 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/app_log.jpeg?alt=media&token=d650fe3a-54da-4ca7-ab7f-f5ea1e23e344',
    };

    // Get logo URL for current language, fallback to default
    this.logoUrl = logoMap[language] || logoMap['en'];
  }

  private isDeadlockTranslation(text: string): boolean {
    const lowerText = text.toLowerCase().trim();

    // Check for various deadlock patterns
    const deadlockPatterns = [
      // Empty or invalid responses
      text.length === 0,
      text.trim().length === 0,
      text === 'null',
      text === 'undefined',
      text === 'error',
      text === 'failed',
      text === 'timeout',
      text === 'processing',
      // Error patterns
      text.includes('processing') && text.includes('error'),
      text.includes('translation') && text.includes('failed'),
      text.includes('system') && text.includes('error'),
      text.includes('network') && text.includes('error'),
      text.includes('server') && text.includes('error'),
      // Very long error messages
      text.length > 1000 && lowerText.includes('error'),
      // Repeated text patterns
      this.isRepeatedText(text),
    ];

    return deadlockPatterns.some((pattern) => pattern);
  }

  private isRepeatedText(text: string): boolean {
    if (text.length < 10) return false;

    // Check for repeated words or phrases
    const words = text.split(' ');
    if (words.length < 3) return false;

    // Check if more than 70% of words are the same
    const wordCounts = words.reduce((acc, word) => {
      acc[word] = (acc[word] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    const maxCount = Math.max(...Object.values(wordCounts));
    const repetitionRatio = maxCount / words.length;

    return repetitionRatio > 0.7;
  }

  private filterLanguageText(text: string, targetLanguage: string): string {
    // For now, we'll use a simple approach and let the system prompt handle language filtering
    // The system prompt should already be configured to only transcribe the target language
    // This method can be enhanced later with more sophisticated filtering

    switch (targetLanguage) {
      case 'en':
        // Allow English characters, numbers, and common punctuation
        if (/^[a-zA-Z0-9\s.,!?\-'"]+$/.test(text)) {
          return text;
        }
        break;
      case 'ko':
        // Allow Korean characters, English, numbers, and common punctuation
        if (/^[가-힣a-zA-Z0-9\s.,!?\-'"]+$/.test(text)) {
          return text;
        }
        break;
      case 'zh-CN':
      case 'zh-HK':
      case 'zh': // Backward compatibility
        // Allow Chinese characters, English, numbers, and common punctuation
        if (/^[\u4e00-\u9fff\u3400-\u4dbfa-zA-Z0-9\s.,!?\-'"]+$/.test(text)) {
          return text;
        }
        break;
      case 'ky':
      case 'ru':
      case 'kk':
      case 'tg':
        // Allow Cyrillic characters, English, numbers, and common punctuation
        if (/^[а-яА-Яa-zA-Z0-9\s.,!?\-'"]+$/.test(text)) {
          return text;
        }
        break;
      case 'tk':
      case 'uz':
        // Allow Latin characters, numbers, and common punctuation
        if (/^[a-zA-Z0-9\s.,!?\-'"]+$/.test(text)) {
          return text;
        }
        break;
      case 'fa':
        // Allow Persian/Arabic characters, English, numbers, and common punctuation
        if (
          /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-Z0-9\s.,!?\-'"]+$/.test(
            text
          )
        ) {
          return text;
        }
        break;
    }

    // If filtering fails, return original text
    return text;
  }

  getLanguageName(languageCode: string): string {
    const languageNames: { [key: string]: string } = {
      en: 'English',
      ko: 'Korean (한국어)',
      'zh-CN': 'Mandarin (普通话)',
      'zh-HK': 'Cantonese (粵語)',
      zh: 'Mandarin (普通话)', // Backward compatibility
      ky: 'Kyrgyz (Кыргызча)',
      ru: 'Russian (Русский)',
      kk: 'Kazakh (Қазақша)',
      tg: 'Tajik (Тоҷикӣ)',
      tk: 'Turkmen (Türkmençe)',
      uz: "Uzbek (O'zbekcha)",
      fa: 'Farsi (فارسی)',
      es: 'Spanish (Español)',
    };
    return languageNames[languageCode] || languageCode;
  }

  // Copy to clipboard with feedback

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.showSuccessMessage('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers or when clipboard API is not available
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.showSuccessMessage('Copied to clipboard!');
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        this.showTranslationError('Failed to copy text');
      }
    }
  }

  // Show success message (similar to error message)
  private showSuccessMessage(message: string): void {
    // Create a temporary success message element
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #10b981;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    document.body.appendChild(messageEl);

    // Remove after 2 seconds
    setTimeout(() => {
      messageEl.remove();
    }, 2000);
  }

  // Simple error display method (replaces toast for performance)
  private updateConversationEntry(
    lane: 'user' | 'partner',
    id: number,
    changes: Partial<ConversationEntry>
  ) {
    const apply = (conv: ConversationEntry[]) =>
      conv.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry));
    this.zone.run(() => {
      if (lane === 'user') {
        this.enConversation.update(apply);
      } else {
        this.kyConversation.update(apply);
      }
      this.cdr.detectChanges();
    });
  }

  /**
   * Speech failures must not hide or drop a translation that already succeeded.
   */
  private async ttsOrNull(
    text: string,
    speed: number,
    voice: string,
    targetLanguage?: string
  ): Promise<{ audioUrl: string; audioData: string } | null> {
    try {
      return await this.svc.tts(text, speed, voice, targetLanguage);
    } catch (error) {
      console.warn(
        '⚠️ Audio generation failed, keeping translation without audio:',
        error
      );
      return null;
    }
  }

  private async showTranslationError(message: string) {
    console.error('Translation Error:', message);

    // Check if it's a quota error - show banner instead of alert
    if (
      message.includes('quota') ||
      message.includes('insufficient_quota') ||
      message.includes('exceeded your current quota') ||
      message.includes('OpenAI API quota exceeded')
    ) {
      this.showQuotaErrorBannerMessage(message);
      return;
    }

    // Check if it's a network error (Failed to fetch, connection error, etc.)
    const isNetworkError =
      message.includes('Failed to fetch') ||
      message.includes('connection') ||
      message.includes('Connection error') ||
      message.includes('Network error') ||
      message.includes('ERR_CONNECTION_REFUSED') ||
      message.includes('ERR_FAILED');

    // Show alert for critical errors including word limit exceeded and network errors
    const isCriticalError =
      message.includes('Translation failed') ||
      message.includes('Could not transcribe') ||
      message.includes('Word limit exceeded') ||
      message.includes('Please buy more words') ||
      isNetworkError;

    if (isCriticalError) {
      // Use Ionic AlertController for mobile-friendly alerts with close button
      const alert = await this.alertController.create({
        header: isNetworkError ? 'Connection Error' : 'Translation Error',
        message: message,
        buttons: [
          {
            text: 'Close',
            role: 'cancel',
            handler: () => {
              console.log('Error alert dismissed');
            },
          },
        ],
      });
      await alert.present();
    }
  }

  /**
   * Show conversation topic help information
   * Uses alert on mobile, tooltip on web
   */
  async showConversationTopicHelp(): Promise<void> {
    const hintText =
      this.translationService.getTranslation('conversationTopicHint') ||
      'Optional: Helps improve translation accuracy for specific topics';

    // Show alert on both mobile and web when user clicks the info icon
    // On web, the title attribute also provides hover tooltip
    const alert = await this.alertController.create({
      header:
        this.translationService.getTranslation(
          'conversationTopicPlaceholder'
        ) || 'Conversation Topic',
      message: hintText,
      buttons: ['OK'],
      cssClass: 'conversation-topic-help-alert',
    });
    await alert.present();
  }

  // Word counting utility
  countWords(text: string): number {
    if (!text || text.trim() === '') {
      return 0;
    }
    return text.trim().split(/\s+/).length;
  }

  // Validate word count (max 200 words)
  validateWordCount(event: any, section: 'user' | 'partner'): void {
    const text = event.target.value;
    const wordCount = this.countWords(text);

    if (wordCount > 200) {
      // Trim to 200 words
      const words = text.trim().split(/\s+/);
      const trimmedText = words.slice(0, 200).join(' ');

      if (section === 'user') {
        this.userTextInput = trimmedText;
      } else {
        this.partnerTextInput = trimmedText;
      }

      // Show warning toast
      this.showToast('Maximum 200 words allowed', 'warning');
    }
  }

  // Debug methods for conversation history (temporary)
  async debugConversationHistory(): Promise<void> {
    console.log('🔍 Debug: Conversation History Debug');
    await this.conversationHistoryService.debugLocalStorage();
  }

  async clearCorruptedHistory(): Promise<void> {
    if (
      confirm(
        'Clear all corrupted conversation history? This will remove the fake translation data.'
      )
    ) {
      await this.conversationHistoryService.clearCorruptedHistory();
      console.log('🧹 Corrupted history cleared. Please refresh the app.');
    }
  }

  debugTranslationCache(): void {
    const stats = this.svc.getCacheStats();
    console.log('📊 Translation Cache Statistics:', stats);
    console.log('📊 Cache size:', stats.size);
    console.log('📊 Cache limit:', stats.limit);
    console.log('📊 Cache keys:', stats.keys);
  }

  /**
   * Dismiss the mobile keyboard
   */
  dismissKeyboard(): void {
    if (this.isMobile) {
      // Method 1: Blur the active element
      if (
        document.activeElement &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }

      // Method 2: Use Capacitor Keyboard plugin (if available)
      if (typeof window !== 'undefined' && (window as any).Capacitor) {
        const { Keyboard } = (window as any).Capacitor.Plugins;
        if (Keyboard && Keyboard.hide) {
          Keyboard.hide().catch((error: any) => {
            console.log('Keyboard.hide() not available or failed:', error);
          });
        }
      }

      // Method 3: Focus on a non-input element
      const body = document.body;
      if (body) {
        body.focus();
      }
    }
  }

  /**
   * Auto-dismiss keyboard after user stops typing
   */
  private keyboardDismissTimer: any = null;

  /**
   * Keyboard toolbar state
   */
  showKeyboardToolbar = false;
  activeTextarea: 'user' | 'partner' | null = null;
  keyboardHeight = 0;

  onTextInput(event: any, section: 'user' | 'partner'): void {
    // Clear existing timer
    if (this.keyboardDismissTimer) {
      clearTimeout(this.keyboardDismissTimer);
    }

    // Validate word count
    this.validateWordCount(event, section);

    // Set timer to dismiss keyboard after 5 seconds of inactivity (only on mobile)
    if (this.isMobile) {
      this.keyboardDismissTimer = setTimeout(() => {
        this.dismissKeyboard();
        this.showKeyboardToolbar = false;
        this.activeTextarea = null;
      }, 5000);
    }
  }

  /**
   * Handle textarea focus - show keyboard toolbar
   */
  onTextareaFocus(section: 'user' | 'partner'): void {
    if (this.isMobile) {
      // Show toolbar immediately on focus
      this.zone.run(() => {
        this.showKeyboardToolbar = true;
        this.activeTextarea = section;
        console.log('⌨️ Showing keyboard toolbar for:', section);
      });

      // Double-check after a short delay in case keyboard takes time to appear
      setTimeout(() => {
        if (document.activeElement?.matches('textarea')) {
          this.zone.run(() => {
            this.showKeyboardToolbar = true;
          });
        }
      }, 150);
    }
  }

  /**
   * Handle textarea blur - hide keyboard toolbar after a short delay
   */
  onTextareaBlur(section: 'user' | 'partner'): void {
    // Longer delay to allow the Done button to be clicked
    setTimeout(() => {
      if (this.isMobile && !document.activeElement?.matches('textarea')) {
        this.zone.run(() => {
          this.showKeyboardToolbar = false;
          this.activeTextarea = null;
          console.log('⌨️ Hiding keyboard toolbar');
        });
      }
    }, 300);
  }

  /**
   * Dismiss keyboard using the toolbar Done button
   */
  dismissKeyboardWithToolbar(): void {
    console.log('⌨️ Dismiss keyboard button clicked');

    // Force blur any active textarea
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.matches('textarea')) {
      activeElement.blur();
    }

    // Hide toolbar immediately
    this.zone.run(() => {
      this.showKeyboardToolbar = false;
      this.activeTextarea = null;
    });

    // Use multiple methods to ensure keyboard dismissal
    this.dismissKeyboard();

    // Clear any pending auto-dismiss timer
    if (this.keyboardDismissTimer) {
      clearTimeout(this.keyboardDismissTimer);
    }
  }

  /**
   * Toggle diarization mode
   */
  toggleDiarization(): void {
    this.useDiarization = !this.useDiarization;
    console.log(
      '👥 Diarization mode:',
      this.useDiarization ? 'enabled' : 'disabled'
    );

    // Clear previous diarization results when toggling
    if (!this.useDiarization) {
      this.clearDiarizationResults();
    }
  }

  /**
   * Clear diarization results
   */
  clearDiarizationResults(): void {
    this.diarizationResults = [];
    this.showDiarizationResults = false;
    console.log('👥 Diarization results cleared');
  }

  /**
   * Toggle conversation visibility
   */
  toggleConversationVisibility(): void {
    this.showAllConversations = !this.showAllConversations;
    console.log(
      '👁️ Conversation visibility:',
      this.showAllConversations ? 'all' : 'latest only'
    );
    this.cdr.detectChanges();
  }

  /**
   * Toggle partner conversation visibility
   */
  togglePartnerConversationVisibility(): void {
    this.showAllPartnerConversations = !this.showAllPartnerConversations;
    console.log(
      '👁️ Partner conversation visibility:',
      this.showAllPartnerConversations ? 'all' : 'latest only'
    );
    this.cdr.detectChanges();
  }

  /**
   * Get speaker color for diarization display
   */
  getSpeakerColor(speaker: string): string {
    const colors = [
      '#3B82F6', // Blue
      '#EF4444', // Red
      '#10B981', // Green
      '#F59E0B', // Yellow
      '#8B5CF6', // Purple
      '#06B6D4', // Cyan
    ];

    // Simple hash function to get consistent color for each speaker
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }
}
